// src/core/SubRav.ts
import { CryptoUtils, MultibaseCodec } from "@nuwa-ai/identity-kit";
import { bcs } from "@roochnetwork/rooch-sdk";
var SUBRAV_VERSION_1 = 1;
var CURRENT_SUBRAV_VERSION = SUBRAV_VERSION_1;
var SubRAVBCSSchema = bcs.struct("SubRAV", {
  version: bcs.u8(),
  chain_id: bcs.u64(),
  channel_id: bcs.ObjectId,
  channel_epoch: bcs.u64(),
  vm_id_fragment: bcs.string(),
  accumulated_amount: bcs.u256(),
  nonce: bcs.u64()
});
var SubRAVCodec = class {
  /**
   * Encode a SubRAV to bytes using BCS serialization
   * This ensures cross-platform consistency with Move contracts
   */
  static encode(subRav) {
    try {
      const bcsSubRAV = {
        version: subRav.version,
        chain_id: subRav.chainId.toString(),
        channel_id: subRav.channelId,
        channel_epoch: subRav.channelEpoch.toString(),
        vm_id_fragment: subRav.vmIdFragment,
        accumulated_amount: subRav.accumulatedAmount.toString(),
        nonce: subRav.nonce.toString()
      };
      return SubRAVBCSSchema.serialize(bcsSubRAV).toBytes();
    } catch (error) {
      throw new Error(
        `Failed to encode SubRAV: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  }
  /**
   * Decode bytes to SubRAV using BCS deserialization
   */
  static decode(bytes) {
    try {
      const bcsSubRAV = SubRAVBCSSchema.parse(bytes);
      return {
        version: bcsSubRAV.version,
        chainId: BigInt(bcsSubRAV.chain_id),
        channelId: bcsSubRAV.channel_id,
        channelEpoch: BigInt(bcsSubRAV.channel_epoch),
        vmIdFragment: bcsSubRAV.vm_id_fragment,
        accumulatedAmount: BigInt(bcsSubRAV.accumulated_amount),
        nonce: BigInt(bcsSubRAV.nonce)
      };
    } catch (error) {
      throw new Error(
        `Failed to decode SubRAV: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  }
  /**
   * Get the BCS hex string representation of a SubRAV (useful for debugging)
   */
  static toHex(subRav) {
    const bytes = this.encode(subRav);
    return "0x" + Array.from(bytes).map((byte) => byte.toString(16).padStart(2, "0")).join("");
  }
  /**
   * Create SubRAV from BCS hex string
   */
  static fromHex(hex) {
    const cleanHex = hex.startsWith("0x") ? hex.slice(2) : hex;
    const bytes = new Uint8Array(cleanHex.match(/.{1,2}/g)?.map((byte) => parseInt(byte, 16)) || []);
    return this.decode(bytes);
  }
};
var SubRAVSigner = class {
  /**
   * Sign a SubRAV using the provided signer and key
   */
  static async sign(subRav, signer, keyId) {
    const bytes = SubRAVCodec.encode(subRav);
    const signature = await signer.signWithKeyId(bytes, keyId);
    return { subRav, signature };
  }
  /**
   * Verify a signed SubRAV using public key or DID document
   */
  static async verify(signedSubRAV, verificationMethod) {
    try {
      const bytes = SubRAVCodec.encode(signedSubRAV.subRav);
      let publicKey;
      let keyType;
      if ("publicKey" in verificationMethod) {
        publicKey = verificationMethod.publicKey;
        keyType = verificationMethod.keyType;
      } else {
        const { didDocument } = verificationMethod;
        const keyId = `${didDocument.id}#${signedSubRAV.subRav.vmIdFragment}`;
        const vm = didDocument.verificationMethod?.find(
          (vm2) => vm2.id === keyId
        );
        if (!vm) return false;
        keyType = vm.type;
        if (vm.publicKeyMultibase) {
          publicKey = MultibaseCodec.decodeBase58btc(vm.publicKeyMultibase);
        } else if (vm.publicKeyJwk) {
          return false;
        } else {
          return false;
        }
      }
      return CryptoUtils.verify(bytes, signedSubRAV.signature, publicKey, keyType);
    } catch (error) {
      return false;
    }
  }
  /**
   * Verify a signed SubRAV using DID resolver (convenience method)
   */
  static async verifyWithResolver(signedSubRAV, payerDid, resolver) {
    try {
      const didDocument = await resolver.resolveDID(payerDid);
      if (!didDocument) return false;
      return this.verify(signedSubRAV, { didDocument });
    } catch (error) {
      return false;
    }
  }
};
var SubRAVUtils = class {
  /**
   * Create a new SubRAV with default version
   */
  static create(params) {
    return {
      version: params.version ?? CURRENT_SUBRAV_VERSION,
      chainId: params.chainId,
      channelId: params.channelId,
      channelEpoch: params.channelEpoch,
      vmIdFragment: params.vmIdFragment,
      accumulatedAmount: params.accumulatedAmount,
      nonce: params.nonce
    };
  }
  /**
   * Check if a SubRAV version is supported
   */
  static isSupportedVersion(version) {
    return version === SUBRAV_VERSION_1;
  }
};

// src/errors/codes.ts
var PaymentErrorCode = /* @__PURE__ */ ((PaymentErrorCode2) => {
  PaymentErrorCode2["AUTH_MISSING"] = "AUTH_MISSING";
  PaymentErrorCode2["AUTH_INVALID"] = "AUTH_INVALID";
  PaymentErrorCode2["AUTH_ADMIN_FORBIDDEN"] = "AUTH_ADMIN_FORBIDDEN";
  PaymentErrorCode2["DID_RESOLVE_FAILED"] = "DID_RESOLVE_FAILED";
  PaymentErrorCode2["CHANNEL_NOT_FOUND"] = "CHANNEL_NOT_FOUND";
  PaymentErrorCode2["SUBCHANNEL_NOT_AUTHORIZED"] = "SUBCHANNEL_NOT_AUTHORIZED";
  PaymentErrorCode2["CHANNEL_CONTEXT_MISSING"] = "CHANNEL_CONTEXT_MISSING";
  PaymentErrorCode2["PAYMENT_REQUIRED"] = "PAYMENT_REQUIRED";
  PaymentErrorCode2["RAV_CONFLICT"] = "RAV_CONFLICT";
  PaymentErrorCode2["INVALID_SIGNATURE"] = "INVALID_SIGNATURE";
  PaymentErrorCode2["UNKNOWN_SUBRAV"] = "UNKNOWN_SUBRAV";
  PaymentErrorCode2["TAMPERED_SUBRAV"] = "TAMPERED_SUBRAV";
  PaymentErrorCode2["BILLING_RATE_FETCH_FAILED"] = "BILLING_RATE_FETCH_FAILED";
  PaymentErrorCode2["BILLING_RATE_NOT_AVAILABLE"] = "BILLING_RATE_NOT_AVAILABLE";
  PaymentErrorCode2["BILLING_MAX_AMOUNT_EXCEEDED"] = "BILLING_MAX_AMOUNT_EXCEEDED";
  PaymentErrorCode2["BILLING_CONFIG_ERROR"] = "BILLING_CONFIG_ERROR";
  PaymentErrorCode2["HUB_INSUFFICIENT_FUNDS"] = "HUB_INSUFFICIENT_FUNDS";
  PaymentErrorCode2["HUB_BALANCE_FETCH_FAILED"] = "HUB_BALANCE_FETCH_FAILED";
  PaymentErrorCode2["CHANNEL_CLOSED"] = "CHANNEL_CLOSED";
  PaymentErrorCode2["EPOCH_MISMATCH"] = "EPOCH_MISMATCH";
  PaymentErrorCode2["INTERNAL_SERVER_ERROR"] = "INTERNAL_SERVER_ERROR";
  return PaymentErrorCode2;
})(PaymentErrorCode || {});
function httpStatusFor(code) {
  switch (code) {
    case "PAYMENT_REQUIRED" /* PAYMENT_REQUIRED */:
    case "HUB_INSUFFICIENT_FUNDS" /* HUB_INSUFFICIENT_FUNDS */:
      return 402;
    case "RAV_CONFLICT" /* RAV_CONFLICT */:
    case "CHANNEL_CONTEXT_MISSING" /* CHANNEL_CONTEXT_MISSING */:
      return 409;
    case "INVALID_SIGNATURE" /* INVALID_SIGNATURE */:
    case "UNKNOWN_SUBRAV" /* UNKNOWN_SUBRAV */:
    case "TAMPERED_SUBRAV" /* TAMPERED_SUBRAV */:
    case "CHANNEL_CLOSED" /* CHANNEL_CLOSED */:
    case "EPOCH_MISMATCH" /* EPOCH_MISMATCH */:
    case "BILLING_MAX_AMOUNT_EXCEEDED" /* BILLING_MAX_AMOUNT_EXCEEDED */:
    case "AUTH_MISSING" /* AUTH_MISSING */:
    case "AUTH_INVALID" /* AUTH_INVALID */:
    case "AUTH_ADMIN_FORBIDDEN" /* AUTH_ADMIN_FORBIDDEN */:
    case "DID_RESOLVE_FAILED" /* DID_RESOLVE_FAILED */:
      return 400;
    case "CHANNEL_NOT_FOUND" /* CHANNEL_NOT_FOUND */:
    case "SUBCHANNEL_NOT_AUTHORIZED" /* SUBCHANNEL_NOT_AUTHORIZED */:
      return 404;
    case "BILLING_RATE_NOT_AVAILABLE" /* BILLING_RATE_NOT_AVAILABLE */:
    case "BILLING_RATE_FETCH_FAILED" /* BILLING_RATE_FETCH_FAILED */:
    case "BILLING_CONFIG_ERROR" /* BILLING_CONFIG_ERROR */:
    case "HUB_BALANCE_FETCH_FAILED" /* HUB_BALANCE_FETCH_FAILED */:
    default:
      return 500;
  }
}
var Errors = {
  paymentRequired: (msg) => ({
    code: "PAYMENT_REQUIRED" /* PAYMENT_REQUIRED */,
    message: msg ?? "Signature required for pending proposal"
  }),
  ravConflict: (msg) => ({
    code: "RAV_CONFLICT" /* RAV_CONFLICT */,
    message: msg
  }),
  channelContextMissing: () => ({
    code: "CHANNEL_CONTEXT_MISSING" /* CHANNEL_CONTEXT_MISSING */,
    message: "channelId or vmIdFragment not derivable. Check DID authentication."
  }),
  channelNotFound: (id) => ({
    code: "CHANNEL_NOT_FOUND" /* CHANNEL_NOT_FOUND */,
    message: `CHANNEL_NOT_FOUND: ${id}`
  }),
  subchannelNotAuthorized: (id, vm) => ({
    code: "SUBCHANNEL_NOT_AUTHORIZED" /* SUBCHANNEL_NOT_AUTHORIZED */,
    message: `SUBCHANNEL_NOT_AUTHORIZED: ${id}#${vm}`
  }),
  didResolveFailed: (did) => ({
    code: "DID_RESOLVE_FAILED" /* DID_RESOLVE_FAILED */,
    message: `DID_RESOLVE_FAILED: ${did}`
  }),
  rateFetchFailed: (e) => ({
    code: "BILLING_RATE_FETCH_FAILED" /* BILLING_RATE_FETCH_FAILED */,
    message: String(e)
  }),
  rateNotAvailable: (assetId) => ({
    code: "BILLING_RATE_NOT_AVAILABLE" /* BILLING_RATE_NOT_AVAILABLE */,
    message: `Missing exchange rate for asset ${assetId}`
  }),
  maxAmountExceeded: (finalCost, max) => ({
    code: "BILLING_MAX_AMOUNT_EXCEEDED" /* BILLING_MAX_AMOUNT_EXCEEDED */,
    message: `Cost ${finalCost} exceeds maxAmount ${max}`,
    details: { finalCost: finalCost.toString(), max: max.toString() }
  }),
  internal: (msg) => ({
    code: "INTERNAL_SERVER_ERROR" /* INTERNAL_SERVER_ERROR */,
    message: msg
  }),
  hubInsufficientFunds: (balance, required, assetId) => ({
    code: "HUB_INSUFFICIENT_FUNDS" /* HUB_INSUFFICIENT_FUNDS */,
    message: `PaymentHub balance insufficient: ${balance}, required: ${required} for asset ${assetId}`,
    details: { balance: balance.toString(), required: required.toString(), assetId }
  }),
  hubBalanceFetchFailed: (error, ownerDid, assetId) => ({
    code: "HUB_BALANCE_FETCH_FAILED" /* HUB_BALANCE_FETCH_FAILED */,
    message: `Failed to fetch PaymentHub balance for ${ownerDid}#${assetId}: ${String(error)}`,
    details: { ownerDid, assetId, error: String(error) }
  })
};

// src/billing/core/strategy-registry.ts
var registry = /* @__PURE__ */ new Map();
var cache = /* @__PURE__ */ new WeakMap();
function register(type, builder) {
  if (registry.has(type)) {
    console.warn(`Billing strategy type '${type}' is already registered \u2013 overwriting.`);
  }
  registry.set(type, builder);
}
function getStrategy(rule) {
  const cached = cache.get(rule);
  if (cached) {
    return cached;
  }
  const builder = registry.get(rule.strategy.type);
  if (!builder) {
    throw new Error(
      `Unknown billing strategy type '${rule.strategy.type}'. Make sure it has been registered.`
    );
  }
  const strategy = builder(rule.strategy);
  cache.set(rule, strategy);
  return strategy;
}

// src/billing/rate/types.ts
var BaseRateProvider = class {
  constructor(cacheTimeoutMs = 6e4) {
    this.cache = /* @__PURE__ */ new Map();
    this.cacheTimeout = cacheTimeoutMs;
  }
  getLastUpdated(assetId) {
    const cached = this.cache.get(assetId);
    return cached?.timestamp || null;
  }
  clearCache(assetId) {
    if (assetId) {
      this.cache.delete(assetId);
    } else {
      this.cache.clear();
    }
  }
  updateCache(assetId, price, assetInfo) {
    this.cache.set(assetId, {
      price,
      timestamp: Date.now(),
      assetInfo
    });
  }
  isCacheValid(assetId) {
    const cached = this.cache.get(assetId);
    if (!cached) return false;
    return Date.now() - cached.timestamp < this.cacheTimeout;
  }
  getCachedPrice(assetId) {
    const cached = this.cache.get(assetId);
    if (!cached || !this.isCacheValid(assetId)) return null;
    return cached.price;
  }
};
var RateProviderError = class extends Error {
  constructor(message, assetId, provider) {
    super(message);
    this.assetId = assetId;
    this.provider = provider;
    this.name = "RateProviderError";
  }
};
var RateNotFoundError = class extends RateProviderError {
  constructor(assetId, provider) {
    super(`Rate not found for asset ${assetId}`, assetId, provider);
    this.name = "RateNotFoundError";
  }
};

// src/billing/core/converter.ts
function convertUsdToAssetUsingPrice(usdCost, rate) {
  if (usdCost === 0n) {
    return {
      assetCost: 0n,
      usdCost,
      priceUsed: rate.price,
      priceTimestamp: rate.timestamp,
      rateProvider: rate.provider,
      assetId: rate.assetId
    };
  }
  if (rate.price <= 0n) {
    throw new RateProviderError(
      `Invalid price for asset ${rate.assetId}: ${rate.price}`,
      rate.assetId,
      rate.provider
    );
  }
  const assetCost = (usdCost + rate.price - 1n) / rate.price;
  return {
    assetCost,
    usdCost,
    priceUsed: rate.price,
    priceTimestamp: rate.timestamp,
    rateProvider: rate.provider,
    assetId: rate.assetId
  };
}

// src/billing/strategies/base.ts
var BaseStrategy = class {
  constructor() {
    /**
     * Default implementation: most strategies don't require execution results
     */
    this.deferred = false;
  }
  /**
   * Convert string or bigint to bigint
   * Handles scientific notation strings like "5e12"
   */
  toBigInt(value) {
    if (typeof value === "bigint") {
      return value;
    }
    if (typeof value === "number") {
      return BigInt(value);
    }
    if (value.includes("e") || value.includes("E")) {
      const num = parseFloat(value);
      return BigInt(Math.round(num));
    }
    return BigInt(value);
  }
};

// src/billing/strategies/perRequest.ts
var PerRequestStrategy = class extends BaseStrategy {
  constructor(config) {
    super();
    this.price = this.toBigInt(config.price);
  }
  evaluate(ctx, units) {
    const u = Number.isFinite(units) && units > 0 ? Math.floor(units) : 1;
    return this.price * BigInt(u);
  }
};

// src/billing/strategies/perToken.ts
var PerTokenStrategy = class extends BaseStrategy {
  constructor(config) {
    super();
    /**
     * PerToken strategy requires execution results (usage data) from LLM calls
     */
    this.deferred = true;
    this.unitPrice = this.toBigInt(config.unitPricePicoUSD);
  }
  evaluate(ctx, units) {
    const tokenCount = Number.isFinite(units) && units > 0 ? Math.floor(units) : 1;
    return this.unitPrice * BigInt(tokenCount);
  }
};

// src/billing/strategies/finalCost.ts
var FinalCostStrategy = class extends BaseStrategy {
  constructor(_config = {}) {
    super();
    /** Requires post-flight usage to be available */
    this.deferred = true;
  }
  evaluate(_ctx, units) {
    const value = Number.isFinite(units) && units > 0 ? Math.floor(units) : 0;
    return BigInt(value);
  }
};

// src/billing/strategies/index.ts
function registerBuiltinStrategies() {
  register("PerRequest", (cfg) => new PerRequestStrategy(cfg));
  register("PerToken", (cfg) => new PerTokenStrategy(cfg));
  register("FinalCost", (cfg) => new FinalCostStrategy(cfg));
}

// src/rooch/ChannelUtils.ts
import { parseDid } from "@nuwa-ai/identity-kit";
import {
  bcs as bcs2,
  sha3_256,
  toHEX,
  Serializer,
  RoochAddress,
  normalizeRoochAddress
} from "@roochnetwork/rooch-sdk";
var ChannelKeySchema = bcs2.struct("ChannelKey", {
  sender: bcs2.Address,
  receiver: bcs2.Address,
  coin_type: bcs2.string()
});
var CancellationInfoSchema = bcs2.struct("CancellationInfo", {
  initiated_time: bcs2.u64(),
  pending_amount: bcs2.u256()
});
var SubChannelSchema = bcs2.struct("SubChannel", {
  pk_multibase: bcs2.string(),
  method_type: bcs2.string(),
  last_claimed_amount: bcs2.u256(),
  last_confirmed_nonce: bcs2.u64()
});
var PaymentChannelSchema = bcs2.struct("PaymentChannel", {
  sender: bcs2.Address,
  receiver: bcs2.Address,
  coin_type: bcs2.string(),
  sub_channels: bcs2.ObjectId,
  // Table<String, SubChannel> stored as ObjectID
  status: bcs2.u8(),
  channel_epoch: bcs2.u64(),
  cancellation_info: bcs2.option(CancellationInfoSchema)
});
var PaymentHubSchema = bcs2.struct("PaymentHub", {
  multi_coin_store: bcs2.ObjectId,
  active_channels: bcs2.ObjectId
  // Table<String, u64>
});
var PaymentRevenueHubSchema = bcs2.struct("PaymentRevenueHub", {
  multi_coin_store: bcs2.ObjectId,
  revenue_by_source: bcs2.ObjectId
  // Table<String, Table<String, u256>>
});
var BalanceSchema = bcs2.struct("Balance", {
  value: bcs2.u256()
});
var CoinStoreFieldSchema = bcs2.struct("CoinStoreField", {
  coin_type: bcs2.string(),
  balance: BalanceSchema,
  frozen: bcs2.bool()
});
var DynamicFieldSubChannelSchema = bcs2.struct("DynamicField", {
  name: bcs2.string(),
  value: SubChannelSchema
});
var DynamicFieldCoinStoreSchema = bcs2.struct("DynamicField", {
  name: bcs2.string(),
  value: CoinStoreFieldSchema
});
var DynamicFieldU64Schema = bcs2.struct("DynamicField", {
  name: bcs2.string(),
  value: bcs2.u64()
});
function deriveChannelId(payerDid, payeeDid, assetId) {
  const payerParsed = parseDid(payerDid);
  const payeeParsed = parseDid(payeeDid);
  if (payerParsed.method !== "rooch") {
    throw new Error(`Invalid payer DID method: expected 'rooch', got '${payerParsed.method}'`);
  }
  if (payeeParsed.method !== "rooch") {
    throw new Error(`Invalid payee DID method: expected 'rooch', got '${payeeParsed.method}'`);
  }
  return calcChannelObjectId(payerParsed.identifier, payeeParsed.identifier, assetId);
}
function calcChannelObjectId(senderAddress, receiverAddress, coinType) {
  const normalizedCoinType = normalizeAssetId(coinType);
  const channelKey = {
    sender: senderAddress,
    receiver: receiverAddress,
    coin_type: normalizedCoinType
  };
  const paymentChannelStructTag = {
    address: "0x3",
    module: "payment_channel",
    name: "PaymentChannel",
    type_params: []
  };
  const idBytes = ChannelKeySchema.serialize(channelKey).toBytes();
  const typeBytes = new TextEncoder().encode(
    Serializer.structTagToCanonicalString(paymentChannelStructTag)
  );
  const bytes = new Uint8Array(idBytes.length + typeBytes.length);
  bytes.set(idBytes);
  bytes.set(typeBytes, idBytes.length);
  const hash = sha3_256(bytes);
  return `0x${toHEX(hash)}`;
}
function normalizeAssetId(assetId) {
  try {
    const typeTag = Serializer.typeTagParseFromStr(assetId, true);
    if (!("struct" in typeTag)) {
      throw new Error(`Asset ID must be a struct type, got: ${assetId}`);
    }
    return Serializer.structTagToCanonicalString(typeTag.struct);
  } catch (error) {
    throw new Error(`Failed to parse asset ID: ${assetId} - ${error}`);
  }
}
function calculatePaymentHubId(ownerAddress) {
  const paymentHubStructTag = {
    address: "0x3",
    module: "payment_channel",
    name: "PaymentHub",
    type_params: []
  };
  const canonicalStructTag = Serializer.structTagToCanonicalString(paymentHubStructTag);
  const roochAddress = new RoochAddress(ownerAddress);
  const ownerBytes = roochAddress.toBytes();
  const structTagBytes = new TextEncoder().encode(canonicalStructTag);
  const combined = new Uint8Array(ownerBytes.length + structTagBytes.length);
  combined.set(ownerBytes, 0);
  combined.set(structTagBytes, ownerBytes.length);
  const hash = sha3_256(combined);
  return "0x" + toHEX(hash);
}
function deriveFieldKeyFromString(value) {
  try {
    const keyBytes = bcs2.string().serialize(value).toBytes();
    const stringTypeTag = "0x0000000000000000000000000000000000000000000000000000000000000001::string::String";
    const typeTagBytes = new TextEncoder().encode(stringTypeTag);
    const combinedBytes = new Uint8Array(keyBytes.length + typeTagBytes.length);
    combinedBytes.set(keyBytes);
    combinedBytes.set(typeTagBytes, keyBytes.length);
    const hash = sha3_256(combinedBytes);
    return `0x${toHEX(hash)}`;
  } catch (error) {
    throw new Error(`Failed to derive field key: ${error}`);
  }
}
function deriveCoinTypeFieldKey(coinType) {
  const assetIdCanonical = normalizeAssetId(coinType);
  return deriveFieldKeyFromString(assetIdCanonical);
}
function parsePaymentChannelData(value) {
  try {
    const bcsHex = value.startsWith("0x") ? value.slice(2) : value;
    const bcsBytes = new Uint8Array(
      bcsHex.match(/.{1,2}/g)?.map((byte) => parseInt(byte, 16)) || []
    );
    const parsed = PaymentChannelSchema.parse(bcsBytes);
    return {
      sender: parsed.sender,
      receiver: parsed.receiver,
      coin_type: parsed.coin_type,
      sub_channels: parsed.sub_channels,
      status: parsed.status,
      channel_epoch: BigInt(parsed.channel_epoch),
      cancellation_info: parsed.cancellation_info ? {
        initiated_time: BigInt(parsed.cancellation_info.initiated_time),
        pending_amount: BigInt(parsed.cancellation_info.pending_amount)
      } : null
    };
  } catch (error) {
    throw new Error(`Failed to parse PaymentChannel data: ${error}`);
  }
}
function parsePaymentHubData(value) {
  try {
    if (typeof value === "string" && value.startsWith("0x")) {
      const hex = value.slice(2);
      const bcsBytes = new Uint8Array(hex.length / 2);
      for (let i = 0; i < bcsBytes.length; i++) {
        bcsBytes[i] = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
      }
      const parsed = PaymentHubSchema.parse(bcsBytes);
      return {
        multi_coin_store: parsed.multi_coin_store,
        active_channels: parsed.active_channels
      };
    } else {
      throw new Error("Unexpected PaymentHub value format");
    }
  } catch (error) {
    throw new Error(`Failed to parse PaymentHub data: ${error}`);
  }
}
function parsePaymentRevenueHubData(value) {
  try {
    if (typeof value === "string" && value.startsWith("0x")) {
      const hex = value.slice(2);
      const bcsBytes = new Uint8Array(hex.length / 2);
      for (let i = 0; i < bcsBytes.length; i++) {
        bcsBytes[i] = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
      }
      const parsed = PaymentRevenueHubSchema.parse(bcsBytes);
      return {
        multi_coin_store: parsed.multi_coin_store,
        revenue_by_source: parsed.revenue_by_source
      };
    } else {
      throw new Error("Unexpected PaymentRevenueHub value format");
    }
  } catch (error) {
    throw new Error(`Failed to parse PaymentRevenueHub data: ${error}`);
  }
}
function parseDynamicFieldSubChannel(value) {
  try {
    const bcsHex = value.startsWith("0x") ? value.slice(2) : value;
    const bcsBytes = new Uint8Array(
      bcsHex.match(/.{1,2}/g)?.map((byte) => parseInt(byte, 16)) || []
    );
    const parsed = DynamicFieldSubChannelSchema.parse(bcsBytes);
    return {
      name: parsed.name,
      value: {
        pk_multibase: parsed.value.pk_multibase,
        method_type: parsed.value.method_type,
        last_claimed_amount: BigInt(parsed.value.last_claimed_amount),
        last_confirmed_nonce: BigInt(parsed.value.last_confirmed_nonce)
      }
    };
  } catch (error) {
    throw new Error(`Failed to parse DynamicField<String, SubChannel>: ${error}`);
  }
}
function parseDynamicFieldCoinStore(value) {
  try {
    const hex = value.startsWith("0x") ? value.slice(2) : value;
    const bcsBytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < bcsBytes.length; i++) {
      bcsBytes[i] = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
    }
    const parsed = DynamicFieldCoinStoreSchema.parse(bcsBytes);
    return {
      name: parsed.name,
      value: parsed.value
    };
  } catch (error) {
    throw new Error(`Failed to parse DynamicField<String, CoinStoreField>: ${error}`);
  }
}
function parseDynamicFieldU64(value) {
  try {
    const hex = value.startsWith("0x") ? value.slice(2) : value;
    const bcsBytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < bcsBytes.length; i++) {
      bcsBytes[i] = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
    }
    const parsed = DynamicFieldU64Schema.parse(bcsBytes);
    return {
      name: parsed.name,
      value: Number(parsed.value)
      // Convert bigint to number for count
    };
  } catch (error) {
    throw new Error(`Failed to parse DynamicField<String, u64>: ${error}`);
  }
}
function safeBalanceToBigint(balanceValue) {
  if (typeof balanceValue === "string") {
    return BigInt(balanceValue);
  } else if (typeof balanceValue === "bigint") {
    return balanceValue;
  } else {
    return BigInt(0);
  }
}
function calculateRevenueHubId(ownerAddress) {
  try {
    const paymentRevenueHubStructTag = {
      address: "0x3",
      module: "payment_revenue",
      name: "PaymentRevenueHub",
      type_params: []
    };
    const canonicalStructTag = Serializer.structTagToCanonicalString(paymentRevenueHubStructTag);
    const roochAddress = new RoochAddress(ownerAddress);
    const ownerBytes = roochAddress.toBytes();
    const structTagBytes = new TextEncoder().encode(canonicalStructTag);
    const combined = new Uint8Array(ownerBytes.length + structTagBytes.length);
    combined.set(ownerBytes, 0);
    combined.set(structTagBytes, ownerBytes.length);
    const hash = sha3_256(combined);
    return "0x" + toHEX(hash);
  } catch (error) {
    const normalizedAddress = normalizeRoochAddress(ownerAddress);
    const typeIdentifier = "PaymentRevenueHub";
    const combined = `${normalizedAddress}::${typeIdentifier}`;
    const hash = sha3_256(new TextEncoder().encode(combined));
    return `0x${toHEX(hash)}`;
  }
}
function parseU256FromBCS(value) {
  try {
    const hex = value.startsWith("0x") ? value.slice(2) : value;
    const bcsBytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < bcsBytes.length; i++) {
      bcsBytes[i] = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
    }
    const parsed = bcs2.u256().parse(bcsBytes);
    return BigInt(parsed);
  } catch (error) {
    throw new Error(`Failed to parse u256 from BCS: ${error}`);
  }
}
function extractTableIdFromField(fieldValue) {
  try {
    if (typeof fieldValue === "string" && fieldValue.startsWith("0x")) {
      const hex = fieldValue.slice(2);
      const bcsBytes = new Uint8Array(hex.length / 2);
      for (let i = 0; i < bcsBytes.length; i++) {
        bcsBytes[i] = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
      }
      const parsed = bcs2.ObjectId.parse(bcsBytes);
      return parsed;
    }
    return null;
  } catch (error) {
    return null;
  }
}

// src/core/RavVerifier.ts
async function verify(params) {
  const result = {
    decision: "ALLOW",
    signedVerified: !params.signedSubRav,
    // default to true if no signedSubRav is provided
    pendingMatched: false
  };
  const signed = params.signedSubRav;
  const billingRule = params.billingRule;
  const isFreeRoute = !!billingRule && billingRule.paymentRequired === false;
  if (signed) {
    const ok = await SubRAVSigner.verify(signed, { didDocument: params.payerDidDoc });
    result.signedVerified = !!ok;
    if (!ok) {
      result.error = {
        code: "INVALID_SIGNATURE" /* INVALID_SIGNATURE */,
        message: `Invalid signature for signed SubRAV`
      };
      return finalize();
    }
  }
  if (params.latestPendingSubRav) {
    if (!signed) {
      if (!isFreeRoute) {
        result.decision = "REQUIRE_SIGNATURE_402";
        result.error = {
          code: "PAYMENT_REQUIRED" /* PAYMENT_REQUIRED */,
          message: `Signature required for pending proposal (channel: ${params.channelInfo.channelId}, nonce: ${params.latestPendingSubRav.nonce})`
        };
        return finalize();
      }
    } else {
      const matches = signed.subRav.channelId === params.latestPendingSubRav.channelId && signed.subRav.vmIdFragment === params.latestPendingSubRav.vmIdFragment && signed.subRav.nonce === params.latestPendingSubRav.nonce;
      if (!matches) {
        result.decision = "CONFLICT";
        result.error = {
          code: "RAV_CONFLICT" /* RAV_CONFLICT */,
          message: `SignedSubRAV does not match pending proposal (expected nonce: ${params.latestPendingSubRav.nonce}, accumulatedAmount: ${params.latestPendingSubRav.accumulatedAmount}, received: ${signed.subRav.nonce}, ${signed.subRav.accumulatedAmount})`
        };
        return finalize();
      }
      result.pendingMatched = true;
    }
  } else {
    if (signed) {
      if (params.latestSignedSubRav) {
        if (signed.subRav.channelId === params.latestSignedSubRav.subRav.channelId && signed.subRav.vmIdFragment === params.latestSignedSubRav.subRav.vmIdFragment && signed.subRav.nonce > params.latestSignedSubRav.subRav.nonce && signed.subRav.accumulatedAmount > params.latestSignedSubRav.subRav.accumulatedAmount) {
          result.decision = "ALLOW";
        } else if (
          // Special case: Allow nonce that is exactly +1 from latest signed SubRAV
          // This handles the race condition where server sent a proposal via in-band frame
          // but hasn't persisted it to pendingSubRAVStore yet
          signed.subRav.channelId === params.latestSignedSubRav.subRav.channelId && signed.subRav.vmIdFragment === params.latestSignedSubRav.subRav.vmIdFragment && signed.subRav.nonce === params.latestSignedSubRav.subRav.nonce + 1n && signed.subRav.accumulatedAmount >= params.latestSignedSubRav.subRav.accumulatedAmount
        ) {
          result.decision = "ALLOW";
        } else {
          result.decision = "CONFLICT";
          result.error = {
            code: "RAV_CONFLICT" /* RAV_CONFLICT */,
            message: `SignedSubRAV does not match latest signed SubRAV (expected nonce: ${params.latestSignedSubRav.subRav.nonce} accumulatedAmount: ${params.latestSignedSubRav.subRav.accumulatedAmount}, received: ${signed.subRav.nonce}, ${signed.subRav.accumulatedAmount})`
          };
          return finalize();
        }
      } else {
        if (signed.subRav.channelId === params.subChannelInfo.channelId && signed.subRav.vmIdFragment === params.subChannelInfo.vmIdFragment && signed.subRav.nonce > params.subChannelInfo.lastConfirmedNonce && signed.subRav.accumulatedAmount >= params.subChannelInfo.lastClaimedAmount) {
          result.decision = "ALLOW";
        } else {
          result.decision = "CONFLICT";
          result.error = {
            code: "RAV_CONFLICT" /* RAV_CONFLICT */,
            message: `SignedSubRAV does not match subchannel state (expected nonce: ${params.subChannelInfo.lastConfirmedNonce}, accumulatedAmount: ${params.subChannelInfo.lastClaimedAmount}, received: ${signed.subRav.nonce}, ${signed.subRav.accumulatedAmount})`
          };
          return finalize();
        }
      }
    }
  }
  return finalize();
  function finalize() {
    return result;
  }
}
function assertRavProgression(prevNonce, prevAccumulatedAmount, nextNonce, nextAccumulatedAmount, allowSameAccumulated = false) {
  const expectedNonce = prevNonce + 1n;
  if (nextNonce !== expectedNonce) {
    throw new Error(`Invalid nonce: expected ${expectedNonce}, got ${nextNonce}`);
  }
  if (allowSameAccumulated) {
    if (nextAccumulatedAmount < prevAccumulatedAmount) {
      throw new Error(
        `Amount must not decrease: previous ${prevAccumulatedAmount}, new ${nextAccumulatedAmount}`
      );
    }
  } else {
    if (nextAccumulatedAmount <= prevAccumulatedAmount) {
      throw new Error(
        `Amount must increase: previous ${prevAccumulatedAmount}, new ${nextAccumulatedAmount}`
      );
    }
  }
}
function assertSubRavProgression(prev, next, allowSameAccumulated = false) {
  assertRavProgression(
    prev.nonce,
    prev.accumulatedAmount,
    next.nonce,
    next.accumulatedAmount,
    allowSameAccumulated
  );
}

// src/core/PaymentProcessor.ts
import { DebugLogger } from "@nuwa-ai/identity-kit";
var PaymentProcessor = class {
  constructor(config) {
    this.config = config;
    try {
      registerBuiltinStrategies();
    } catch {
    }
    this.pendingSubRAVStore = config.pendingSubRAVStore;
    this.ravRepository = config.ravRepository;
    this.stats = {
      totalRequests: 0,
      successfulPayments: 0,
      failedPayments: 0,
      autoClaimsTriggered: 0
    };
    this.logger = DebugLogger.get("PaymentProcessor");
    this.logger.setLevel(config.debug ? "debug" : "info");
  }
  getServiceId() {
    return this.config.serviceId;
  }
  /**
   * Step A: Pre-process request - complete all I/O operations and verification
   * Returns context with state populated for both pre-flight and post-flight
   */
  async preProcess(ctx) {
    this.stats.totalRequests++;
    this.log("Pre-processing payment for operation:", ctx.meta.operation);
    const pctx = this.asPreprocessed(ctx);
    try {
      const rule = pctx.meta.billingRule;
      if (rule && rule.paymentRequired === false && !pctx.meta.signedSubRav) {
        this.log("Free route without SignedSubRAV - skipping preProcess verification");
        return pctx;
      }
      let channelId;
      let vmIdFragment;
      if (ctx.meta.signedSubRav) {
        channelId = ctx.meta.signedSubRav.subRav.channelId;
        vmIdFragment = ctx.meta.signedSubRav.subRav.vmIdFragment;
      } else {
        const didAuth = await this.tryDIDAuthFallback(ctx);
        this.log("DIDAuth fallback result:", didAuth);
        if (didAuth) {
          channelId = didAuth.channelId;
          vmIdFragment = didAuth.vmIdFragment;
        }
      }
      if (!channelId || !vmIdFragment) {
        return this.fail(pctx, Errors.channelContextMissing(), { attachHeader: false });
      }
      const channelInfo = await this.config.payeeClient.getChannelInfo(channelId);
      if (!channelInfo) {
        return this.fail(pctx, Errors.channelNotFound(channelId), { attachHeader: false });
      }
      const subChannelInfo = await this.config.payeeClient.getSubChannelState(
        channelId,
        vmIdFragment
      );
      if (!subChannelInfo) {
        return this.fail(pctx, Errors.subchannelNotAuthorized(channelId, vmIdFragment), {
          attachHeader: false
        });
      }
      const latestSignedSubRav = await this.ravRepository.getLatest(channelId, vmIdFragment);
      pctx.state.chainId = await this.config.payeeClient.getContract().getChainId();
      const payerDidDoc = await this.config.didResolver.resolveDID(channelInfo.payerDid);
      if (!payerDidDoc) {
        return this.fail(pctx, Errors.didResolveFailed(channelInfo.payerDid), {
          attachHeader: false
        });
      }
      const latestPendingSubRav = await this.pendingSubRAVStore.findLatestBySubChannel(
        channelId,
        vmIdFragment
      );
      const ravResult = await verify({
        channelInfo,
        subChannelInfo,
        billingRule: ctx.meta.billingRule,
        payerDidDoc,
        signedSubRav: ctx.meta.signedSubRav,
        latestSignedSubRav: latestSignedSubRav || void 0,
        latestPendingSubRav: latestPendingSubRav || void 0,
        debug: this.config.debug
      });
      pctx.state.channelInfo = channelInfo;
      pctx.state.subChannelInfo = subChannelInfo;
      pctx.state.latestSignedSubRav = latestSignedSubRav || void 0;
      pctx.state.latestPendingSubRav = latestPendingSubRav || void 0;
      try {
        const hubBalance = await this.config.hubBalanceService.getBalance(
          channelInfo.payerDid,
          channelInfo.assetId
        );
        const latestAccumulatedForDelta = latestSignedSubRav?.subRav?.accumulatedAmount ?? subChannelInfo.lastClaimedAmount;
        const lastClaimedAmount = subChannelInfo.lastClaimedAmount;
        const deltaOutstanding = latestAccumulatedForDelta > lastClaimedAmount ? latestAccumulatedForDelta - lastClaimedAmount : 0n;
        this.log("Hub balance check (cached)", {
          payerDid: channelInfo.payerDid,
          assetId: channelInfo.assetId,
          cachedBalance: hubBalance.toString(),
          minClaimAmount: this.config.minClaimAmount.toString(),
          deltaOutstanding: deltaOutstanding.toString(),
          sufficient: hubBalance >= this.config.minClaimAmount && hubBalance >= deltaOutstanding,
          source: "HubBalanceService_cache"
        });
        if (deltaOutstanding >= this.config.minClaimAmount && hubBalance < deltaOutstanding) {
          this.log("\u274C Hub balance insufficient for outstanding delta - rejecting request", {
            payerDid: channelInfo.payerDid,
            assetId: channelInfo.assetId,
            cachedBalance: hubBalance.toString(),
            deltaOutstanding: deltaOutstanding.toString()
          });
          return this.fail(
            pctx,
            Errors.hubInsufficientFunds(hubBalance, deltaOutstanding, channelInfo.assetId),
            { attachHeader: true }
          );
        }
        if (hubBalance < this.config.minClaimAmount) {
          this.log("\u274C Hub balance insufficient - rejecting request", {
            payerDid: channelInfo.payerDid,
            assetId: channelInfo.assetId,
            cachedBalance: hubBalance.toString(),
            minClaimAmount: this.config.minClaimAmount.toString(),
            deficit: (this.config.minClaimAmount - hubBalance).toString()
          });
          return this.fail(
            pctx,
            Errors.hubInsufficientFunds(
              hubBalance,
              this.config.minClaimAmount,
              channelInfo.assetId
            ),
            { attachHeader: true }
          );
        }
      } catch (error) {
        this.log("\u26A0\uFE0F Hub balance check failed - proceeding anyway", {
          payerDid: channelInfo.payerDid,
          assetId: channelInfo.assetId,
          error: error instanceof Error ? error.message : String(error),
          source: "HubBalanceService_cache"
        });
      }
      if (ravResult.decision === "REQUIRE_SIGNATURE_402" || ravResult.decision === "CONFLICT") {
        this.log(
          "\u26A0\uFE0F Early return from preProcess due to decision:",
          ravResult.decision,
          ravResult.error
        );
        if (pctx.state.latestPendingSubRav) {
          try {
            const decoded = {
              version: 1,
              clientTxRef: pctx.meta.clientTxRef,
              serviceTxRef: pctx.state.serviceTxRef,
              subRav: pctx.state.latestPendingSubRav,
              error: ravResult.error
            };
            pctx.state.responsePayload = decoded;
            this.log("\u2705 Attached pending SubRAV to PAYMENT_REQUIRED response:", {
              channelId: pctx.state.latestPendingSubRav.channelId,
              nonce: pctx.state.latestPendingSubRav.nonce.toString(),
              accumulatedAmount: pctx.state.latestPendingSubRav.accumulatedAmount.toString(),
              clientTxRef: pctx.meta.clientTxRef
            });
          } catch (e) {
            this.logger.error("\u26A0\uFE0F Failed to attach pending SubRAV to error payload:", e);
            pctx.state.responsePayload = {
              version: 1,
              clientTxRef: pctx.meta.clientTxRef,
              serviceTxRef: pctx.state.serviceTxRef,
              error: ravResult.error
            };
          }
        } else {
          pctx.state.responsePayload = {
            version: 1,
            clientTxRef: pctx.meta.clientTxRef,
            serviceTxRef: pctx.state.serviceTxRef,
            error: ravResult.error
          };
          this.logger.error("\u26A0\uFE0F No pending SubRAV available for PAYMENT_REQUIRED response:", {
            channelId: pctx.meta.channelId,
            vmIdFragment: pctx.meta.vmIdFragment,
            clientTxRef: pctx.meta.clientTxRef,
            decision: ravResult.decision
          });
        }
        return this.fail(
          pctx,
          {
            code: ravResult.error?.code ?? "INTERNAL_SERVER_ERROR",
            message: ravResult.error?.message ?? "Verification decision error"
          },
          { attachHeader: false }
        );
      }
      pctx.state.signedSubRavVerified = ravResult.signedVerified;
      if (ravResult.signedVerified && pctx.meta.signedSubRav) {
        this.log("\u{1F5D1}\uFE0F Removing matched pending SubRAV and persisting verified SignedSubRAV:", {
          channelId: pctx.meta.signedSubRav.subRav.channelId,
          vmIdFragment: pctx.meta.signedSubRav.subRav.vmIdFragment,
          nonce: pctx.meta.signedSubRav.subRav.nonce.toString()
        });
        if (ravResult.pendingMatched) {
          try {
            await this.pendingSubRAVStore.remove(
              pctx.meta.signedSubRav.subRav.channelId,
              pctx.meta.signedSubRav.subRav.vmIdFragment,
              pctx.meta.signedSubRav.subRav.nonce
            );
          } catch (e) {
            this.log("\u26A0\uFE0F Failed to remove matched pending:", e);
          }
        }
        try {
          await this.ravRepository.save(pctx.meta.signedSubRav);
        } catch (e) {
          this.log("\u26A0\uFE0F Failed to persist verified SignedSubRAV:", e);
        }
      }
      const assetId = channelInfo.assetId;
      {
        try {
          const price = await this.config.rateProvider.getPricePicoUSD(assetId);
          const timestamp = this.config.rateProvider.getLastUpdated(assetId) ?? Date.now();
          const exchangeRate = {
            price,
            timestamp,
            provider: "rate-provider",
            assetId
          };
          pctx.state.exchangeRate = exchangeRate;
        } catch (e) {
          pctx.state.error = Errors.rateFetchFailed(e);
        }
      }
      this.log("\u2705 Pre-processing completed");
      return pctx;
    } catch (error) {
      this.log("\u{1F6A8} Pre-processing error:", error);
      pctx.state.signedSubRavVerified = false;
      return this.fail(pctx, Errors.internal(String(error)), {
        attachHeader: false
      });
    }
  }
  getPendingSubRAVRepository() {
    return this.pendingSubRAVStore;
  }
  settle(ctx, units) {
    this.log("\u{1F504} Settling billing with units:", units);
    const pctx = this.asPreprocessed(ctx);
    try {
      const rule = pctx.meta.billingRule;
      if (pctx.state.error) {
        return this.fail(pctx, pctx.state.error);
      }
      if (rule) {
        this.log(
          "\u{1F4CA} Processing billing rule:",
          rule.id,
          "paymentRequired:",
          rule.paymentRequired,
          "units:",
          units
        );
        const usageUnits = Number.isFinite(units) && units >= 0 ? Math.floor(units) : 0;
        const strategy = getStrategy(rule);
        const usdCost = strategy.evaluate(pctx, usageUnits);
        let finalCost = usdCost;
        if (rule.paymentRequired !== false) {
          if (usdCost > 0n) {
            const assetId = pctx.state?.channelInfo?.assetId;
            if (!assetId) {
              throw new Error("CHANNEL_INFO_MISSING: assetId not derivable. Check channelInfo.");
            }
            const rate = pctx.state.exchangeRate;
            if (!rate) {
              return this.fail(pctx, Errors.rateNotAvailable(String(assetId)));
            }
            const conversion = convertUsdToAssetUsingPrice(usdCost, rate);
            finalCost = conversion.assetCost;
          } else {
            finalCost = 0n;
          }
        } else {
          finalCost = 0n;
        }
        pctx.state.cost = finalCost;
        if (pctx.meta.maxAmount && pctx.meta.maxAmount > 0n && finalCost > pctx.meta.maxAmount) {
          this.log(`Cost ${finalCost} exceeds maxAmount ${pctx.meta.maxAmount}`);
          return this.fail(pctx, Errors.maxAmountExceeded(finalCost, pctx.meta.maxAmount));
        }
        if (rule.paymentRequired === false) {
          if (pctx.meta.signedSubRav && finalCost === 0n) {
            this.log(
              "\u{1F4DD} Free route with SignedSubRAV - verified but not generating unsigned SubRAV"
            );
            pctx.state.cost = finalCost;
          } else if (pctx.meta.signedSubRav && finalCost > 0n) {
            throw new Error("FREE_ROUTE_WITH_COST: free routes should have cost=0");
          } else {
            this.log("\u{1F4DD} Free route without SignedSubRAV - no payment processing needed");
            pctx.state.cost = finalCost;
          }
        } else {
          const hasSigned = !!pctx.meta.signedSubRav;
          const hasLatest = !!pctx.state && !!pctx.state.latestSignedSubRav;
          const hasSubState = !!pctx.state && !!pctx.state.subChannelInfo;
          this.log("\u{1F50D} Baseline check:", {
            hasSigned,
            hasLatest,
            hasSubState,
            hasState: !!pctx.state,
            stateKeys: pctx.state ? Object.keys(pctx.state) : []
          });
          if (!hasSigned && !hasLatest && !hasSubState) {
            return this.fail(pctx, Errors.channelContextMissing());
          }
          this.log("\u{1F527} Generating SubRAV with finalCost:", finalCost);
          const { unsignedSubRAV, serviceTxRef } = this.generateNextSubRAV({
            signedSubRAV: pctx.meta.signedSubRav,
            latestSignedSubRav: pctx.state?.latestSignedSubRav,
            subChannelInfo: pctx.state?.subChannelInfo,
            chainId: pctx.state?.chainId,
            clientTxRef: pctx.meta.clientTxRef,
            cost: finalCost,
            costUsd: usdCost
          });
          this.log("\u{1F527} Generated SubRAV:", {
            nonce: unsignedSubRAV.nonce,
            accumulatedAmount: unsignedSubRAV.accumulatedAmount
          });
          pctx.state.unsignedSubRav = unsignedSubRAV;
          pctx.state.serviceTxRef = serviceTxRef;
          pctx.state.nonce = unsignedSubRAV.nonce;
          pctx.state.responsePayload = {
            subRav: unsignedSubRAV,
            cost: finalCost,
            costUsd: usdCost,
            clientTxRef: pctx.meta.clientTxRef,
            serviceTxRef,
            version: 1
          };
        }
        this.log("\u2705 Billing settled successfully");
      }
      return pctx;
    } catch (error) {
      this.log("\u{1F6A8} Billing settlement error:", error);
      const p = this.asPreprocessed(ctx);
      p.state.cost = 0n;
      return this.fail(p, Errors.internal(String(error)));
    }
  }
  async persist(ctx) {
    const pctx = this.asPreprocessed(ctx);
    if (!pctx.state?.unsignedSubRav) {
      this.log("\u26A0\uFE0F No unsignedSubRAV to persist");
      return;
    }
    try {
      this.log("\u{1F4BE} Persisting billing state");
      const newSubRAV = pctx.state.unsignedSubRav;
      await this.pendingSubRAVStore.save(newSubRAV);
      pctx.state.persisted = true;
      if (this.config.claimTriggerService && this.config.minClaimAmount && pctx.state.subChannelInfo && pctx.state.latestSignedSubRav) {
        try {
          const latestAccumulated = pctx.state.latestSignedSubRav.subRav.accumulatedAmount;
          const lastClaimed = pctx.state.subChannelInfo.lastClaimedAmount;
          const delta = latestAccumulated > lastClaimed ? latestAccumulated - lastClaimed : 0n;
          this.log("Reactive claim evaluation", {
            channelId: newSubRAV.channelId,
            vmIdFragment: newSubRAV.vmIdFragment,
            latestAccumulated: latestAccumulated.toString(),
            lastClaimed: lastClaimed.toString(),
            delta: delta.toString(),
            minClaimAmount: this.config.minClaimAmount.toString()
          });
          if (delta >= this.config.minClaimAmount) {
            await this.config.claimTriggerService.maybeQueue(
              newSubRAV.channelId,
              newSubRAV.vmIdFragment,
              delta
            );
            this.log("Reactive claim queued", {
              channelId: newSubRAV.channelId,
              vmIdFragment: newSubRAV.vmIdFragment,
              delta: delta.toString()
            });
          }
        } catch (error) {
          this.log("\u26A0\uFE0F Reactive claim trigger failed", { error });
        }
      }
      this.log("\u2705 Billing state persisted successfully");
    } catch (error) {
      this.log("\u{1F6A8} Persistence error:", error);
      throw error;
    }
  }
  /**
   * Clear expired pending SubRAV proposals
   */
  async clearExpiredProposals(maxAgeMinutes = 30) {
    const maxAge = maxAgeMinutes * 60 * 1e3;
    const clearedCount = await this.pendingSubRAVStore.cleanup(maxAge);
    this.log(
      `Cleared ${clearedCount} expired pending SubRAV proposals (older than ${maxAgeMinutes} minutes)`
    );
    return clearedCount;
  }
  /**
   * Get payment processing statistics
   */
  getProcessingStats() {
    return { ...this.stats };
  }
  /**
   * Find pending SubRAV proposal by channel and nonce
   */
  async findPendingProposal(channelId, vmIdFragment, nonce) {
    return await this.pendingSubRAVStore.find(channelId, vmIdFragment, nonce);
  }
  /**
   * Find the latest pending SubRAV proposal for a channel (for recovery scenarios)
   */
  async findLatestPendingProposal(channelId, vmIdFragment) {
    return await this.pendingSubRAVStore.findLatestBySubChannel(channelId, vmIdFragment);
  }
  /**
   * Generate SubRAV and header synchronously for post-flight billing
   */
  generateNextSubRAV(params) {
    const serviceTxRef = `srv-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const current = params.signedSubRAV?.subRav ?? params.latestSignedSubRav?.subRav ?? {
      channelId: params.subChannelInfo.channelId,
      vmIdFragment: params.subChannelInfo.vmIdFragment,
      nonce: params.subChannelInfo.lastConfirmedNonce,
      accumulatedAmount: params.subChannelInfo.lastClaimedAmount,
      chainId: params.chainId,
      channelEpoch: params.subChannelInfo.epoch,
      version: 1
    };
    const next = this.buildNextUnsigned(current, params.cost);
    const responsePayload = {
      subRav: next,
      cost: params.cost,
      costUsd: params.costUsd,
      clientTxRef: params.clientTxRef,
      serviceTxRef,
      version: 1
    };
    return { unsignedSubRAV: next, serviceTxRef };
  }
  /**
   * Build next unsigned SubRAV following the prior signed SubRAV, reused by async/sync paths.
   */
  buildNextUnsigned(current, cost) {
    const channelId = current.channelId;
    const vmIdFragment = current.vmIdFragment;
    const currentNonce = current.nonce;
    const nextNonce = currentNonce + 1n;
    const newAccumulatedAmount = current.accumulatedAmount + cost;
    return {
      version: 1,
      chainId: current.chainId,
      channelId,
      channelEpoch: current.channelEpoch,
      vmIdFragment,
      nonce: nextNonce,
      accumulatedAmount: newAccumulatedAmount
    };
  }
  /**
   * Try to locate sub-channel using DIDAuth fallback (§4.4 of rav-handling.md)
   */
  async tryDIDAuthFallback(ctx) {
    try {
      const didInfo = ctx.meta.didInfo;
      if (!didInfo || !didInfo.did || !didInfo.keyId) {
        this.log("No DIDAuth info available for fallback");
        return null;
      }
      const keyIdParts = didInfo.keyId.split("#");
      if (keyIdParts.length < 2) {
        this.log("Invalid keyId format for DIDAuth fallback:", didInfo.keyId);
        return null;
      }
      const vmIdFragment = keyIdParts[1];
      const payeeDid = await this.config.payeeClient.getPayeeDid();
      const defaultAssetId = this.config.defaultAssetId || "0x3::gas_coin::RGas";
      const channelId = deriveChannelId(didInfo.did, payeeDid, defaultAssetId);
      this.log("DIDAuth fallback derived:", {
        channelId,
        vmIdFragment,
        payerDid: didInfo.did,
        payeeDid
      });
      return { channelId, vmIdFragment };
    } catch (error) {
      this.log("Error in DIDAuth fallback:", error);
      return null;
    }
  }
  // ---------- Internal helpers ----------
  asPreprocessed(ctx) {
    if (!ctx.state) {
      ctx.state = {};
    }
    return ctx;
  }
  attachErrorHeader(ctx, error) {
    try {
      if (!ctx.state.serviceTxRef) {
        ctx.state.serviceTxRef = `srv-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      }
      if (!ctx.state.responsePayload) {
        ctx.state.responsePayload = {
          error,
          clientTxRef: ctx.meta.clientTxRef,
          serviceTxRef: ctx.state.serviceTxRef,
          version: 1
        };
      }
    } catch (e) {
      this.log("Failed to build error header:", e);
    }
  }
  fail(ctx, error, options) {
    ctx.state.error = error;
    if (options?.attachHeader !== false) {
      this.attachErrorHeader(ctx, error);
    }
    return ctx;
  }
  /**
   * Debug logging
   */
  log(...args) {
    this.logger.debug(...args);
  }
  /**
   * Destroy background services and release resources.
   * - Stops ClaimTriggerService processing loop
   * - Clears HubBalanceService cache
   */
  destroy() {
    try {
      this.config?.claimTriggerService?.destroy?.();
    } catch {
    }
    try {
      this.config.hubBalanceService?.clear?.();
    } catch {
    }
    try {
      this.logger.info("PaymentProcessor destroyed");
    } catch {
    }
  }
};

// src/core/PaymentUtils.ts
var PaymentUtils = class {
  /**
   * Generate transaction reference
   */
  static generateTxRef() {
    return `tx_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }
  /**
   * Check if two SubRAVs match (ignoring signature)
   */
  static subRAVsMatch(subRAV1, subRAV2) {
    return subRAV1.version === subRAV2.version && subRAV1.chainId === subRAV2.chainId && subRAV1.channelId === subRAV2.channelId && subRAV1.channelEpoch === subRAV2.channelEpoch && subRAV1.vmIdFragment === subRAV2.vmIdFragment && subRAV1.accumulatedAmount === subRAV2.accumulatedAmount && subRAV1.nonce === subRAV2.nonce;
  }
  /**
   * Generate unique ID for tracking
   */
  static generateId(prefix = "") {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 9);
    return prefix ? `${prefix}_${timestamp}_${random}` : `${timestamp}_${random}`;
  }
  /**
   * Safe BigInt comparison
   */
  static compareBigInt(a, b) {
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
  }
};

// src/billing/rate/contract.ts
import { DebugLogger as DebugLogger2 } from "@nuwa-ai/identity-kit";
var ContractRateProvider = class extends BaseRateProvider {
  constructor(contract, cacheTimeoutMs = 3e4) {
    super(cacheTimeoutMs);
    this.contract = contract;
    this.logger = DebugLogger2.get("ContractRateProvider");
  }
  async getPricePicoUSD(assetId) {
    const cached = this.getCachedPrice(assetId);
    if (cached !== null) {
      return cached;
    }
    try {
      const pricePicoUsd = await this.contract.getAssetPrice(assetId);
      if (pricePicoUsd <= 0n) {
        throw new RateNotFoundError(
          `Contract returned invalid price for ${assetId}: ${pricePicoUsd}`,
          assetId
        );
      }
      let assetInfo;
      try {
        assetInfo = await this.contract.getAssetInfo(assetId);
      } catch (error) {
        this.logger.warn(`Failed to get asset info for ${assetId}, using price only:`, error);
      }
      this.updateCache(assetId, pricePicoUsd, assetInfo);
      return pricePicoUsd;
    } catch (error) {
      const staleCache = this.cache.get(assetId);
      if (staleCache) {
        this.logger.warn(`Using stale contract price cache for ${assetId} due to error:`, error);
        return staleCache.price;
      }
      if (error instanceof RateProviderError) {
        throw error;
      }
      throw new RateProviderError(`Failed to get contract price for ${assetId}: ${error}`, assetId);
    }
  }
  async getAssetInfo(assetId) {
    const cached = this.cache.get(assetId);
    if (cached?.assetInfo) {
      return cached.assetInfo;
    }
    try {
      const info = await this.contract.getAssetInfo(assetId);
      this.updateCache(assetId, cached?.price || 0n, info);
      return info;
    } catch (error) {
      this.logger.warn(`Failed to get asset info for ${assetId}:`, error);
      throw error;
    }
  }
  /**
   * Force refresh price from contract (bypass cache)
   */
  async refreshPrice(assetId) {
    this.clearCache(assetId);
    return this.getPricePicoUSD(assetId);
  }
  /**
   * Get current chain ID from contract
   */
  async getChainId() {
    return this.contract.getChainId();
  }
};

// src/client/PaymentHubClient.ts
var PaymentHubClient = class {
  constructor(options) {
    this.contract = options.contract;
    this.signer = options.signer;
    this.defaultAssetId = options.defaultAssetId;
    this.rateProvider = options.rateProvider || new ContractRateProvider(this.contract);
  }
  // -------- Hub Operations --------
  /**
   * Deposit funds to the payment hub for the current signer
   */
  async deposit(assetId, amount, ownerDid) {
    if (!ownerDid) {
      ownerDid = await this.signer.getDid();
    }
    const params = {
      ownerDid,
      assetId,
      amount,
      signer: this.signer
    };
    const result = await this.contract.depositToHub(params);
    return { txHash: result.txHash };
  }
  /**
   * Withdraw funds from the payment hub to the owner's account
   * @param assetId Asset to withdraw
   * @param amount Amount to withdraw (0 = withdraw all)
   * @param recipient Optional recipient address/DID (defaults to owner's account)
   */
  async withdraw(assetId, amount, recipient) {
    const ownerDid = await this.signer.getDid();
    const params = {
      ownerDid,
      assetId,
      amount,
      recipient,
      signer: this.signer
    };
    const result = await this.contract.withdrawFromHub(params);
    return { txHash: result.txHash };
  }
  /**
   * Get balance of a specific asset in the payment hub
   */
  async getBalance(options = {}) {
    const ownerDid = options.ownerDid || await this.signer.getDid();
    const assetId = options.assetId || this.defaultAssetId;
    return this.contract.getHubBalance(ownerDid, assetId);
  }
  /**
   * Get balance and its value in picoUSD using a rate provider.
   * Returns: { assetId, balance, pricePicoUSD, balancePicoUSD }
   */
  async getBalanceWithUsd(options = {}) {
    const ownerDid = options.ownerDid || await this.signer.getDid();
    const assetId = options.assetId || this.defaultAssetId;
    const balance = await this.contract.getHubBalance(ownerDid, assetId);
    const pricePicoUSD = await this.rateProvider.getPricePicoUSD(assetId);
    const balancePicoUSD = balance * pricePicoUSD;
    return { assetId, balance, pricePicoUSD, balancePicoUSD };
  }
  /**
   * Get all balances in the hub for all assets
   */
  async getAllBalances(ownerDid) {
    if (!ownerDid) {
      ownerDid = await this.signer.getDid();
    }
    return this.contract.getAllHubBalances(ownerDid);
  }
  /**
   * Get active channel count for an asset (defaults to client's default asset)
   */
  async getActiveChannelCount(assetId, ownerDid) {
    if (!ownerDid) {
      ownerDid = await this.signer.getDid();
    }
    const targetAssetId = assetId || this.defaultAssetId;
    return this.contract.getActiveChannelCount(ownerDid, targetAssetId);
  }
  /**
   * Check if hub has sufficient balance for a payment
   */
  async hasBalance(options = {}) {
    const { requiredAmount = BigInt(0), ...balanceOptions } = options;
    const balance = await this.getBalance(balanceOptions);
    return balance >= requiredAmount;
  }
  /**
   * Transfer funds from this hub to another address's payment hub
   * @param recipientDid DID of the recipient
   * @param assetId Asset to transfer
   * @param amount Amount to transfer
   * @returns Transaction hash
   */
  async transfer(recipientDid, assetId, amount) {
    const senderDid = await this.signer.getDid();
    const params = {
      senderDid,
      receiverDid: recipientDid,
      assetId,
      amount,
      signer: this.signer
    };
    const result = await this.contract.transferToHub(params);
    return { txHash: result.txHash };
  }
  /**
   * Get unlocked balance (available for transfer) from this hub
   * @param options Optional balance options
   * @returns Unlocked balance amount
   */
  async getUnlockedBalance(options = {}) {
    const ownerDid = options.ownerDid || await this.signer.getDid();
    const assetId = options.assetId || this.defaultAssetId;
    return this.contract.getUnlockedBalance(ownerDid, assetId);
  }
};

// src/client/PaymentChannelPayerClient.ts
var PaymentChannelPayerClient = class {
  constructor(options) {
    this.contract = options.contract;
    this.signer = options.signer;
    this.keyId = options.keyId;
    this.defaultAssetId = "0x3::gas_coin::RGas";
    this.channelRepo = options.storageOptions.channelRepo;
  }
  // -------- Channel Management --------
  /**
   * Open a new payment channel
   */
  async openChannel(params) {
    const payerDid = await this.signer.getDid();
    const openParams = {
      payerDid,
      payeeDid: params.payeeDid,
      assetId: params.assetId,
      signer: this.signer
    };
    const result = await this.contract.openChannel(openParams);
    const channelInfo = {
      channelId: result.channelId,
      payerDid,
      payeeDid: params.payeeDid,
      assetId: params.assetId,
      epoch: BigInt(0),
      status: "active"
    };
    await this.channelRepo.setChannelMetadata(result.channelId, channelInfo);
    if (!this.activeChannelId) {
      this.activeChannelId = result.channelId;
    }
    return {
      channelId: result.channelId,
      channelInfo,
      txHash: result.txHash,
      events: result.events
    };
  }
  /**
   * Open a payment channel with a sub-channel in one transaction
   */
  async openChannelWithSubChannel(params) {
    const payerDid = await this.signer.getDid();
    const useFragment = params.vmIdFragment || this.extractFragment(this.keyId);
    const openParams = {
      payerDid,
      payeeDid: params.payeeDid,
      assetId: params.assetId,
      vmIdFragment: useFragment,
      signer: this.signer
    };
    const result = await this.contract.openChannelWithSubChannel(openParams);
    const channelInfo = {
      channelId: result.channelId,
      payerDid,
      payeeDid: params.payeeDid,
      assetId: params.assetId,
      epoch: BigInt(0),
      status: "active"
    };
    await this.channelRepo.setChannelMetadata(result.channelId, channelInfo);
    const subChannelInfo = {
      channelId: result.channelId,
      epoch: BigInt(0),
      vmIdFragment: useFragment,
      lastClaimedAmount: BigInt(0),
      lastConfirmedNonce: BigInt(0),
      lastUpdated: Date.now()
    };
    await this.channelRepo.updateSubChannelState(result.channelId, useFragment, subChannelInfo);
    if (!this.activeChannelId) {
      this.activeChannelId = result.channelId;
    }
    return {
      channelId: result.channelId,
      channelInfo,
      subChannelInfo,
      txHash: result.txHash,
      events: result.events
    };
  }
  /**
   * Authorize a sub-channel for an existing channel
   */
  async authorizeSubChannel(params) {
    const payerDid = await this.signer.getDid();
    const useFragment = params.vmIdFragment || this.extractFragment(this.keyId);
    const result = await this.contract.authorizeSubChannel({
      channelId: params.channelId,
      vmIdFragment: useFragment,
      signer: this.signer
    });
    const subChannelInfo = {
      channelId: params.channelId,
      epoch: BigInt(0),
      vmIdFragment: useFragment,
      lastClaimedAmount: BigInt(0),
      lastConfirmedNonce: BigInt(0),
      lastUpdated: Date.now()
    };
    await this.channelRepo.updateSubChannelState(params.channelId, useFragment, subChannelInfo);
    return {
      subChannelInfo,
      txHash: result.txHash,
      events: result.events
    };
  }
  /**
   * Set the active channel for subsequent operations
   * This is a convenience method for single-channel usage patterns
   */
  async setActiveChannel(channelId) {
    const metadata = await this.channelRepo.getChannelMetadata(channelId);
    if (!metadata) {
      throw new Error(`Channel ${channelId} not found in local storage`);
    }
    if (metadata.status !== "active") {
      throw new Error(`Channel ${channelId} is not active (status: ${metadata.status})`);
    }
    this.activeChannelId = channelId;
  }
  // -------- SubRAV Signing (for API clients) --------
  /**
   * Sign a SubRAV generated by a service provider
   * The caller should validate the SubRAV with previous rav before signing it
   * This is used by API clients to approve payment requests from services
   */
  async signSubRAV(subRAV) {
    await this.validateSubRAV(subRAV);
    const ourFragment = this.extractFragment(this.keyId);
    if (ourFragment !== subRAV.vmIdFragment) {
      throw new Error(`Key fragment mismatch: our ${ourFragment}, SubRAV ${subRAV.vmIdFragment}`);
    }
    const signedSubRAV = SubRAVSigner.sign(subRAV, this.signer, this.keyId);
    return signedSubRAV;
  }
  /**
   * Validate a SubRAV via on chain information
   */
  async validateSubRAV(subRAV) {
    const channelInfo = await this.contract.getChannelStatus({ channelId: subRAV.channelId });
    if (channelInfo.epoch !== subRAV.channelEpoch) {
      throw new Error(
        `Epoch mismatch: channel ${channelInfo.epoch}, SubRAV ${subRAV.channelEpoch}`
      );
    }
    if (subRAV.nonce === BigInt(0)) {
      throw new Error(`SubRAV nonce cannot be 0`);
    }
    const expectedChainId = await this.getChainId();
    if (subRAV.chainId !== expectedChainId) {
      throw new Error(`Chain ID mismatch: expected ${expectedChainId}, got ${subRAV.chainId}`);
    }
  }
  /**
   * Get a PaymentHubClient instance that reuses this client's contract and signer
   * This is the recommended way to access payment hub operations
   */
  getHubClient() {
    return new PaymentHubClient({
      contract: this.contract,
      signer: this.signer,
      defaultAssetId: this.defaultAssetId
    });
  }
  /**
   * Close a payment channel
   */
  async closeChannel(channelId, cooperative = true) {
    const result = await this.contract.closeChannel({
      channelId,
      cooperative,
      signer: this.signer
    });
    const metadata = await this.channelRepo.getChannelMetadata(channelId);
    if (metadata) {
      metadata.status = "closed";
      await this.channelRepo.setChannelMetadata(channelId, metadata);
    }
    if (this.activeChannelId === channelId) {
      this.activeChannelId = void 0;
    }
    return { txHash: result.txHash };
  }
  /**
   * Get channel information
   */
  async getChannelInfo(channelId) {
    return this.contract.getChannelStatus({ channelId });
  }
  /**
   * Get channels for the current payer
   */
  async getChannelsByPayer(payerDid) {
    const result = await this.channelRepo.listChannelMetadata({ payerDid });
    return result.items;
  }
  /**
   * Get the currently active channel ID
   */
  getActiveChannelId() {
    return this.activeChannelId;
  }
  getContract() {
    return this.contract;
  }
  // -------- Asset Information --------
  /**
   * Get asset information
   */
  async getAssetInfo(assetId) {
    return this.contract.getAssetInfo(assetId);
  }
  /**
   * Get asset price in pUSD
   */
  async getAssetPrice(assetId) {
    return this.contract.getAssetPrice(assetId);
  }
  async getChainId() {
    if (this.chainIdCache === void 0) {
      this.chainIdCache = await this.contract.getChainId();
    }
    return this.chainIdCache;
  }
  /**
   * Get on-chain sub-channel info (state) for the specified vmIdFragment
   */
  async getSubChannelInfo(channelId, vmIdFragment) {
    return this.contract.getSubChannel({ channelId, vmIdFragment });
  }
  // -------- Private Helpers --------
  /**
   * Extract fragment from full key ID
   */
  extractFragment(keyId) {
    const parts = keyId.split("#");
    return parts[parts.length - 1] || keyId;
  }
};

// src/client/PaymentRevenueClient.ts
var PaymentRevenueClient = class {
  constructor(options) {
    if (!options.contract) {
      throw new Error("Contract is required");
    }
    if (!options.signer) {
      throw new Error("Signer is required");
    }
    if (!options.defaultAssetId || options.defaultAssetId.trim() === "") {
      throw new Error("Default asset ID is required");
    }
    this.contract = options.contract;
    this.signer = options.signer;
    this.defaultAssetId = options.defaultAssetId;
    this.rateProvider = options.rateProvider || this.createDefaultRateProvider();
  }
  createDefaultRateProvider() {
    return {
      getPricePicoUSD: async (assetId) => {
        return BigInt(1e6);
      },
      getAssetInfo: async (assetId) => {
        return {
          assetId,
          symbol: assetId.split("::").pop() || "UNKNOWN",
          decimals: 8
        };
      },
      getLastUpdated: (assetId) => {
        return Date.now();
      },
      clearCache: async () => {
      }
    };
  }
  // -------- Revenue Hub Management --------
  /**
   * Create a revenue hub for the current signer
   */
  async createRevenueHub(ownerDid) {
    if (!ownerDid) {
      ownerDid = await this.signer.getDid();
    }
    const result = await this.contract.createRevenueHub(ownerDid, this.signer);
    return { txHash: result.txHash };
  }
  /**
   * Check if revenue hub exists for the current signer or specified owner
   */
  async revenueHubExists(ownerDid) {
    if (!ownerDid) {
      ownerDid = await this.signer.getDid();
    }
    return this.contract.revenueHubExists(ownerDid);
  }
  /**
   * Ensure revenue hub exists, create if not (lazy creation)
   */
  async ensureRevenueHub(ownerDid) {
    if (!ownerDid) {
      ownerDid = await this.signer.getDid();
    }
    const exists = await this.revenueHubExists(ownerDid);
    if (!exists) {
      await this.createRevenueHub(ownerDid);
    }
  }
  // -------- Revenue Operations --------
  /**
   * Withdraw revenue from hub to owner's account
   * @param assetId Asset to withdraw
   * @param amount Amount to withdraw (0 = withdraw all available)
   * @param ownerDid Optional owner DID (defaults to signer's DID)
   */
  async withdraw(assetId, amount, ownerDid) {
    if (!ownerDid) {
      ownerDid = await this.signer.getDid();
    }
    if (amount === BigInt(0)) {
      amount = await this.getRevenueBalance({ ownerDid, assetId });
    }
    const params = {
      ownerDid,
      assetId,
      amount,
      signer: this.signer
    };
    const result = await this.contract.withdrawRevenue(params);
    return { txHash: result.txHash };
  }
  /**
   * Preview withdrawal fees and net amount
   */
  async previewWithdrawal(assetId, amount, ownerDid) {
    if (!ownerDid) {
      ownerDid = await this.signer.getDid();
    }
    return this.contract.previewWithdrawalFee(ownerDid, assetId, amount);
  }
  // -------- Balance Queries --------
  /**
   * Get revenue balance for a specific asset
   */
  async getRevenueBalance(options = {}) {
    const ownerDid = options.ownerDid || await this.signer.getDid();
    const assetId = options.assetId || this.defaultAssetId;
    return this.contract.getRevenueBalance(ownerDid, assetId);
  }
  /**
   * Get revenue balance and its value in picoUSD using a rate provider.
   * Returns: { assetId, balance, pricePicoUSD, balancePicoUSD }
   */
  async getRevenueBalanceWithUsd(options = {}) {
    const ownerDid = options.ownerDid || await this.signer.getDid();
    const assetId = options.assetId || this.defaultAssetId;
    const balance = await this.contract.getRevenueBalance(ownerDid, assetId);
    const pricePicoUSD = await this.rateProvider.getPricePicoUSD(assetId);
    const balancePicoUSD = balance * pricePicoUSD;
    return { assetId, balance, pricePicoUSD, balancePicoUSD };
  }
  /**
   * Get revenue balance by source type
   */
  async getRevenueBySource(options) {
    const ownerDid = options.ownerDid || await this.signer.getDid();
    const assetId = options.assetId || this.defaultAssetId;
    return this.contract.getRevenueBySource(ownerDid, options.sourceType, assetId);
  }
  /**
   * Check if revenue hub has sufficient balance for withdrawal
   */
  async hasRevenue(options = {}) {
    const { requiredAmount = BigInt(0), ...balanceOptions } = options;
    const balance = await this.getRevenueBalance(balanceOptions);
    return balance >= requiredAmount;
  }
  // -------- Utility Methods --------
  /**
   * Create a revenue source for tracking purposes
   */
  createRevenueSource(sourceType, sourceId, description) {
    return {
      sourceType,
      sourceId,
      description: description || `Revenue from ${sourceType}`
    };
  }
};

// src/client/PaymentChannelPayeeClient.ts
var PaymentChannelPayeeClient = class {
  constructor(options) {
    this.contract = options.contract;
    this.signer = options.signer;
    this.didResolver = options.didResolver;
    this.defaultAssetId = "0x3::gas_coin::RGas";
    this.revenueContract = options.revenueContract;
    this.channelRepo = options.storageOptions.channelRepo;
    this.ravRepo = options.storageOptions.ravRepo;
    this.pendingSubRAVRepo = options.storageOptions.pendingSubRAVRepo;
  }
  // -------- Public accessors for internal components --------
  /**
   * Get the RAV repository used by this client
   * This is needed for ClaimScheduler to access the same RAV data
   */
  getRAVRepository() {
    return this.ravRepo;
  }
  getDidResolver() {
    return this.didResolver;
  }
  /**
   * Get the contract instance used by this client
   * This is needed for ClaimScheduler to perform claims
   */
  getContract() {
    return this.contract;
  }
  /**
   * Get the channel repository used by this client
   */
  getChannelRepository() {
    return this.channelRepo;
  }
  /**
   * Get sub-channel state by (channelId, vmIdFragment)
   * If not found locally, try deriving from on-chain and sync into repository.
   */
  async getSubChannelState(channelId, vmIdFragment) {
    const local = await this.channelRepo.getSubChannelState(channelId, vmIdFragment);
    if (local) return local;
    try {
      const [channelInfo, subInfo] = await Promise.all([
        this.getChannelInfoCached(channelId),
        this.contract.getSubChannel({ channelId, vmIdFragment })
      ]);
      const derived = {
        channelId,
        epoch: channelInfo.epoch,
        vmIdFragment: subInfo.vmIdFragment,
        lastClaimedAmount: subInfo.lastClaimedAmount,
        lastConfirmedNonce: subInfo.lastConfirmedNonce,
        lastUpdated: Date.now()
      };
      await this.channelRepo.updateSubChannelState(channelId, vmIdFragment, derived);
      return derived;
    } catch {
      return null;
    }
  }
  /**
   * Get the payee DID from this client's signer
   */
  async getPayeeDid() {
    return await this.signer.getDid();
  }
  /**
   * Get the pending SubRAV repository used by this client
   */
  getPendingSubRAVRepository() {
    return this.pendingSubRAVRepo;
  }
  // -------- Claims Management --------
  /**
   * Claim payment from a channel using a signed SubRAV
   */
  async claimFromChannel(options) {
    const { signedSubRAV } = options;
    const claimParams = {
      signedSubRAV,
      signer: this.signer
    };
    const result = await this.contract.claimFromChannel(claimParams);
    return result;
  }
  /**
   * Batch claim multiple SubRAVs
   * Falls back to individual claims
   */
  async batchClaimFromChannels(signedSubRAVs) {
    const results = [];
    for (const signedSubRAV of signedSubRAVs) {
      try {
        const result = await this.claimFromChannel({ signedSubRAV });
        results.push(result);
      } catch (error) {
        throw error;
      }
    }
    return results;
  }
  // -------- Channel Management --------
  /**
   * Close a payment channel (from payee side)
   */
  async closeChannel(channelId, cooperative = true) {
    const result = await this.contract.closeChannel({
      channelId,
      cooperative,
      signer: this.signer
    });
    const metadata = await this.channelRepo.getChannelMetadata(channelId);
    if (metadata) {
      metadata.status = "closed";
      await this.channelRepo.setChannelMetadata(channelId, metadata);
    }
    return { txHash: result.txHash };
  }
  /**
   * Get channel information
   */
  async getChannelInfo(channelId) {
    return this.contract.getChannelStatus({ channelId });
  }
  /**
   * Get channel info with caching using ChannelRepository
   * This checks local storage first, then falls back to chain if not found or stale
   */
  async getChannelInfoCached(channelId, forceRefresh = false) {
    if (!forceRefresh) {
      const cachedMetadata = await this.channelRepo.getChannelMetadata(channelId);
      if (cachedMetadata) {
        return cachedMetadata;
      }
    }
    try {
      const channelInfo = await this.contract.getChannelStatus({ channelId });
      await this.channelRepo.setChannelMetadata(channelId, channelInfo);
      return channelInfo;
    } catch (error) {
      if (!forceRefresh) {
        const staleMetadata = await this.channelRepo.getChannelMetadata(channelId);
        if (staleMetadata) {
          return staleMetadata;
        }
      }
      throw error;
    }
  }
  /**
   * List active channels where this payee is involved
   */
  async listActiveChannels(options = {}) {
    const payeeDid = await this.signer.getDid();
    const storageResult = await this.channelRepo.listChannelMetadata(
      {
        payeeDid,
        status: options.status || "active"
      },
      {
        offset: options.offset || 0,
        limit: options.limit || 50
      }
    );
    const channelInfos = [];
    for (const metadata of storageResult.items) {
      try {
        const info = await this.getChannelInfoCached(metadata.channelId);
        channelInfos.push(info);
      } catch (error) {
      }
    }
    return channelInfos;
  }
  /**
   * Sync channel state from blockchain to local storage
   * This force-refreshes the cached channel metadata
   */
  async syncChannelState(channelId) {
    try {
      await this.getChannelInfoCached(channelId, true);
    } catch (error) {
      throw error;
    }
  }
  // -------- Asset Information --------
  /**
   * Get asset information
   */
  async getAssetInfo(assetId) {
    return this.contract.getAssetInfo(assetId);
  }
  /**
   * Get asset price in pUSD
   */
  async getAssetPrice(assetId) {
    return this.contract.getAssetPrice(assetId);
  }
  // -------- Private Helpers --------
  /**
   * Extract vmIdFragment from full keyId (e.g., "did:example:payer#key1" → "key1")
   */
  extractVmIdFragment(keyId) {
    const parts = keyId.split("#");
    if (parts.length !== 2) {
      throw new Error(
        `Invalid keyId format: ${keyId}. Expected format: "did:example:payer#fragment"`
      );
    }
    return parts[1];
  }
  /**
   * Get cached chain ID or fetch from contract
   */
  async getChainId() {
    if (this.chainIdCache === void 0) {
      this.chainIdCache = await this.contract.getChainId();
    }
    return this.chainIdCache;
  }
  /**
   * Get a PaymentHubClient instance that reuses this client's contract and signer
   * Primarily useful for balance queries and withdraw operations on the payee side
   */
  getHubClient() {
    return new PaymentHubClient({
      contract: this.contract,
      signer: this.signer,
      defaultAssetId: this.defaultAssetId
    });
  }
  /**
   * Get a PaymentRevenueClient instance for revenue management operations
   * Requires revenueContract to be provided in constructor options
   * @throws Error if revenueContract was not provided
   */
  getRevenueClient() {
    if (!this.revenueContract) {
      throw new Error(
        "PaymentRevenueClient requires revenueContract to be provided in PaymentChannelPayeeClientOptions"
      );
    }
    return new PaymentRevenueClient({
      contract: this.revenueContract,
      signer: this.signer,
      defaultAssetId: this.defaultAssetId
    });
  }
  /**
   * Get channel health metrics
   */
  async getChannelHealth(channelId) {
    const issues = [];
    try {
      const channelInfo = await this.getChannelInfoCached(channelId);
      if (channelInfo.status === "closed") {
        issues.push("Channel is closed");
      }
      if (channelInfo.status === "closing") {
        issues.push("Channel is in closing state");
      }
      return {
        isHealthy: issues.length === 0,
        issues,
        pendingClaims: 0
        // Would query from contract/storage
      };
    } catch (error) {
      return {
        isHealthy: false,
        issues: [`Channel access failed: ${error}`],
        pendingClaims: 0
      };
    }
  }
};

// src/rooch/RoochPaymentChannelContract.ts
import {
  Args,
  RoochAddress as RoochAddress2
} from "@roochnetwork/rooch-sdk";
import { bcs as bcs3 } from "@roochnetwork/rooch-sdk";
import { parseDid as parseDid2 } from "@nuwa-ai/identity-kit";

// src/types/api.ts
var ErrorCode = {
  // Authentication & Authorization
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  // Payment related
  PAYMENT_REQUIRED: "PAYMENT_REQUIRED",
  INSUFFICIENT_FUNDS: "INSUFFICIENT_FUNDS",
  CONFLICT: "CONFLICT",
  // General errors
  NOT_FOUND: "NOT_FOUND",
  BAD_REQUEST: "BAD_REQUEST",
  INTERNAL_ERROR: "INTERNAL_ERROR",
  SERVICE_UNAVAILABLE: "SERVICE_UNAVAILABLE"
};

// src/errors/PaymentKitError.ts
var PaymentKitError = class extends Error {
  constructor(code, message, httpStatus = 500, details) {
    super(message);
    this.name = "PaymentKitError";
    this.code = code;
    this.httpStatus = httpStatus;
    this.details = details;
  }
  /**
   * Convert to standard API error format
   */
  toApiError() {
    return {
      code: this.code,
      message: this.message,
      details: this.details,
      httpStatus: this.httpStatus
    };
  }
};
function toApiError(error) {
  if (error instanceof PaymentKitError) {
    return error.toApiError();
  }
  if (error instanceof Error) {
    return {
      code: ErrorCode.INTERNAL_ERROR,
      message: error.message,
      details: error.stack,
      httpStatus: 500
    };
  }
  return {
    code: ErrorCode.INTERNAL_ERROR,
    message: String(error),
    httpStatus: 500
  };
}
function createSuccessResponse(data) {
  return {
    success: true,
    data,
    timestamp: (/* @__PURE__ */ new Date()).toISOString()
  };
}
function createErrorResponse(error) {
  return {
    success: false,
    error,
    timestamp: (/* @__PURE__ */ new Date()).toISOString()
  };
}

// src/errors/RoochErrorMapper.ts
function httpStatusForErrorCode(code) {
  switch (code) {
    case ErrorCode.PAYMENT_REQUIRED:
    case ErrorCode.INSUFFICIENT_FUNDS:
      return 402;
    case ErrorCode.BAD_REQUEST:
      return 400;
    case ErrorCode.UNAUTHORIZED:
      return 401;
    case ErrorCode.FORBIDDEN:
      return 403;
    case ErrorCode.NOT_FOUND:
      return 404;
    case ErrorCode.CONFLICT:
      return 409;
    case ErrorCode.SERVICE_UNAVAILABLE:
      return 503;
    case ErrorCode.INTERNAL_ERROR:
    default:
      return 500;
  }
}
function createError(code, message, details, httpStatus) {
  const status = httpStatus ?? httpStatusForErrorCode(code);
  return new PaymentKitError(code, message, status, details);
}
function badRequest(message, details) {
  return createError(ErrorCode.BAD_REQUEST, message, details);
}
function notFound(message, details) {
  return createError(ErrorCode.NOT_FOUND, message, details);
}
function serviceUnavailable(message, details) {
  return createError(ErrorCode.SERVICE_UNAVAILABLE, message, details);
}
function internalError(message, details) {
  return createError(ErrorCode.INTERNAL_ERROR, message, details);
}
function wrapUnknownError(operation, error) {
  if (error instanceof PaymentKitError) {
    return error;
  }
  const mapped = tryMapRoochRpcError(operation, error);
  if (mapped) {
    return mapped;
  }
  const details = normalizeUnknown(error);
  const message = `[${operation}] ${details.message ?? "Unexpected error"}`;
  return internalError(message, { operation, error: details });
}
function normalizeUnknown(err) {
  if (err instanceof Error) {
    return { name: err.name, message: err.message, stack: err.stack };
  }
  if (typeof err === "object" && err !== null) {
    try {
      return JSON.parse(JSON.stringify(err));
    } catch {
      return { error: String(err) };
    }
  }
  return { error: String(err) };
}
function mapTxFailureToPaymentKitError(operation, executionInfo) {
  const baseDetails = { operation, executionInfo };
  const status = executionInfo?.status;
  if (!status) {
    return internalError(`[${operation}] Missing execution status`, baseDetails);
  }
  switch (status.type) {
    case "executed":
      return internalError(`[${operation}] mapTxFailure called for executed tx`, baseDetails);
    case "outofgas":
      return badRequest(`[${operation}] Out of gas`, baseDetails);
    case "moveabort": {
      const abortCode = parseAbortCode(status.abort_code);
      const location = status.location;
      if (isPaymentChannelModule(location)) {
        return mapPaymentChannelAbort(operation, abortCode, baseDetails);
      }
      return badRequest(
        `[${operation}] Move abort ${String(status.abort_code)} at ${location}`,
        baseDetails
      );
    }
    case "executionfailure": {
      return internalError(
        `[${operation}] Execution failure at ${status.location}#${status.function}@${status.code_offset}`,
        baseDetails
      );
    }
    case "miscellaneouserror":
    default:
      return internalError(`[${operation}] Transaction failed`, baseDetails);
  }
}
function isPaymentChannelModule(location) {
  if (!location) return false;
  return location.includes("payment_channel");
}
function parseAbortCode(code) {
  if (typeof code === "number") return code;
  if (typeof code === "string") {
    if (/^0x[0-9a-fA-F]+$/.test(code)) {
      try {
        return parseInt(code, 16);
      } catch {
        return null;
      }
    }
    const n = Number(code);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
function mapPaymentChannelAbort(operation, abortCode, details) {
  switch (abortCode) {
    case 1:
      return createError(ErrorCode.FORBIDDEN, `[${operation}] Not receiver`, details);
    case 2:
      return createError(ErrorCode.CONFLICT, `[${operation}] Channel not active`, details);
    case 3:
      return createError(ErrorCode.BAD_REQUEST, `[${operation}] Invalid sender signature`, details);
    case 4:
      return createError(ErrorCode.BAD_REQUEST, `[${operation}] VM not found`, details);
    case 5:
      return createError(ErrorCode.FORBIDDEN, `[${operation}] Insufficient permission`, details);
    case 6:
      return createError(ErrorCode.CONFLICT, `[${operation}] Invalid payment hub`, details);
    case 7:
      return createError(ErrorCode.CONFLICT, `[${operation}] Invalid nonce`, details);
    case 8:
      return createError(ErrorCode.CONFLICT, `[${operation}] Invalid amount`, details);
    case 9:
      return createError(ErrorCode.FORBIDDEN, `[${operation}] Hub owner mismatch`, details);
    case 10:
      return createError(
        ErrorCode.CONFLICT,
        `[${operation}] Challenge period not elapsed`,
        details
      );
    case 11:
      return createError(ErrorCode.CONFLICT, `[${operation}] Channel already cancelling`, details);
    case 12:
      return createError(ErrorCode.CONFLICT, `[${operation}] Channel already closed`, details);
    case 13:
      return createError(
        ErrorCode.INSUFFICIENT_FUNDS,
        `[${operation}] Insufficient hub balance`,
        details
      );
    case 14:
      return createError(ErrorCode.FORBIDDEN, `[${operation}] Not sender`, details);
    case 15:
      return createError(ErrorCode.NOT_FOUND, `[${operation}] Sub-channel not authorized`, details);
    case 16:
      return createError(
        ErrorCode.FORBIDDEN,
        `[${operation}] Only sender can authorize VM`,
        details
      );
    case 17:
      return createError(
        ErrorCode.CONFLICT,
        `[${operation}] Verification method already exists`,
        details
      );
    case 18:
      return createError(ErrorCode.CONFLICT, `[${operation}] Channel already exists`, details);
    case 19:
      return createError(
        ErrorCode.CONFLICT,
        `[${operation}] Active channel exists for coin`,
        details
      );
    case 20:
      return createError(ErrorCode.BAD_REQUEST, `[${operation}] Sender must have DID`, details);
    case 21:
      return createError(ErrorCode.BAD_REQUEST, `[${operation}] Mismatched coin type`, details);
    case 22:
      return createError(ErrorCode.CONFLICT, `[${operation}] Invalid channel epoch`, details);
    case 23:
      return createError(ErrorCode.BAD_REQUEST, `[${operation}] Invalid chain id`, details);
    case 24:
      return createError(
        ErrorCode.BAD_REQUEST,
        `[${operation}] Unsupported SubRAV version`,
        details
      );
    default:
      return badRequest(`[${operation}] Move abort ${String(abortCode)}`, details);
  }
}
function tryMapRoochRpcError(operation, error) {
  const e = error;
  const message = typeof e?.message === "string" ? e.message : "";
  if (includesAny(message, ["maintenance mode", "Service Maintenance", "service is in maintenance"])) {
    return serviceUnavailable(`[${operation}] Rooch service is in maintenance`, {
      operation,
      message
    });
  }
  const subStatus = parseSubStatusFromMessage(message);
  if (subStatus != null) {
    switch (subStatus) {
      case 1004:
        return createError(ErrorCode.INSUFFICIENT_FUNDS, `[${operation}] Insufficient gas`, {
          operation,
          message,
          subStatus
        });
      default:
        return badRequest(`[${operation}] Execution aborted (sub status ${subStatus})`, {
          operation,
          message,
          subStatus
        });
    }
  }
  if (typeof e?.code === "number") {
    const code = e.code;
    if (code === 1004) {
      return createError(ErrorCode.INSUFFICIENT_FUNDS, `[${operation}] Insufficient gas`, {
        operation,
        code,
        message
      });
    }
  }
  if (includesAny(message.toLowerCase(), ["insufficient gas", "out of gas", "insufficient balance"])) {
    return createError(ErrorCode.INSUFFICIENT_FUNDS, `[${operation}] Insufficient gas`, {
      operation,
      message
    });
  }
  return null;
}
function parseSubStatusFromMessage(message) {
  if (!message) return null;
  const m = message.match(/sub status\s+(\d+)/i);
  if (m && m[1]) {
    const n = Number(m[1]);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
function includesAny(str, needles) {
  for (const n of needles) {
    if (str.includes(n)) return true;
  }
  return false;
}

// src/rooch/RoochContractBase.ts
import {
  RoochClient,
  Transaction,
  getRoochNodeUrl
} from "@roochnetwork/rooch-sdk";
import {
  DebugLogger as DebugLogger3,
  DidAccountSigner,
  isSignerInterface
} from "@nuwa-ai/identity-kit";
var RoochContractBase = class {
  constructor(options, defaultContractAddress, loggerName) {
    const rpcUrl = options.rpcUrl || this.getDefaultRpcUrl(options.network || "test");
    this.client = new RoochClient({ url: rpcUrl });
    this.contractAddress = options.contractAddress || defaultContractAddress;
    this.logger = DebugLogger3.get(loggerName);
    if (options.debug) {
      this.logger.setLevel("debug");
    }
    this.logger.debug(`${loggerName} initialized with rpcUrl: ${rpcUrl}`);
  }
  /**
   * Create a new transaction instance
   */
  createTransaction() {
    return new Transaction();
  }
  /**
   * Convert SignerInterface to Rooch Signer
   * Handles both SignerInterface and direct Signer instances
   */
  async convertSigner(signer) {
    if (isSignerInterface(signer)) {
      if (signer instanceof DidAccountSigner) {
        return signer;
      }
      return DidAccountSigner.create(signer);
    }
    return signer;
  }
  /**
   * Get default RPC URL for a given network
   * Maps our network names to Rooch SDK network names
   */
  getDefaultRpcUrl(network) {
    const networkMap = {
      local: "localnet",
      dev: "devnet",
      test: "testnet",
      main: "mainnet"
    };
    const roochNetwork = networkMap[network] || network;
    return getRoochNodeUrl(roochNetwork);
  }
  /**
   * Get the RoochClient instance
   */
  getClient() {
    return this.client;
  }
  /**
   * Get the contract address
   */
  getContractAddress() {
    return this.contractAddress;
  }
  /**
   * Get the logger instance
   */
  getLogger() {
    return this.logger;
  }
};

// src/rooch/RoochPaymentChannelContract.ts
var CloseProofSchema = bcs3.struct("CloseProof", {
  vm_id_fragment: bcs3.string(),
  accumulated_amount: bcs3.u256(),
  nonce: bcs3.u64(),
  sender_signature: bcs3.vector(bcs3.u8())
});
var CloseProofsSchema = bcs3.struct("CloseProofs", {
  proofs: bcs3.vector(CloseProofSchema)
});
var CancelProofSchema = bcs3.struct("CancelProof", {
  vm_id_fragment: bcs3.string(),
  accumulated_amount: bcs3.u256(),
  nonce: bcs3.u64()
});
var CancelProofsSchema = bcs3.struct("CancelProofs", {
  proofs: bcs3.vector(CancelProofSchema)
});
var RGAS_CANONICAL_TAG = "0x0000000000000000000000000000000000000000000000000000000000000003::gas_coin::RGas";
var PAYMENT_CHANNEL_MODULE = "payment_channel";
var DEFAULT_PAYMENT_CHANNEL_ADDRESS = "0x3";
var DEFAULT_LOCK_AMOUNT_PER_CHANNEL = 1000000n;
var RoochPaymentChannelContract = class extends RoochContractBase {
  constructor(options = {}) {
    super(
      { ...options, contractAddress: options.contractAddress || DEFAULT_PAYMENT_CHANNEL_ADDRESS },
      DEFAULT_PAYMENT_CHANNEL_ADDRESS,
      "RoochPaymentChannelContract"
    );
  }
  async openChannel(params) {
    try {
      this.getLogger().debug("Opening payment channel with params:", params);
      const payerParsed = parseDid2(params.payerDid);
      const payeeParsed = parseDid2(params.payeeDid);
      if (payerParsed.method !== "rooch") {
        throw badRequest(`Invalid payer DID method: expected 'rooch', got '${payerParsed.method}'`);
      }
      if (payeeParsed.method !== "rooch") {
        throw badRequest(`Invalid payee DID method: expected 'rooch', got '${payeeParsed.method}'`);
      }
      const signer = await this.convertSigner(params.signer);
      const transaction = this.createTransaction();
      transaction.callFunction({
        target: `${this.getContractAddress()}::${PAYMENT_CHANNEL_MODULE}::open_channel_entry`,
        typeArgs: [params.assetId],
        // CoinType as type argument
        args: [Args.address(payeeParsed.identifier)],
        maxGas: 1e8
      });
      this.getLogger().debug("Executing openChannel transaction");
      const result = await this.getClient().signAndExecuteTransaction({
        transaction,
        signer,
        option: { withOutput: true }
      });
      if (result.execution_info.status.type !== "executed") {
        throw mapTxFailureToPaymentKitError("openChannel", result.execution_info);
      }
      const channelId = calcChannelObjectId(
        payerParsed.identifier,
        payeeParsed.identifier,
        params.assetId
      );
      return {
        channelId,
        txHash: result.execution_info.tx_hash || "",
        blockHeight: BigInt(0),
        // TODO: Extract from result if available
        events: result.output?.events
      };
    } catch (error) {
      this.getLogger().error("Error opening channel:", error);
      throw wrapUnknownError("openChannel", error);
    }
  }
  async openChannelWithSubChannel(params) {
    try {
      this.getLogger().debug("Opening payment channel with sub-channel in one step:", params);
      const payerParsed = parseDid2(params.payerDid);
      const payeeParsed = parseDid2(params.payeeDid);
      if (payerParsed.method !== "rooch") {
        throw badRequest(`Invalid payer DID method: expected 'rooch', got '${payerParsed.method}'`);
      }
      if (payeeParsed.method !== "rooch") {
        throw badRequest(`Invalid payee DID method: expected 'rooch', got '${payeeParsed.method}'`);
      }
      const signer = await this.convertSigner(params.signer);
      const transaction = this.createTransaction();
      transaction.callFunction({
        target: `${this.getContractAddress()}::${PAYMENT_CHANNEL_MODULE}::open_channel_with_sub_channel_entry`,
        typeArgs: [params.assetId],
        // CoinType as type argument
        args: [Args.address(payeeParsed.identifier), Args.string(params.vmIdFragment)],
        maxGas: 1e8
      });
      this.getLogger().debug("Executing openChannelWithSubChannel transaction");
      const result = await this.getClient().signAndExecuteTransaction({
        transaction,
        signer,
        option: { withOutput: true }
      });
      if (result.execution_info.status.type !== "executed") {
        throw mapTxFailureToPaymentKitError("openChannelWithSubChannel", result.execution_info);
      }
      const channelId = calcChannelObjectId(
        payerParsed.identifier,
        payeeParsed.identifier,
        params.assetId
      );
      return {
        channelId,
        txHash: result.execution_info.tx_hash || "",
        blockHeight: BigInt(0),
        // TODO: Extract from result if available
        events: result.output?.events
      };
    } catch (error) {
      this.getLogger().error("Error opening channel with sub-channel:", error);
      throw wrapUnknownError("openChannelWithSubChannel", error);
    }
  }
  async authorizeSubChannel(params) {
    try {
      this.getLogger().debug("Authorizing sub-channel with params:", params);
      const signer = await this.convertSigner(params.signer);
      const transaction = this.createTransaction();
      transaction.callFunction({
        target: `${this.getContractAddress()}::${PAYMENT_CHANNEL_MODULE}::authorize_sub_channel_entry`,
        args: [Args.objectId(params.channelId), Args.string(params.vmIdFragment)],
        maxGas: 1e8
      });
      this.getLogger().debug("Executing authorizeSubChannel transaction");
      const result = await this.getClient().signAndExecuteTransaction({
        transaction,
        signer,
        option: { withOutput: true }
      });
      if (result.execution_info.status.type !== "executed") {
        throw mapTxFailureToPaymentKitError("authorizeSubChannel", result.execution_info);
      }
      return {
        txHash: result.execution_info.tx_hash || "",
        blockHeight: BigInt(0),
        events: result.output?.events
      };
    } catch (error) {
      this.getLogger().error("Error authorizing sub-channel:", error);
      throw wrapUnknownError("authorizeSubChannel", error);
    }
  }
  async claimFromChannel(params) {
    try {
      this.getLogger().debug("Claiming from channel with params:", params);
      const signer = await this.convertSigner(params.signer);
      const { subRav, signature } = params.signedSubRAV;
      const transaction = this.createTransaction();
      transaction.callFunction({
        target: `${this.getContractAddress()}::${PAYMENT_CHANNEL_MODULE}::claim_from_channel_entry`,
        args: [
          Args.objectId(subRav.channelId),
          Args.string(subRav.vmIdFragment),
          Args.u256(subRav.accumulatedAmount),
          Args.u64(subRav.nonce),
          Args.vec("u8", Array.from(signature))
        ],
        maxGas: 1e8
      });
      this.getLogger().debug("Executing claimFromChannel transaction");
      const result = await this.getClient().signAndExecuteTransaction({
        transaction,
        signer,
        option: { withOutput: true }
      });
      if (result.execution_info.status.type !== "executed") {
        throw mapTxFailureToPaymentKitError("claimFromChannel", result.execution_info);
      }
      const claimedAmount = this.parseClaimedAmountFromEvents(result.output?.events);
      return {
        txHash: result.execution_info.tx_hash || "",
        claimedAmount,
        blockHeight: BigInt(0),
        events: result.output?.events
      };
    } catch (error) {
      this.getLogger().error("Error claiming from channel:", error);
      throw wrapUnknownError("claimFromChannel", error);
    }
  }
  async closeChannel(params) {
    try {
      this.getLogger().debug("Closing channel with params:", params);
      const signer = await this.convertSigner(params.signer);
      const transaction = this.createTransaction();
      if (params.cooperative && params.closeProofs) {
        const serializedProofs = CloseProofsSchema.serialize(params.closeProofs).toBytes();
        transaction.callFunction({
          target: `${this.getContractAddress()}::${PAYMENT_CHANNEL_MODULE}::close_channel_entry`,
          args: [Args.objectId(params.channelId), Args.vec("u8", serializedProofs)],
          maxGas: 1e8
        });
      } else {
        transaction.callFunction({
          target: `${this.getContractAddress()}::${PAYMENT_CHANNEL_MODULE}::initiate_cancellation_entry`,
          args: [Args.objectId(params.channelId)],
          maxGas: 1e8
        });
      }
      this.getLogger().debug("Executing closeChannel transaction");
      const result = await this.getClient().signAndExecuteTransaction({
        transaction,
        signer,
        option: { withOutput: true }
      });
      if (result.execution_info.status.type !== "executed") {
        throw mapTxFailureToPaymentKitError("closeChannel", result.execution_info);
      }
      return {
        txHash: result.execution_info.tx_hash || "",
        blockHeight: BigInt(0),
        events: result.output?.events
      };
    } catch (error) {
      this.getLogger().error("Error closing channel:", error);
      throw wrapUnknownError("closeChannel", error);
    }
  }
  async getChannelStatus(params) {
    try {
      this.getLogger().debug("Getting channel status for channel:", params.channelId);
      const channelObject = await this.getClient().getObjectStates({
        ids: [params.channelId]
      });
      if (!channelObject || channelObject.length === 0 || !channelObject[0]) {
        throw notFound(`Channel ${params.channelId} not found`);
      }
      const channelState = channelObject[0];
      const channelData = parsePaymentChannelData(channelState.value);
      const statusString = this.convertChannelStatus(channelData.status);
      const channel_sender = new RoochAddress2(channelData.sender);
      const channel_receiver = new RoochAddress2(channelData.receiver);
      return {
        channelId: params.channelId,
        payerDid: `did:rooch:${channel_sender.toBech32Address()}`,
        payeeDid: `did:rooch:${channel_receiver.toBech32Address()}`,
        assetId: channelData.coin_type,
        epoch: channelData.channel_epoch,
        status: statusString
      };
    } catch (error) {
      this.getLogger().error("Error getting channel status:", error);
      throw wrapUnknownError("getChannelStatus", error);
    }
  }
  async getSubChannel(params) {
    try {
      this.getLogger().debug("Getting sub-channel info for:", params);
      const channelObject = await this.getClient().getObjectStates({
        ids: [params.channelId]
      });
      if (!channelObject || channelObject.length === 0 || !channelObject[0]) {
        throw notFound(`Channel ${params.channelId} not found`);
      }
      const channelData = parsePaymentChannelData(channelObject[0].value);
      const subChannelData = await this.getSubChannelData(
        channelData.sub_channels,
        params.vmIdFragment
      );
      if (!subChannelData) {
        throw notFound(
          `Sub-channel ${params.vmIdFragment} not found in channel ${params.channelId}`
        );
      }
      return {
        channelId: params.channelId,
        epoch: channelData.channel_epoch,
        vmIdFragment: params.vmIdFragment,
        publicKey: subChannelData.pk_multibase,
        methodType: subChannelData.method_type,
        lastClaimedAmount: subChannelData.last_claimed_amount,
        lastConfirmedNonce: subChannelData.last_confirmed_nonce
      };
    } catch (error) {
      this.getLogger().error("Error getting sub-channel info:", error);
      throw wrapUnknownError("getSubChannel", error);
    }
  }
  async getAssetInfo(assetId) {
    this.getLogger().debug("Getting asset info for:", assetId);
    let canonicalAssetId = normalizeAssetId(assetId);
    if (canonicalAssetId === RGAS_CANONICAL_TAG) {
      let assetInfo = {
        assetId: canonicalAssetId,
        symbol: this.parseAssetSymbol(assetId),
        decimals: 8
      };
      return assetInfo;
    }
    throw badRequest(`Unsupported asset type: ${assetId}`);
  }
  async getAssetPrice(assetId) {
    try {
      this.getLogger().debug("Getting asset price for:", assetId);
      const canonicalAssetId = normalizeAssetId(assetId);
      if (canonicalAssetId === RGAS_CANONICAL_TAG) {
        return BigInt(100);
      }
      throw badRequest(`Unsupported asset type: ${assetId}`);
    } catch (error) {
      this.getLogger().error("Error getting asset price:", error);
      throw wrapUnknownError("getAssetPrice", error);
    }
  }
  async getChainId() {
    try {
      this.getLogger().debug("Getting chain ID");
      const chainId = await this.getClient().getChainId();
      return BigInt(chainId);
    } catch (error) {
      this.getLogger().error("Error getting chain ID:", error);
      throw wrapUnknownError("getChainId", error);
    }
  }
  async depositToHub(params) {
    try {
      this.getLogger().debug("Depositing to payment hub with params:", params);
      const ownerParsed = parseDid2(params.ownerDid);
      if (ownerParsed.method !== "rooch") {
        throw badRequest(`Invalid owner DID method: expected 'rooch', got '${ownerParsed.method}'`);
      }
      const signer = await this.convertSigner(params.signer);
      const transaction = this.createTransaction();
      transaction.callFunction({
        target: `${this.getContractAddress()}::${PAYMENT_CHANNEL_MODULE}::deposit_to_hub_entry`,
        typeArgs: [params.assetId],
        // CoinType as type argument
        args: [Args.address(ownerParsed.identifier), Args.u256(params.amount)],
        maxGas: 1e8
      });
      this.getLogger().debug("Executing depositToHub transaction");
      const result = await this.getClient().signAndExecuteTransaction({
        transaction,
        signer,
        option: { withOutput: true }
      });
      if (result.execution_info.status.type !== "executed") {
        throw mapTxFailureToPaymentKitError("depositToHub", result.execution_info);
      }
      return {
        txHash: result.execution_info.tx_hash || "",
        blockHeight: BigInt(0),
        events: result.output?.events
      };
    } catch (error) {
      this.getLogger().error("Error depositing to hub:", error);
      throw wrapUnknownError("depositToHub", error);
    }
  }
  async withdrawFromHub(params) {
    try {
      this.getLogger().debug("Withdrawing from payment hub with params:", params);
      const ownerParsed = parseDid2(params.ownerDid);
      if (ownerParsed.method !== "rooch") {
        throw badRequest(`Invalid owner DID method: expected 'rooch', got '${ownerParsed.method}'`);
      }
      const signer = await this.convertSigner(params.signer);
      const transaction = this.createTransaction();
      transaction.callFunction({
        target: `${this.getContractAddress()}::${PAYMENT_CHANNEL_MODULE}::withdraw_from_hub_entry`,
        typeArgs: [params.assetId],
        // CoinType as type argument
        args: [Args.u256(params.amount)],
        maxGas: 1e8
      });
      this.getLogger().debug("Executing withdrawFromHub transaction");
      const result = await this.getClient().signAndExecuteTransaction({
        transaction,
        signer,
        option: { withOutput: true }
      });
      if (result.execution_info.status.type !== "executed") {
        throw mapTxFailureToPaymentKitError("withdrawFromHub", result.execution_info);
      }
      return {
        txHash: result.execution_info.tx_hash || "",
        blockHeight: BigInt(0),
        events: result.output?.events
      };
    } catch (error) {
      this.getLogger().error("Error withdrawing from hub:", error);
      throw wrapUnknownError("withdrawFromHub", error);
    }
  }
  async getHubBalance(ownerDid, assetId) {
    try {
      this.getLogger().debug("Getting hub balance for DID:", ownerDid, "asset:", assetId);
      const paymentHub = await this.getPaymentHub(ownerDid);
      if (!paymentHub) {
        this.getLogger().debug("PaymentHub not found for owner:", ownerDid);
        return BigInt(0);
      }
      this.getLogger().debug("MultiCoinStore ID extracted:", paymentHub.multi_coin_store);
      const fieldKey = deriveCoinTypeFieldKey(assetId);
      const fieldStates = await this.getClient().getFieldStates({
        objectId: paymentHub.multi_coin_store,
        fieldKey: [fieldKey]
      });
      if (!fieldStates || fieldStates.length === 0 || !fieldStates[0]) {
        this.getLogger().debug("Asset not found in multi_coin_store:", assetId);
        return BigInt(0);
      }
      const fieldState = fieldStates[0];
      const fieldValue = fieldState.value;
      if (typeof fieldValue === "string" && fieldValue.startsWith("0x")) {
        try {
          const parsed = parseDynamicFieldCoinStore(fieldValue);
          return safeBalanceToBigint(parsed.value.balance.value);
        } catch (parseError) {
          this.getLogger().warn("Failed to parse CoinStoreField BCS data:", parseError);
          return BigInt(0);
        }
      } else if (fieldValue && typeof fieldValue === "object" && "value" in fieldValue) {
        const coinStoreField = fieldValue.value;
        if (coinStoreField && typeof coinStoreField === "object" && "balance" in coinStoreField) {
          return BigInt(coinStoreField.balance);
        }
      }
      this.getLogger().warn("Could not parse balance from field state");
      return BigInt(0);
    } catch (error) {
      this.getLogger().error("Error getting hub balance:", error);
      return BigInt(0);
    }
  }
  async getAllHubBalances(ownerDid) {
    try {
      this.getLogger().debug("Getting all hub balances for DID:", ownerDid);
      const paymentHub = await this.getPaymentHub(ownerDid);
      if (!paymentHub) {
        this.getLogger().debug("PaymentHub not found for owner:", ownerDid);
        return {};
      }
      this.getLogger().debug(
        "MultiCoinStore ID extracted for all balances:",
        paymentHub.multi_coin_store
      );
      const balances = {};
      let cursor = null;
      const pageSize = 100;
      const fieldStates = await this.getClient().listFieldStates({
        objectId: paymentHub.multi_coin_store,
        cursor,
        limit: pageSize.toString()
      });
      if (!fieldStates || !fieldStates.data) {
        return balances;
      }
      for (const state of fieldStates.data) {
        try {
          const fieldValue = state.state.value;
          if (typeof fieldValue === "string" && fieldValue.startsWith("0x")) {
            try {
              const parsed = parseDynamicFieldCoinStore(fieldValue);
              const coinType = parsed.name;
              const balance = safeBalanceToBigint(parsed.value.balance.value);
              if (coinType && balance > 0) {
                balances[coinType] = balance;
              }
            } catch (parseError) {
              this.getLogger().warn("Failed to parse CoinStoreField BCS data:", parseError);
              continue;
            }
          } else {
            this.getLogger().warn("Could not parse balance from field state");
            throw new Error("Could not parse balance from field state");
          }
        } catch (parseError) {
          this.getLogger().warn("Failed to parse field state for balance:", parseError);
          continue;
        }
      }
      return balances;
    } catch (error) {
      this.getLogger().error("Error getting all hub balances:", error);
      return {};
    }
  }
  async getActiveChannelCount(ownerDid, assetId) {
    try {
      this.getLogger().debug("Getting active channel count for DID:", ownerDid, "asset:", assetId);
      const paymentHub = await this.getPaymentHub(ownerDid);
      if (!paymentHub) {
        this.getLogger().debug("PaymentHub not found for owner:", ownerDid);
        return 0;
      }
      return await this.getActiveChannelCountFromHub(paymentHub, assetId);
    } catch (error) {
      this.getLogger().error("Error getting active channel count:", error);
      return 0;
    }
  }
  async getActiveChannelCountFromHub(paymentHub, assetId) {
    try {
      const fieldKey = deriveCoinTypeFieldKey(assetId);
      const fieldStates = await this.getClient().getFieldStates({
        objectId: paymentHub.active_channels,
        fieldKey: [fieldKey]
      });
      if (!fieldStates || fieldStates.length === 0 || !fieldStates[0]) {
        return 0;
      }
      const fieldValue = fieldStates[0].value;
      try {
        if (typeof fieldValue === "string") {
          const parsed = parseDynamicFieldU64(fieldValue);
          return parsed.value;
        }
        if (fieldValue && typeof fieldValue === "object" && "value" in fieldValue) {
          const parsedValue = fieldValue.value;
          if (typeof parsedValue === "number") {
            return parsedValue;
          }
          if (typeof parsedValue === "bigint") {
            return Number(parsedValue);
          }
          if (typeof parsedValue === "string") {
            return Number(parsedValue);
          }
        }
      } catch (parseError) {
        this.getLogger().warn("Failed to parse active channel count field:", parseError);
        return 0;
      }
      return 0;
    } catch (error) {
      this.getLogger().error("Error querying active channel count from hub:", error);
      return 0;
    }
  }
  async getLockedUnit(assetId) {
    try {
      const normalizedAssetId = normalizeAssetId(assetId);
      const result = await this.getClient().executeViewFunction({
        target: `${this.getContractAddress()}::${PAYMENT_CHANNEL_MODULE}::get_locked_unit`,
        args: [Args.string(normalizedAssetId)]
      });
      const rv = result?.return_values?.[0];
      const decoded = rv?.decoded_value ?? rv?.value;
      if (typeof decoded === "bigint") {
        return decoded;
      }
      if (typeof decoded === "number") {
        return BigInt(decoded);
      }
      if (typeof decoded === "string") {
        if (decoded.startsWith("0x")) {
          return parseU256FromBCS(decoded);
        }
        return BigInt(decoded);
      }
      return DEFAULT_LOCK_AMOUNT_PER_CHANNEL;
    } catch (error) {
      this.getLogger().warn("Failed to fetch locked unit from view, using default", error);
      return DEFAULT_LOCK_AMOUNT_PER_CHANNEL;
    }
  }
  /**
   * Transfer funds from sender's payment hub to receiver's payment hub
   * Calls the payment_channel::transfer_to_hub_entry function
   */
  async transferToHub(params) {
    try {
      this.getLogger().debug("Transferring from hub to hub:", {
        senderDid: params.senderDid,
        receiverDid: params.receiverDid,
        assetId: params.assetId,
        amount: params.amount.toString()
      });
      const { senderDid, receiverDid, assetId, amount, signer } = params;
      const senderParsed = parseDid2(senderDid);
      if (senderParsed.method !== "rooch") {
        throw badRequest(
          `Invalid sender DID method: expected 'rooch', got '${senderParsed.method}'`
        );
      }
      const receiverParsed = parseDid2(receiverDid);
      if (receiverParsed.method !== "rooch") {
        throw badRequest(
          `Invalid receiver DID method: expected 'rooch', got '${receiverParsed.method}'`
        );
      }
      const receiverAddress = receiverParsed.identifier;
      const roochSigner = await this.convertSigner(signer);
      const tx = this.createTransaction();
      tx.callFunction({
        target: `${this.getContractAddress()}::${PAYMENT_CHANNEL_MODULE}::transfer_to_hub_entry`,
        typeArgs: [assetId],
        args: [Args.address(receiverAddress), Args.u256(amount)],
        maxGas: 1e8
      });
      const result = await this.getClient().signAndExecuteTransaction({
        transaction: tx,
        signer: roochSigner
      });
      if (result.execution_info.status.type !== "executed") {
        throw mapTxFailureToPaymentKitError("transferToHub", result.execution_info);
      }
      const txHash = result.execution_info.tx_hash || "";
      this.getLogger().debug("Hub transfer completed:", { txHash });
      return { txHash };
    } catch (error) {
      this.getLogger().error("Error transferring from hub to hub:", error);
      throw wrapUnknownError("transferToHub", error);
    }
  }
  /**
   * Get unlocked balance in payment hub (excluding locked amounts for active channels)
   * This calculates the available balance by subtracting locked amounts from total balance
   */
  async getUnlockedBalance(ownerDid, assetId) {
    try {
      this.getLogger().debug("Getting unlocked balance:", { ownerDid, assetId });
      const totalBalance = await this.getHubBalance(ownerDid, assetId);
      const activeChannelsCount = await this.getActiveChannelCount(ownerDid, assetId);
      const lockAmountPerChannel = await this.getLockedUnit(assetId);
      const lockedBalance = BigInt(activeChannelsCount) * lockAmountPerChannel;
      const unlockedBalance = totalBalance > lockedBalance ? totalBalance - lockedBalance : BigInt(0);
      this.getLogger().debug("Unlocked balance calculated:", {
        totalBalance: totalBalance.toString(),
        activeChannelsCount,
        lockedBalance: lockedBalance.toString(),
        unlockedBalance: unlockedBalance.toString()
      });
      return unlockedBalance;
    } catch (error) {
      this.getLogger().error("Error getting unlocked balance:", error);
      return BigInt(0);
    }
  }
  // PaymentHub ID calculation and field key derivation are now in ChannelUtils.ts
  // Get PaymentHub object for a given owner DID
  async getPaymentHub(ownerDid) {
    try {
      this.getLogger().debug("Getting PaymentHub for DID:", ownerDid);
      const ownerParsed = parseDid2(ownerDid);
      if (ownerParsed.method !== "rooch") {
        throw new Error(`Invalid owner DID method: expected 'rooch', got '${ownerParsed.method}'`);
      }
      const hubId = calculatePaymentHubId(ownerParsed.identifier);
      const hubObjectViews = await this.getClient().getObjectStates({ ids: [hubId] });
      if (!hubObjectViews || hubObjectViews.length === 0 || !hubObjectViews[0]) {
        this.getLogger().debug("PaymentHub not found for owner:", ownerDid);
        return null;
      }
      const hubObjectView = hubObjectViews[0];
      try {
        const hubValue = hubObjectView.value;
        if (typeof hubValue === "string" && hubValue.startsWith("0x")) {
          const parsed = parsePaymentHubData(hubValue);
          return parsed;
        } else {
          throw new Error("Unexpected PaymentHub value format");
        }
      } catch (parseError) {
        this.getLogger().error("Failed to parse PaymentHub object:", parseError);
        return null;
      }
    } catch (error) {
      this.getLogger().error("Error getting PaymentHub:", error);
      return null;
    }
  }
  // Helper methods
  parseChannelIdFromEvents(events) {
    if (!events) {
      throw internalError("No events found to parse channel ID");
    }
    for (const event of events) {
      if (event.event_type.includes("PaymentChannelOpenedEvent")) {
        try {
          const eventDataHex = event.event_data.startsWith("0x") ? event.event_data.slice(2) : event.event_data;
          const eventDataBytes = new Uint8Array(
            eventDataHex.match(/.{1,2}/g)?.map((byte) => parseInt(byte, 16)) || []
          );
          return event.event_data;
        } catch (error) {
          this.getLogger().error("Failed to parse channel ID from event:", error);
          throw internalError(`Failed to parse channel ID from event: ${error}`);
        }
      }
    }
    throw internalError("PaymentChannelOpenedEvent not found in transaction events");
  }
  parseClaimedAmountFromEvents(events) {
    if (!events) {
      return BigInt(0);
    }
    for (const event of events) {
      if (event.event_type.includes("ChannelClaimedEvent")) {
        return BigInt(0);
      }
    }
    return BigInt(0);
  }
  // parseChannelData method moved to ChannelUtils.parsePaymentChannelData
  /**
   * Get sub-channel data by VM ID fragment
   * @param subChannelsTableId The ObjectID of the sub_channels Table
   * @param vmIdFragment The VM ID fragment to query
   * @returns SubChannel data or null if not found
   */
  async getSubChannelData(subChannelsTableId, vmIdFragment) {
    try {
      this.getLogger().debug(
        `Getting sub-channel data for: ${vmIdFragment} from table: ${subChannelsTableId}`
      );
      const fieldKey = deriveFieldKeyFromString(vmIdFragment);
      const fieldStates = await this.getClient().getFieldStates({
        objectId: subChannelsTableId,
        fieldKey: [fieldKey]
      });
      if (!fieldStates || fieldStates.length === 0 || !fieldStates[0]) {
        this.getLogger().debug(
          `Sub-channel ${vmIdFragment} not found in table ${subChannelsTableId}`
        );
        return null;
      }
      const fieldState = fieldStates[0];
      const dynamicField = parseDynamicFieldSubChannel(fieldState.value);
      return dynamicField.value;
    } catch (error) {
      this.getLogger().error("Error getting sub-channel data:", error);
      return null;
    }
  }
  convertChannelStatus(status) {
    switch (status) {
      case 0:
        return "active";
      case 1:
        return "closing";
      case 2:
        return "closed";
      default:
        return "closed";
    }
  }
  parseAssetSymbol(assetId) {
    const parts = assetId.split("::");
    return parts[parts.length - 1] || "UNKNOWN";
  }
};

// src/factory/chainFactory.ts
var PaymentChannelFactory = class {
  /**
   * Create a chain-specific contract implementation
   */
  static createContract(config) {
    switch (config.chain) {
      case "rooch":
        return new RoochPaymentChannelContract({
          rpcUrl: config.rpcUrl,
          network: config.network,
          contractAddress: config.contractAddress,
          debug: config.debug
        });
      default:
        throw new Error(`Unsupported chain: ${config.chain}`);
    }
  }
  /**
   * Create a payment channel client with chain-specific contract
   */
  static createClient(options) {
    const contract = this.createContract(options.chainConfig);
    return new PaymentChannelPayerClient({
      contract,
      signer: options.signer,
      keyId: options.keyId,
      storageOptions: options.storageOptions
    });
  }
};
function createPaymentChannelClient(options) {
  return PaymentChannelFactory.createClient(options);
}

// src/middlewares/http/HttpPaymentCodec.ts
import { MultibaseCodec as MultibaseCodec2 } from "@nuwa-ai/identity-kit";

// src/codecs/PaymentCodec.ts
var CodecError = class extends Error {
  constructor(message, cause) {
    super(message);
    this.cause = cause;
    this.name = "CodecError";
  }
};
var EncodingError = class extends CodecError {
  constructor(message, cause) {
    super(`Encoding failed: ${message}`, cause);
    this.name = "EncodingError";
  }
};
var DecodingError = class extends CodecError {
  constructor(message, cause) {
    super(`Decoding failed: ${message}`, cause);
    this.name = "DecodingError";
  }
};

// src/middlewares/http/HttpPaymentCodec.ts
var _HttpPaymentCodec = class _HttpPaymentCodec {
  /**
   * Encode payment header payload for HTTP request header (new interface)
   */
  encodePayload(payload) {
    try {
      return _HttpPaymentCodec.buildRequestHeader(payload);
    } catch (error) {
      throw new EncodingError(
        "Failed to encode HTTP payment data",
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }
  /**
   * Decode HTTP request header to payment header payload (new interface)
   */
  decodePayload(encoded) {
    try {
      return _HttpPaymentCodec.parseRequestHeader(encoded);
    } catch (error) {
      throw new DecodingError(
        "Failed to decode HTTP payment data",
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }
  /**
   * Encode SubRAV proposal for HTTP response header
   */
  encodeResponse(subRAV, cost, serviceTxRef, metadata) {
    try {
      const payload = {
        subRav: subRAV,
        cost,
        serviceTxRef,
        version: 1,
        ...metadata
      };
      return _HttpPaymentCodec.buildResponseHeader(payload);
    } catch (error) {
      throw new EncodingError(
        "Failed to encode HTTP response payment data",
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }
  /**
   * Encode protocol-level error for HTTP response header
   */
  encodeError(error, metadata) {
    try {
      const payload = {
        error,
        clientTxRef: metadata?.clientTxRef,
        serviceTxRef: metadata?.serviceTxRef,
        version: metadata?.version ?? 1
      };
      return _HttpPaymentCodec.buildResponseHeader(payload);
    } catch (error2) {
      throw new EncodingError(
        "Failed to encode HTTP response error data",
        error2 instanceof Error ? error2 : new Error(String(error2))
      );
    }
  }
  /**
   * Decode HTTP response header to SubRAV proposal
   */
  decodeResponse(encoded) {
    try {
      const payload = _HttpPaymentCodec.parseResponseHeader(encoded);
      const out = {
        subRav: payload.subRav,
        cost: payload.cost,
        costUsd: payload.costUsd,
        clientTxRef: payload.clientTxRef,
        serviceTxRef: payload.serviceTxRef,
        error: payload.error,
        version: payload.version
      };
      return out;
    } catch (error) {
      throw new DecodingError(
        "Failed to decode HTTP response payment data",
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }
  // ============================================================================
  // Static HTTP Header Building Methods
  // ============================================================================
  /**
   * Build HTTP request header value
   */
  static buildRequestHeader(payload) {
    const serializable = this.toJSONRequest(payload);
    const json = JSON.stringify(serializable);
    return MultibaseCodec2.encodeBase64url(json);
  }
  /**
   * Parse HTTP request header value
   */
  static parseRequestHeader(headerValue) {
    try {
      const json = MultibaseCodec2.decodeBase64urlToString(headerValue);
      const data = JSON.parse(json);
      return this.fromJSONRequest(data);
    } catch (error) {
      throw new Error(`Failed to parse request header: ${error}`);
    }
  }
  /**
   * Build HTTP response header value
   */
  static buildResponseHeader(payload) {
    const serializable = this.toJSONResponse(payload);
    const json = JSON.stringify(serializable);
    return MultibaseCodec2.encodeBase64url(json);
  }
  /**
   * Parse HTTP response header value
   */
  static parseResponseHeader(headerValue) {
    try {
      const json = MultibaseCodec2.decodeBase64urlToString(headerValue);
      const data = JSON.parse(json);
      return this.fromJSONResponse(data);
    } catch (error) {
      throw new Error(`Failed to parse response header: ${error}`);
    }
  }
  // ============================================================================
  // Structured JSON helpers for PaymentResponsePayload (for MCP and others)
  // Note: subRav is serialized as JSON (not encoded string) for readability.
  // In a future revision, we may add an additional encoded field (e.g., subRavHeader)
  // without breaking compatibility.
  // ============================================================================
  static toJSONResponse(payload) {
    const out = {
      version: payload.version ?? 1,
      clientTxRef: payload.clientTxRef,
      serviceTxRef: payload.serviceTxRef
    };
    if (payload.subRav) {
      out.subRav = this.serializeSubRAV(payload.subRav);
    }
    if (payload.cost !== void 0) {
      out.cost = payload.cost.toString();
    }
    if (payload.costUsd !== void 0) {
      out.costUsd = payload.costUsd.toString();
    }
    if (payload.error) {
      out.error = payload.error;
    }
    return out;
  }
  static fromJSONResponse(data) {
    const payload = {
      version: data?.version ? Number(data.version) : 1,
      clientTxRef: data?.clientTxRef,
      serviceTxRef: data?.serviceTxRef
    };
    if (data?.subRav) {
      payload.subRav = this.deserializeSubRAV(data.subRav);
    }
    const costStr = data.cost;
    if (costStr !== void 0) payload.cost = BigInt(costStr);
    if (data.costUsd !== void 0) payload.costUsd = BigInt(data.costUsd);
    if (data?.error) {
      payload.error = data.error;
    }
    return payload;
  }
  // Structured JSON helpers for PaymentRequestPayload (for MCP and others)
  static toJSONRequest(payload) {
    const out = {
      version: payload.version ?? 1,
      clientTxRef: payload.clientTxRef
    };
    if (payload.maxAmount !== void 0) out.maxAmount = payload.maxAmount.toString();
    if (payload.signedSubRav) out.signedSubRav = this.serializeSignedSubRAV(payload.signedSubRav);
    return out;
  }
  static fromJSONRequest(data) {
    if (!data?.clientTxRef) throw new Error("clientTxRef is required in payment header");
    const out = {
      version: data.version ?? 1,
      clientTxRef: data.clientTxRef,
      maxAmount: data.maxAmount ? BigInt(data.maxAmount) : BigInt(0),
      signedSubRav: data.signedSubRav ? this.deserializeSignedSubRAV(data.signedSubRav) : void 0
    };
    return out;
  }
  /**
   * Get HTTP header name for payment data
   */
  static getHeaderName() {
    return this.HEADER_NAME;
  }
  /**
   * Check if request contains payment data
   */
  static hasPaymentData(headers) {
    const headerName = this.getHeaderName();
    return !!(headers[headerName.toLowerCase()] || headers[headerName]);
  }
  /**
   * Extract payment header value from request headers
   */
  static extractPaymentHeader(headers) {
    const headerName = this.getHeaderName();
    const headerValue = headers[headerName.toLowerCase()] || headers[headerName];
    if (Array.isArray(headerValue)) {
      return headerValue[0] || null;
    }
    return headerValue || null;
  }
  /**
   * Extract payment data from request headers
   */
  static extractPaymentData(headers) {
    const headerValue = headers[this.getHeaderName().toLowerCase()] || headers[this.getHeaderName()];
    if (!headerValue) {
      return null;
    }
    try {
      return this.parseRequestHeader(headerValue);
    } catch (error) {
      throw new Error(`Invalid payment channel header: ${error}`);
    }
  }
  /**
   * Add payment data to response headers
   */
  static addPaymentData(headers, payload) {
    const headerValue = this.buildResponseHeader(payload);
    return {
      ...headers,
      [this.getHeaderName()]: headerValue
    };
  }
  /**
   * Validate payment requirements for a request
   */
  static validatePaymentRequirement(paymentData, requiredAmount) {
    if (!paymentData) {
      return { valid: false, error: "Payment required" };
    }
    if (paymentData.maxAmount < requiredAmount) {
      return { valid: false, error: "Insufficient payment allowance" };
    }
    return { valid: true };
  }
  // ============================================================================
  // Private Serialization Helpers
  // ============================================================================
  /**
   * Helper: Serialize SubRAV for JSON transport
   */
  static serializeSubRAV(subRav) {
    return {
      version: subRav.version.toString(),
      chainId: subRav.chainId.toString(),
      channelId: subRav.channelId,
      channelEpoch: subRav.channelEpoch.toString(),
      vmIdFragment: subRav.vmIdFragment,
      accumulatedAmount: subRav.accumulatedAmount.toString(),
      nonce: subRav.nonce.toString()
    };
  }
  /**
   * Helper: Deserialize SubRAV from JSON transport
   */
  static deserializeSubRAV(data) {
    return {
      version: parseInt(data.version),
      chainId: BigInt(data.chainId),
      channelId: data.channelId,
      channelEpoch: BigInt(data.channelEpoch),
      vmIdFragment: data.vmIdFragment,
      accumulatedAmount: BigInt(data.accumulatedAmount),
      nonce: BigInt(data.nonce)
    };
  }
  /**
   * Helper: Serialize SignedSubRAV for JSON transport
   */
  static serializeSignedSubRAV(signedSubRav) {
    if (!signedSubRav) {
      return void 0;
    }
    return {
      subRav: this.serializeSubRAV(signedSubRav.subRav),
      signature: MultibaseCodec2.encodeBase64url(signedSubRav.signature)
    };
  }
  /**
   * Helper: Deserialize SignedSubRAV from JSON transport
   */
  static deserializeSignedSubRAV(data) {
    if (!data) {
      return void 0;
    }
    return {
      subRav: this.deserializeSubRAV(data.subRav),
      signature: MultibaseCodec2.decodeBase64url(data.signature)
    };
  }
  // ==========================================================================
  // MCP Content helpers (resource content for payment payload)
  // ==========================================================================
  /** Build a FastMCP-compatible resource content item carrying payment payload */
  static buildMcpPaymentResource(payload) {
    return {
      type: "resource",
      resource: {
        uri: this.MCP_PAYMENT_URI,
        mimeType: this.MCP_PAYMENT_MIME,
        text: JSON.stringify(payload)
      }
    };
  }
  /** Try to parse payment payload from a FastMCP content array */
  static parseMcpPaymentFromContents(contents) {
    if (!Array.isArray(contents)) return void 0;
    const item = contents.find(
      (c) => c && c.type === "resource" && c.resource && (c.resource.uri === this.MCP_PAYMENT_URI || c.resource.mimeType === this.MCP_PAYMENT_MIME)
    );
    try {
      const text = item?.resource?.text;
      if (!text || typeof text !== "string") return void 0;
      return JSON.parse(text);
    } catch {
      return void 0;
    }
  }
  /** Attach payment resource to an existing content array (non-mutating) */
  static attachPaymentResource(contents, payload) {
    const out = Array.isArray(contents) ? [...contents] : [];
    if (payload) {
      out.push(this.buildMcpPaymentResource(payload));
    }
    return out;
  }
};
_HttpPaymentCodec.HEADER_NAME = "X-Payment-Channel-Data";
// MCP content constants
_HttpPaymentCodec.MCP_PAYMENT_URI = "nuwa:payment";
_HttpPaymentCodec.MCP_PAYMENT_MIME = "application/vnd.nuwa.payment+json";
var HttpPaymentCodec = _HttpPaymentCodec;

// src/billing/core/rule-matcher.ts
var regexCache = /* @__PURE__ */ new Map();
function findRule(meta, rules) {
  for (const rule of rules) {
    if (matchesRule(meta, rule)) {
      return rule;
    }
  }
  return void 0;
}
function getCachedRegex(pattern) {
  let regex = regexCache.get(pattern);
  if (!regex) {
    try {
      regex = new RegExp(pattern);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(
        `Invalid regular expression pattern "${pattern}": ${errorMessage}. Please check your billing rule configuration and ensure the pathRegex field contains a valid regular expression.`
      );
    }
    if (regexCache.size >= 100) {
      const firstKey = regexCache.keys().next().value;
      if (firstKey) {
        regexCache.delete(firstKey);
      }
    }
    regexCache.set(pattern, regex);
  }
  return regex;
}
function matchesRule(meta, rule) {
  if (rule.default) {
    return true;
  }
  if (!rule.when) {
    return false;
  }
  const when = rule.when;
  if (when.path && meta.path !== when.path) {
    return false;
  }
  if (when.pathRegex) {
    try {
      if (rule.originalRegex && rule.originalRegex instanceof RegExp) {
        if (!rule.originalRegex.test(meta.path || "")) {
          return false;
        }
      } else {
        const regex = getCachedRegex(when.pathRegex);
        if (!regex.test(meta.path || "")) {
          return false;
        }
      }
    } catch (error) {
      console.error(`[BillingRule] Failed to compile regex pattern for rule "${rule.id}":`, error);
      return false;
    }
  }
  if (when.method && meta.method !== when.method) {
    return false;
  }
  for (const [key, value] of Object.entries(when)) {
    if (["path", "pathRegex", "model", "method", "assetId"].includes(key)) {
      continue;
    }
    if (meta[key] !== value) {
      return false;
    }
  }
  return true;
}

// src/middlewares/http/HttpBillingMiddleware.ts
import { DebugLogger as DebugLogger4 } from "@nuwa-ai/identity-kit";
var HttpBillingMiddleware = class _HttpBillingMiddleware {
  constructor(config) {
    this.config = config;
    this.processor = config.processor;
    this.codec = new HttpPaymentCodec();
    this.logger = DebugLogger4.get("HttpBillingMiddleware");
    this.logger.setLevel(config.debug ? "debug" : "info");
  }
  /**
   * New unified billing handler using the three-step process
   */
  async handleWithNewAPI(req) {
    try {
      this.log("\u{1F50D} Processing HTTP payment request with new API:", req.method, req.path);
      const rule = this.findBillingRule(req);
      if (!rule) {
        this.log("\u{1F4DD} No billing rule matched - proceeding without payment processing");
        return null;
      }
      const paymentData = this.extractPaymentData(req.headers);
      const ctx = this.buildBillingContext(req, paymentData || void 0, rule);
      const processedCtx = await this.processor.preProcess(ctx);
      if (processedCtx.state && processedCtx.state.signedSubRavVerified === false) {
        this.log("\u{1F6A8} Payment verification failed during pre-processing");
        return null;
      }
      this.log(`\u{1F4CB} Request pre-processed for ${rule.strategy.type}`);
      if (processedCtx.state) {
        this.log(`\u{1F4CB} Context state:`, {
          signedSubRavVerified: processedCtx.state.signedSubRavVerified,
          cost: processedCtx.state.cost,
          responsePayload: !!processedCtx.state.responsePayload
        });
      }
      return processedCtx;
    } catch (error) {
      this.log("\u{1F6A8} New API payment processing error:", error);
      return null;
    }
  }
  /**
   * Complete billing settlement synchronously (Step B & C) - for on-headers use
   */
  settleBillingSync(ctx, usage, resAdapter) {
    try {
      this.log("\u{1F504} Settling billing synchronously with usage:", usage);
      const settledCtx = this.processor.settle(ctx, usage);
      if (ctx.meta.billingRule && !ctx.meta.billingRule.paymentRequired) {
        this.log("\u{1F4DD} Free route - skipping payment header generation");
        return true;
      }
      const payload = settledCtx.state?.responsePayload;
      const header = payload ? HttpPaymentCodec.buildResponseHeader(payload) : void 0;
      if (!header) {
        this.log("\u26A0\uFE0F No payment header generated during settlement");
        return false;
      }
      if (resAdapter) {
        resAdapter.setHeader("X-Payment-Channel-Data", header);
        this.log("\u2705 Payment header added to response synchronously");
      }
      return true;
    } catch (error) {
      this.log("\u{1F6A8} Synchronous billing settlement error:", error);
      return false;
    }
  }
  /** Save settlement payload for later polling by client (streaming scenarios) */
  saveSettlement(ctx) {
    const state = ctx.state || {};
    const payload = {
      subRav: state.unsignedSubRav,
      cost: state.cost,
      // costUsd is not persisted in state; optional
      clientTxRef: ctx.meta.clientTxRef,
      serviceTxRef: state.serviceTxRef,
      version: 1
    };
    if (!payload.clientTxRef) return;
  }
  /**
   * Persist billing results (Step D) - async persistence only
   */
  async persistBilling(ctx) {
    try {
      this.log("\u{1F4BE} Persisting billing results");
      if (ctx.state?.unsignedSubRav) {
        await this.processor.persist(ctx);
        this.log("\u2705 Billing results persisted successfully");
      } else {
        this.log("\u26A0\uFE0F No SubRAV to persist");
      }
    } catch (error) {
      this.log("\u{1F6A8} Billing persistence error:", error);
      throw error;
    }
  }
  /**
   * Find matching billing rule for the request
   */
  findBillingRule(req) {
    if (!this.config.ruleProvider) {
      throw new Error(
        "RuleProvider is required for auto-detection. Please configure it in HttpBillingMiddlewareConfig."
      );
    }
    const meta = {
      path: req.path,
      method: req.method,
      // Include other relevant metadata for rule matching
      httpQuery: req.query,
      httpBody: req.body,
      httpHeaders: req.headers
    };
    return findRule(meta, this.config.ruleProvider.getRules());
  }
  /**
   * Extract payment data from HTTP request headers
   */
  extractPaymentData(headers) {
    const headerValue = HttpPaymentCodec.extractPaymentHeader(headers);
    if (!headerValue) {
      return null;
    }
    try {
      return this.codec.decodePayload(headerValue);
    } catch (error) {
      throw new Error(`Invalid payment channel header: ${error}`);
    }
  }
  /**
   * Build billing context from HTTP request
   */
  buildBillingContext(req, paymentData, billingRule) {
    return {
      serviceId: this.processor.getServiceId(),
      meta: {
        operation: `${req.method.toLowerCase()}:${req.path}`,
        // Pre-matched billing rule (V2 optimization)
        billingRule,
        // Payment data from HTTP headers
        maxAmount: paymentData?.maxAmount,
        signedSubRav: paymentData?.signedSubRav,
        clientTxRef: paymentData?.clientTxRef || crypto.randomUUID(),
        // DIDAuth (ExpressPaymentKit attaches didInfo onto req)
        didInfo: req.didInfo,
        // HTTP-specific metadata for billing rules
        method: req.method,
        path: req.path,
        // Also keep HTTP-prefixed versions for other uses
        httpMethod: req.method,
        httpPath: req.path,
        httpQuery: req.query,
        httpBody: req.body,
        httpHeaders: req.headers
      }
    };
  }
  /**
   * Map error code to HTTP status code
   */
  mapErrorToHttpStatus(errorCode) {
    return httpStatusFor(errorCode);
  }
  /**
   * Get payment processing statistics
   */
  getProcessingStats() {
    return this.processor.getProcessingStats();
  }
  /**
   * Find pending SubRAV proposal
   */
  async findPendingProposal(channelId, vmIdFragment, nonce) {
    return await this.processor.findPendingProposal(channelId, vmIdFragment, nonce);
  }
  /**
   * Find the latest pending SubRAV proposal for a channel
   */
  async findLatestPendingProposal(channelId, vmIdFragment) {
    return await this.processor.findLatestPendingProposal(channelId, vmIdFragment);
  }
  /**
   * Create ExpressJS ResponseAdapter
   */
  createExpressResponseAdapter(res) {
    return {
      setStatus: (code) => {
        res.status(code);
        return this.createExpressResponseAdapter(res);
      },
      json: (obj) => {
        res.json(obj);
      },
      setHeader: (name, value) => {
        res.setHeader(name, value);
        return this.createExpressResponseAdapter(res);
      }
    };
  }
  /**
   * Debug logging
   */
  log(...args) {
    this.logger.debug(...args);
  }
  /**
   * Static factory method for creating middleware
   */
  static create(config) {
    return new _HttpBillingMiddleware(config);
  }
};

// src/rooch/RoochPaymentRevenueContract.ts
import { Args as Args2 } from "@roochnetwork/rooch-sdk";
import { parseDid as parseDid3 } from "@nuwa-ai/identity-kit";
var DEFAULT_PAYMENT_REVENUE_MODULE = "0x3::payment_revenue";
var RoochPaymentRevenueContract = class extends RoochContractBase {
  constructor(options = {}) {
    super(options, DEFAULT_PAYMENT_REVENUE_MODULE, "RoochPaymentRevenueContract");
  }
  // -------- Revenue Hub Management --------
  async createRevenueHub(ownerDid, signer) {
    try {
      this.logger.debug("Creating revenue hub for DID:", ownerDid);
      const ownerParsed = parseDid3(ownerDid);
      if (ownerParsed.method !== "rooch") {
        throw badRequest(`Invalid owner DID method: expected 'rooch', got '${ownerParsed.method}'`);
      }
      const roochSigner = await this.convertSigner(signer);
      const transaction = this.createTransaction();
      transaction.callFunction({
        target: `${this.getContractAddress()}::create_revenue_hub`,
        typeArgs: [],
        args: [],
        maxGas: 1e8
      });
      this.logger.debug("Executing createRevenueHub transaction");
      const result = await this.getClient().signAndExecuteTransaction({
        transaction,
        signer: roochSigner,
        option: { withOutput: true }
      });
      if (result.execution_info.status.type !== "executed") {
        throw mapTxFailureToPaymentKitError("createRevenueHub", result.execution_info);
      }
      return {
        txHash: result.execution_info.tx_hash || "",
        blockHeight: BigInt(0),
        events: result.output?.events
      };
    } catch (error) {
      this.logger.error("Error creating revenue hub:", error);
      throw wrapUnknownError("createRevenueHub", error);
    }
  }
  async revenueHubExists(ownerDid) {
    try {
      this.logger.debug("Checking if revenue hub exists for DID:", ownerDid);
      const ownerParsed = parseDid3(ownerDid);
      if (ownerParsed.method !== "rooch") {
        throw badRequest(`Invalid owner DID method: expected 'rooch', got '${ownerParsed.method}'`);
      }
      const revenueHubId = calculateRevenueHubId(ownerParsed.identifier);
      const objectState = await this.getClient().getObjectStates({
        ids: [revenueHubId]
      });
      return objectState && objectState.length > 0 && objectState[0] !== null;
    } catch (error) {
      this.logger.error("Error checking revenue hub existence:", error);
      if (error && typeof error === "object" && "toString" in error && error.toString().includes("not found")) {
        return false;
      }
      throw wrapUnknownError("revenueHubExists", error);
    }
  }
  // -------- Revenue Operations --------
  async withdrawRevenue(params) {
    try {
      this.logger.debug("Withdrawing revenue with params:", params);
      const ownerParsed = parseDid3(params.ownerDid);
      if (ownerParsed.method !== "rooch") {
        throw badRequest(`Invalid owner DID method: expected 'rooch', got '${ownerParsed.method}'`);
      }
      const signer = await this.convertSigner(params.signer);
      const transaction = this.createTransaction();
      transaction.callFunction({
        target: `${this.getContractAddress()}::withdraw_revenue_entry`,
        typeArgs: [params.assetId],
        // CoinType as type argument
        args: [Args2.u256(params.amount)],
        maxGas: 1e8
      });
      this.logger.debug("Executing withdrawRevenue transaction");
      const result = await this.getClient().signAndExecuteTransaction({
        transaction,
        signer,
        option: { withOutput: true }
      });
      if (result.execution_info.status.type !== "executed") {
        throw mapTxFailureToPaymentKitError("withdrawRevenue", result.execution_info);
      }
      return {
        txHash: result.execution_info.tx_hash || "",
        blockHeight: BigInt(0),
        events: result.output?.events
      };
    } catch (error) {
      this.logger.error("Error withdrawing revenue:", error);
      throw wrapUnknownError("withdrawRevenue", error);
    }
  }
  async previewWithdrawalFee(ownerDid, assetId, amount) {
    try {
      this.logger.debug(
        "Previewing withdrawal fee for DID:",
        ownerDid,
        "asset:",
        assetId,
        "amount:",
        amount
      );
      const ownerParsed = parseDid3(ownerDid);
      if (ownerParsed.method !== "rooch") {
        throw badRequest(`Invalid owner DID method: expected 'rooch', got '${ownerParsed.method}'`);
      }
      return {
        grossAmount: amount,
        feeAmount: BigInt(0),
        netAmount: amount,
        feeRateBps: 0
      };
    } catch (error) {
      this.logger.error("Error previewing withdrawal fee:", error);
      throw wrapUnknownError("previewWithdrawalFee", error);
    }
  }
  // -------- Balance Queries --------
  async getRevenueBalance(ownerDid, assetId) {
    try {
      this.logger.debug("Getting revenue balance for DID:", ownerDid, "asset:", assetId);
      const ownerParsed = parseDid3(ownerDid);
      if (ownerParsed.method !== "rooch") {
        throw badRequest(`Invalid owner DID method: expected 'rooch', got '${ownerParsed.method}'`);
      }
      const revenueHub = await this.getRevenueHub(ownerParsed.identifier);
      if (!revenueHub) {
        this.logger.debug("Revenue hub not found for owner:", ownerDid);
        return BigInt(0);
      }
      this.logger.debug("MultiCoinStore ID extracted:", revenueHub.multi_coin_store);
      const fieldKey = deriveCoinTypeFieldKey(assetId);
      const fieldStates = await this.getClient().getFieldStates({
        objectId: revenueHub.multi_coin_store,
        fieldKey: [fieldKey]
      });
      if (!fieldStates || fieldStates.length === 0 || !fieldStates[0]) {
        this.logger.debug("Asset not found in multi_coin_store:", assetId);
        return BigInt(0);
      }
      const fieldState = fieldStates[0];
      const fieldValue = fieldState.value;
      if (typeof fieldValue === "string" && fieldValue.startsWith("0x")) {
        const coinStoreField = parseDynamicFieldCoinStore(fieldValue);
        return safeBalanceToBigint(coinStoreField.value.balance.value);
      } else {
        this.logger.warn("Unexpected field value format:", fieldValue);
        return BigInt(0);
      }
    } catch (error) {
      this.logger.error("Error getting revenue balance:", error);
      if (error && typeof error === "object" && "toString" in error && error.toString().includes("not found")) {
        return BigInt(0);
      }
      throw wrapUnknownError("getRevenueBalance", error);
    }
  }
  async getRevenueBySource(ownerDid, sourceType, assetId) {
    try {
      this.logger.debug(
        "Getting revenue by source for DID:",
        ownerDid,
        "source:",
        sourceType,
        "asset:",
        assetId
      );
      const ownerParsed = parseDid3(ownerDid);
      if (ownerParsed.method !== "rooch") {
        throw badRequest(`Invalid owner DID method: expected 'rooch', got '${ownerParsed.method}'`);
      }
      const revenueHub = await this.getRevenueHub(ownerParsed.identifier);
      if (!revenueHub) {
        this.logger.debug("Revenue hub not found for owner:", ownerDid);
        return BigInt(0);
      }
      const sourceFieldKey = deriveFieldKeyFromString(sourceType);
      const sourceFieldStates = await this.getClient().getFieldStates({
        objectId: revenueHub.revenue_by_source,
        fieldKey: [sourceFieldKey]
      });
      if (!sourceFieldStates || sourceFieldStates.length === 0 || !sourceFieldStates[0]) {
        this.logger.debug("Source type not found in revenue_by_source:", sourceType);
        return BigInt(0);
      }
      const sourceTableId = extractTableIdFromField(sourceFieldStates[0].value);
      if (!sourceTableId) {
        return BigInt(0);
      }
      const assetFieldKey = deriveFieldKeyFromString(assetId);
      const assetFieldStates = await this.getClient().getFieldStates({
        objectId: sourceTableId,
        fieldKey: [assetFieldKey]
      });
      if (!assetFieldStates || assetFieldStates.length === 0 || !assetFieldStates[0]) {
        this.logger.debug("Asset not found in source table:", assetId);
        return BigInt(0);
      }
      const fieldValue = assetFieldStates[0].value;
      if (typeof fieldValue === "string" && fieldValue.startsWith("0x")) {
        return parseU256FromBCS(fieldValue);
      } else {
        this.logger.warn("Unexpected field value format for revenue amount:", fieldValue);
        return BigInt(0);
      }
    } catch (error) {
      this.logger.error("Error getting revenue by source:", error);
      if (error && typeof error === "object" && "toString" in error && error.toString().includes("not found")) {
        return BigInt(0);
      }
      throw wrapUnknownError("getRevenueBySource", error);
    }
  }
  // -------- Helper Methods --------
  async getRevenueHub(ownerAddress) {
    try {
      const revenueHubId = calculateRevenueHubId(ownerAddress);
      const objectState = await this.getClient().getObjectStates({
        ids: [revenueHubId]
      });
      if (!objectState || objectState.length === 0 || !objectState[0]) {
        return null;
      }
      return parsePaymentRevenueHubData(objectState[0].value);
    } catch (error) {
      this.logger.debug("Error getting revenue hub:", error);
      return null;
    }
  }
};

// src/integrations/http/internal/DidAuthHelper.ts
import { DIDAuth } from "@nuwa-ai/identity-kit";
var DidAuthHelper = class {
  /**
   * Generate DIDAuthV1 authorization header for HTTP request
   * @param payerDid - The payer's DID
   * @param keyManager - Signer for creating the signature
   * @param requestUrl - The target URL for the request
   * @param method - HTTP method (optional, defaults to 'POST')
   * @param keyId - Optional specific key ID to use (if not provided, uses first available)
   * @returns Authorization header value or undefined if generation fails
   */
  static async generateAuthHeader(payerDid, keyManager, requestUrl, method = "POST", keyId) {
    try {
      let selectedKeyId = keyId;
      if (!selectedKeyId) {
        const keyIds = await keyManager.listKeyIds();
        if (keyIds.length === 0) {
          throw new Error("No key IDs available for signing");
        }
        selectedKeyId = keyIds[0];
      }
      const signedObject = await DIDAuth.v1.createSignature(
        {
          operation: "http_request",
          params: {
            uri: requestUrl,
            method: method.toUpperCase()
          }
        },
        keyManager,
        selectedKeyId
      );
      const authHeader = DIDAuth.v1.toAuthorizationHeader(signedObject);
      return authHeader;
    } catch (error) {
      return void 0;
    }
  }
  /**
   * Generate DIDAuthV1 authorization header with specific key ID
   * @param keyManager - Signer for creating the signature
   * @param keyId - Specific key ID to use
   * @param requestUrl - The target URL for the request
   * @param method - HTTP method (optional, defaults to 'POST')
   * @returns Authorization header value
   */
  static async generateAuthHeaderWithKeyId(keyManager, keyId, requestUrl, method = "POST") {
    const signedObject = await DIDAuth.v1.createSignature(
      {
        operation: "http_request",
        params: {
          uri: requestUrl,
          method: method.toUpperCase()
        }
      },
      keyManager,
      keyId
    );
    return DIDAuth.v1.toAuthorizationHeader(signedObject);
  }
};

// src/schema/core/index.ts
import { z } from "zod";
import { LosslessNumber } from "lossless-json";
var createBigIntSchema = () => z.union([
  z.bigint(),
  z.string().transform(BigInt),
  z.number().transform(BigInt),
  z.instanceof(LosslessNumber).transform((val) => BigInt(val.toString()))
]);
var SerializableSubRAVSchema = z.object({
  version: z.string(),
  chainId: z.string(),
  channelId: z.string(),
  channelEpoch: z.string(),
  vmIdFragment: z.string(),
  accumulatedAmount: z.string(),
  nonce: z.string()
});
var SerializableSignedSubRAVSchema = z.object({
  subRav: SerializableSubRAVSchema,
  signature: z.string()
});
var SerializableRequestPayloadSchema = z.object({
  version: z.number(),
  clientTxRef: z.string(),
  maxAmount: z.string().optional(),
  signedSubRav: SerializableSignedSubRAVSchema.optional()
});
var SerializableResponsePayloadSchema = z.object({
  version: z.number(),
  clientTxRef: z.string().optional(),
  serviceTxRef: z.string().optional(),
  subRav: SerializableSubRAVSchema.optional(),
  cost: z.string().optional(),
  costUsd: z.string().optional(),
  error: z.object({
    code: z.string(),
    message: z.string().optional()
  }).optional()
});
var ChannelStatusSchema = z.enum(["active", "closing", "closed"]);
var ChannelInfoSchema = z.object({
  /** Channel identifier */
  channelId: z.string(),
  /** Payer DID */
  payerDid: z.string(),
  /** Payee DID */
  payeeDid: z.string(),
  /** Asset identifier */
  assetId: z.string(),
  /** Channel epoch */
  epoch: createBigIntSchema(),
  /** Channel status */
  status: ChannelStatusSchema
});
var SubChannelStateSchema = z.object({
  /** Associated channel ID */
  channelId: z.string(),
  /** Channel epoch */
  epoch: createBigIntSchema(),
  /** DID verification method fragment */
  vmIdFragment: z.string(),
  /** Optional public key for the verification method */
  publicKey: z.string().optional(),
  /** Optional DID method type for the verification method */
  methodType: z.string().optional(),
  /** Last on-chain claimed amount for this sub-channel */
  lastClaimedAmount: createBigIntSchema(),
  /** Last on-chain confirmed nonce for this sub-channel */
  lastConfirmedNonce: createBigIntSchema(),
  /** Optional last updated timestamp (ms) for local caches */
  lastUpdated: z.number().optional()
});
var ServiceDiscoverySchema = z.object({
  /** Protocol version */
  version: z.number(),
  /** Service identifier */
  serviceId: z.string(),
  /** Service DID */
  serviceDid: z.string(),
  /** Network identifier */
  network: z.string(),
  /** Default asset identifier */
  defaultAssetId: z.string(),
  /** Default price in pico USD (optional) */
  defaultPricePicoUSD: z.string().optional(),
  /** API base path */
  basePath: z.string()
});
var HealthCheckSchema = z.object({
  /** Operation success status */
  success: z.boolean(),
  /** Health status description */
  status: z.string(),
  /** Response timestamp in ISO-8601 format */
  timestamp: z.string(),
  /** Whether payment kit is enabled */
  paymentKitEnabled: z.boolean()
});
var ClaimTriggerPolicySchema = z.object({
  /** Minimum accumulated amount to trigger claim (in smallest unit) */
  minClaimAmount: createBigIntSchema(),
  /** Maximum number of concurrent claim operations */
  maxConcurrentClaims: z.number().optional(),
  /** Retry attempts for failed claims */
  maxRetries: z.number().optional(),
  /** Delay between retries in milliseconds */
  retryDelayMs: z.number().optional(),
  /** Whether to check hub balance before triggering claims */
  requireHubBalance: z.boolean().optional(),
  /** Fixed backoff for insufficient funds (ms) */
  insufficientFundsBackoffMs: z.number().optional(),
  /** Count insufficient funds as failures */
  countInsufficientAsFailure: z.boolean().optional()
});
var ClaimTriggerStatusSchema = z.object({
  /** Number of active claims currently processing */
  active: z.number(),
  /** Number of tasks queued for processing */
  queued: z.number(),
  /** Total successful claims */
  successCount: z.number(),
  /** Total failed claims */
  failedCount: z.number(),
  /** Total skipped claims */
  skippedCount: z.number(),
  /** Total insufficient funds occurrences */
  insufficientFundsCount: z.number(),
  /** Number of tasks in backoff (waiting for retry) */
  backoffCount: z.number(),
  /** Average claim processing time in milliseconds */
  avgProcessingTimeMs: z.number(),
  /** Claim trigger policy (reactive) */
  policy: ClaimTriggerPolicySchema
});
var PaymentProcessingStatsSchema = z.object({
  /** Total number of payment requests processed */
  totalRequests: z.number(),
  /** Number of successful payments */
  successfulPayments: z.number(),
  /** Number of failed payments */
  failedPayments: z.number(),
  /** Number of auto claims triggered */
  autoClaimsTriggered: z.number()
});
var SystemStatusSchema = z.object({
  /** Reactive claim trigger status */
  claims: ClaimTriggerStatusSchema,
  /** Payment processing statistics */
  processor: PaymentProcessingStatsSchema,
  /** Response timestamp in ISO-8601 format */
  timestamp: z.string()
});
var ClaimTriggerRequestSchema = z.object({
  /** Channel identifier to trigger claim for */
  channelId: z.string()
});
var ClaimTriggerQueuedSchema = z.object({
  vmIdFragment: z.string(),
  delta: createBigIntSchema()
});
var ClaimTriggerSkippedSchema = z.object({
  vmIdFragment: z.string(),
  reason: z.enum(["no_delta", "below_threshold"]),
  delta: createBigIntSchema().optional(),
  threshold: createBigIntSchema().optional()
});
var ClaimTriggerResponseSchema = z.object({
  channelId: z.string(),
  queued: z.array(ClaimTriggerQueuedSchema),
  skipped: z.array(ClaimTriggerSkippedSchema)
});
var ErrorCodeSchema = z.enum([
  // Authentication & Authorization
  "UNAUTHORIZED",
  "FORBIDDEN",
  // Payment related
  "PAYMENT_REQUIRED",
  "INSUFFICIENT_FUNDS",
  "CONFLICT",
  // General errors
  "NOT_FOUND",
  "BAD_REQUEST",
  "INTERNAL_ERROR",
  "SERVICE_UNAVAILABLE"
]);
var ApiErrorSchema = z.object({
  code: ErrorCodeSchema,
  message: z.string(),
  details: z.any().optional(),
  httpStatus: z.number().optional()
});
var PersistedHttpClientStateSchema = z.object({
  /** Channel identifier */
  channelId: z.string().optional(),
  /** Pending SubRAV data */
  pendingSubRAV: SerializableSubRAVSchema.optional(),
  /** Last update timestamp (ISO string) */
  lastUpdated: z.string().optional()
});

// src/utils/json.ts
import { stringify, parse, LosslessNumber as LosslessNumber2 } from "lossless-json";
function serializeJson(value) {
  const result = stringify(value);
  if (result === void 0) {
    throw new Error("Failed to serialize value to JSON");
  }
  return result;
}
function parseJson(text) {
  return parse(text, (key, value) => {
    if (value instanceof LosslessNumber2) {
      const str = value.toString();
      if (str.indexOf(".") === -1 && str.indexOf("e") === -1 && str.indexOf("E") === -1) {
        const num = Number(str);
        if (Number.isSafeInteger(num)) {
          return num;
        }
        return value;
      }
    }
    return value;
  });
}
async function parseJsonResponse(response) {
  const text = await response.text();
  return parseJson(text);
}

// src/storage/memory/channel.memory.ts
var MemoryChannelRepository = class {
  constructor() {
    this.channelMetadata = /* @__PURE__ */ new Map();
    this.subChannelStates = /* @__PURE__ */ new Map();
    this.hitCount = 0;
    this.missCount = 0;
  }
  getSubChannelKey(channelId, vmIdFragment) {
    return `${channelId}:${vmIdFragment}`;
  }
  // -------- Channel Metadata Operations --------
  async getChannelMetadata(channelId) {
    const result = this.channelMetadata.get(channelId) || null;
    if (result) {
      this.hitCount++;
    } else {
      this.missCount++;
    }
    return result;
  }
  async setChannelMetadata(channelId, metadata) {
    this.channelMetadata.set(channelId, { ...metadata });
  }
  async listChannelMetadata(filter, pagination) {
    let channels = Array.from(this.channelMetadata.values());
    if (filter) {
      channels = channels.filter((channel) => {
        if (filter.payerDid && channel.payerDid !== filter.payerDid) return false;
        if (filter.payeeDid && channel.payeeDid !== filter.payeeDid) return false;
        if (filter.status && channel.status !== filter.status) return false;
        if (filter.assetId && channel.assetId !== filter.assetId) return false;
        return true;
      });
    }
    const totalCount = channels.length;
    const offset = pagination?.offset || 0;
    const limit = pagination?.limit || 50;
    const paginatedChannels = channels.slice(offset, offset + limit);
    return {
      items: paginatedChannels,
      totalCount,
      hasMore: offset + limit < totalCount
    };
  }
  async removeChannelMetadata(channelId) {
    this.channelMetadata.delete(channelId);
  }
  // -------- Sub-Channel State Operations --------
  async getSubChannelState(channelId, vmIdFragment) {
    const key = this.getSubChannelKey(channelId, vmIdFragment);
    const existing = this.subChannelStates.get(key);
    if (existing) {
      this.hitCount++;
      return { ...existing };
    }
    this.missCount++;
    return null;
  }
  async updateSubChannelState(channelId, vmIdFragment, updates) {
    const key = this.getSubChannelKey(channelId, vmIdFragment);
    const existing = this.subChannelStates.get(key) || {
      channelId,
      vmIdFragment,
      epoch: BigInt(0),
      lastConfirmedNonce: BigInt(0),
      lastClaimedAmount: BigInt(0),
      lastUpdated: Date.now()
    };
    const updated = {
      ...existing,
      ...updates,
      lastUpdated: Date.now()
    };
    this.subChannelStates.set(key, updated);
  }
  async listSubChannelStates(channelId) {
    const result = {};
    for (const [key, state] of this.subChannelStates.entries()) {
      if (key.startsWith(channelId + ":")) {
        const vmIdFragment = key.substring(channelId.length + 1);
        result[vmIdFragment] = { ...state };
      }
    }
    return result;
  }
  async removeSubChannelState(channelId, vmIdFragment) {
    const key = this.getSubChannelKey(channelId, vmIdFragment);
    this.subChannelStates.delete(key);
  }
  // -------- Management Operations --------
  async getStats() {
    const totalAccess = this.hitCount + this.missCount;
    return {
      channelCount: this.channelMetadata.size,
      subChannelCount: this.subChannelStates.size,
      hitRate: totalAccess > 0 ? this.hitCount / totalAccess : 0,
      sizeBytes: this.estimateSize()
    };
  }
  async clear() {
    this.channelMetadata.clear();
    this.subChannelStates.clear();
    this.hitCount = 0;
    this.missCount = 0;
  }
  estimateSize() {
    let size = 0;
    for (const channel of this.channelMetadata.values()) {
      size += channel.channelId.length * 2;
      size += channel.payerDid.length * 2;
      size += channel.payeeDid.length * 2;
      size += channel.assetId.length * 2;
      size += 100;
    }
    for (const state of this.subChannelStates.values()) {
      size += state.channelId.length * 2;
      size += 200;
    }
    return size;
  }
};

// src/storage/indexeddb/channel.indexeddb.ts
var IndexedDBChannelRepository = class {
  constructor() {
    this.dbName = "nuwa-payment-kit-channels";
    this.version = 1;
  }
  async getDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains("channels")) {
          const store = db.createObjectStore("channels", { keyPath: "channelId" });
          store.createIndex("payerDid", "payerDid", { unique: false });
          store.createIndex("payeeDid", "payeeDid", { unique: false });
          store.createIndex("status", "status", { unique: false });
        }
        if (!db.objectStoreNames.contains("subChannelStates")) {
          const store = db.createObjectStore("subChannelStates", {
            keyPath: ["channelId", "vmIdFragment"]
          });
          store.createIndex("channelId", "channelId", { unique: false });
        }
      };
    });
  }
  // -------- Channel Metadata Operations --------
  async getChannelMetadata(channelId) {
    const db = await this.getDB();
    const tx = db.transaction(["channels"], "readonly");
    const store = tx.objectStore("channels");
    return new Promise((resolve, reject) => {
      const request = store.get(channelId);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }
  async setChannelMetadata(channelId, metadata) {
    const db = await this.getDB();
    const tx = db.transaction(["channels"], "readwrite");
    const store = tx.objectStore("channels");
    await new Promise((resolve, reject) => {
      const request = store.put({ ...metadata, channelId });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
  async listChannelMetadata(filter, pagination) {
    const db = await this.getDB();
    const tx = db.transaction(["channels"], "readonly");
    const store = tx.objectStore("channels");
    const channels = [];
    await new Promise((resolve, reject) => {
      const request = store.openCursor();
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          const channel = cursor.value;
          let includeChannel = true;
          if (filter) {
            if (filter.payerDid && channel.payerDid !== filter.payerDid) includeChannel = false;
            if (filter.payeeDid && channel.payeeDid !== filter.payeeDid) includeChannel = false;
            if (filter.status && channel.status !== filter.status) includeChannel = false;
            if (filter.assetId && channel.assetId !== filter.assetId) includeChannel = false;
          }
          if (includeChannel) {
            channels.push(channel);
          }
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
    const totalCount = channels.length;
    const offset = pagination?.offset || 0;
    const limit = pagination?.limit || 50;
    const paginatedChannels = channels.slice(offset, offset + limit);
    return {
      items: paginatedChannels,
      totalCount,
      hasMore: offset + limit < totalCount
    };
  }
  async removeChannelMetadata(channelId) {
    const db = await this.getDB();
    const tx = db.transaction(["channels"], "readwrite");
    const store = tx.objectStore("channels");
    await new Promise((resolve, reject) => {
      const request = store.delete(channelId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
  // -------- Sub-Channel State Operations --------
  async getSubChannelState(channelId, vmIdFragment) {
    const db = await this.getDB();
    const tx = db.transaction(["subChannelStates"], "readonly");
    const store = tx.objectStore("subChannelStates");
    const existing = await new Promise((resolve, reject) => {
      const request = store.get([channelId, vmIdFragment]);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
    if (existing) {
      return {
        ...existing,
        lastConfirmedNonce: BigInt(
          existing.lastConfirmedNonce ?? existing.nonce ?? 0
        ),
        lastClaimedAmount: BigInt(
          existing.lastClaimedAmount ?? existing.accumulatedAmount ?? 0
        )
      };
    }
    return null;
  }
  async updateSubChannelState(channelId, vmIdFragment, updates) {
    const db = await this.getDB();
    const tx = db.transaction(["subChannelStates"], "readwrite");
    const store = tx.objectStore("subChannelStates");
    const existing = await new Promise((resolve, reject) => {
      const request = store.get([channelId, vmIdFragment]);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
    const baseState = existing || {
      channelId,
      vmIdFragment,
      epoch: BigInt(0),
      lastConfirmedNonce: BigInt(0),
      lastClaimedAmount: BigInt(0),
      lastUpdated: Date.now()
    };
    const updated = {
      ...baseState,
      ...updates,
      channelId,
      vmIdFragment,
      lastUpdated: Date.now(),
      // Convert bigints to strings for storage
      lastConfirmedNonce: updates.lastConfirmedNonce?.toString() || baseState.lastConfirmedNonce?.toString() || "0",
      lastClaimedAmount: updates.lastClaimedAmount?.toString() || baseState.lastClaimedAmount?.toString() || "0"
    };
    await new Promise((resolve, reject) => {
      const request = store.put(updated);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
  async listSubChannelStates(channelId) {
    const db = await this.getDB();
    const tx = db.transaction(["subChannelStates"], "readonly");
    const store = tx.objectStore("subChannelStates");
    const index = store.index("channelId");
    const result = {};
    await new Promise((resolve, reject) => {
      const request = index.openCursor(IDBKeyRange.only(channelId));
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          const state = cursor.value;
          result[state.vmIdFragment] = {
            ...state,
            lastConfirmedNonce: BigInt(state.lastConfirmedNonce ?? state.nonce ?? 0),
            lastClaimedAmount: BigInt(
              state.lastClaimedAmount ?? state.accumulatedAmount ?? 0
            )
          };
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
    return result;
  }
  async removeSubChannelState(channelId, vmIdFragment) {
    const db = await this.getDB();
    const tx = db.transaction(["subChannelStates"], "readwrite");
    const store = tx.objectStore("subChannelStates");
    await new Promise((resolve, reject) => {
      const request = store.delete([channelId, vmIdFragment]);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
  // -------- Management Operations --------
  async getStats() {
    const db = await this.getDB();
    const channelCount = await new Promise((resolve, reject) => {
      const tx = db.transaction(["channels"], "readonly");
      const store = tx.objectStore("channels");
      const request = store.count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    const subChannelCount = await new Promise((resolve, reject) => {
      const tx = db.transaction(["subChannelStates"], "readonly");
      const store = tx.objectStore("subChannelStates");
      const request = store.count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    return {
      channelCount,
      subChannelCount,
      hitRate: void 0,
      // Not tracked in IndexedDB implementation
      sizeBytes: void 0
      // Hard to estimate in IndexedDB
    };
  }
  async clear() {
    const db = await this.getDB();
    const tx = db.transaction(["channels", "subChannelStates"], "readwrite");
    await Promise.all([
      new Promise((resolve, reject) => {
        const request = tx.objectStore("channels").clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      }),
      new Promise((resolve, reject) => {
        const request = tx.objectStore("subChannelStates").clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      })
    ]);
  }
};

// src/storage/memory/transaction.memory.ts
var MemoryTransactionStore = class {
  constructor(maxRecords = 500) {
    this.maxRecords = maxRecords;
    this.records = /* @__PURE__ */ new Map();
    this.order = [];
    this.listeners = /* @__PURE__ */ new Set();
  }
  async create(record) {
    this.records.set(record.clientTxRef, record);
    this.order.push(record.clientTxRef);
    this.trim();
    this.emit({ type: "created", record });
  }
  async update(clientTxRef, updates) {
    const existing = this.records.get(clientTxRef);
    if (!existing) return;
    const updated = { ...existing, ...updates };
    this.records.set(clientTxRef, updated);
    this.emit({ type: "updated", record: updated });
  }
  async get(clientTxRef) {
    return this.records.get(clientTxRef) || null;
  }
  async list(filter, pagination) {
    const offset = pagination?.offset ?? 0;
    const limit = pagination?.limit ?? 50;
    const items = [];
    for (let i = this.order.length - 1; i >= 0; i--) {
      const id = this.order[i];
      const rec = this.records.get(id);
      if (!rec) continue;
      if (filter?.status && rec.status !== filter.status) continue;
      if (filter?.channelId && rec.channelId !== filter.channelId) continue;
      if (filter?.protocol && rec.protocol !== filter.protocol) continue;
      if (typeof filter?.since === "number" && rec.timestamp < filter.since) continue;
      if (typeof filter?.until === "number" && rec.timestamp > filter.until) continue;
      items.push(rec);
      if (items.length >= offset + limit) break;
    }
    const window2 = items.slice(offset, offset + limit);
    const total = items.length;
    const hasMore = total > offset + window2.length;
    return { items: window2, total, hasMore };
  }
  async clear() {
    this.records.clear();
    this.order = [];
  }
  subscribe(cb) {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }
  trim() {
    while (this.order.length > this.maxRecords) {
      const oldest = this.order.shift();
      if (oldest) this.records.delete(oldest);
    }
  }
  emit(e) {
    for (const l of this.listeners) {
      try {
        l(e);
      } catch {
      }
    }
  }
};

// src/storage/indexeddb/transactions.indexeddb.ts
var IndexedDBTransactionStore = class {
  constructor() {
    this.dbName = "nuwa-payment-kit-transactions";
    this.version = 1;
  }
  async getDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains("transactions")) {
          const store = db.createObjectStore("transactions", { keyPath: "clientTxRef" });
          store.createIndex("timestamp", "timestamp", { unique: false });
          store.createIndex("channelId", "channelId", { unique: false });
          store.createIndex("status", "status", { unique: false });
          store.createIndex("protocol", "protocol", { unique: false });
          store.createIndex("vmIdFragment", "vmIdFragment", { unique: false });
        }
      };
    });
  }
  async create(record) {
    const db = await this.getDB();
    const tx = db.transaction(["transactions"], "readwrite");
    const store = tx.objectStore("transactions");
    await new Promise((resolve, reject) => {
      const req = store.put(this.serialize(record));
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }
  async update(clientTxRef, updates) {
    const existing = await this.get(clientTxRef);
    if (!existing) return;
    const updated = { ...existing, ...updates };
    await this.create(updated);
  }
  async get(clientTxRef) {
    const db = await this.getDB();
    const tx = db.transaction(["transactions"], "readonly");
    const store = tx.objectStore("transactions");
    return new Promise((resolve, reject) => {
      const req = store.get(clientTxRef);
      req.onsuccess = () => resolve(req.result ? this.deserialize(req.result) : null);
      req.onerror = () => reject(req.error);
    });
  }
  async list(filter, pagination) {
    const db = await this.getDB();
    const tx = db.transaction(["transactions"], "readonly");
    const store = tx.objectStore("transactions");
    const index = store.index("timestamp");
    const items = [];
    await new Promise((resolve, reject) => {
      const range = void 0;
      const req = index.openCursor(range, "prev");
      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) return resolve();
        const rec = this.deserialize(cursor.value);
        if (filter?.status && rec.status !== filter.status) return cursor.continue();
        if (filter?.channelId && rec.channelId !== filter.channelId) return cursor.continue();
        if (filter?.protocol && rec.protocol !== filter.protocol) return cursor.continue();
        if (typeof filter?.since === "number" && rec.timestamp < filter.since)
          return cursor.continue();
        if (typeof filter?.until === "number" && rec.timestamp > filter.until)
          return cursor.continue();
        items.push(rec);
        cursor.continue();
      };
      req.onerror = () => reject(req.error);
    });
    const offset = pagination?.offset ?? 0;
    const limit = pagination?.limit ?? 50;
    const window2 = items.slice(offset, offset + limit);
    const total = items.length;
    const hasMore = total > offset + window2.length;
    return { items: window2, total, hasMore };
  }
  async clear() {
    const db = await this.getDB();
    const tx = db.transaction(["transactions"], "readwrite");
    const store = tx.objectStore("transactions");
    await new Promise((resolve, reject) => {
      const req = store.clear();
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }
  subscribe() {
    return () => {
    };
  }
  serialize(rec) {
    const copy = { ...rec };
    if (copy.payment) {
      copy.payment = {
        ...copy.payment,
        cost: copy.payment.cost.toString(),
        costUsd: copy.payment.costUsd.toString(),
        nonce: copy.payment.nonce.toString()
      };
    }
    return copy;
  }
  deserialize(obj) {
    const copy = { ...obj };
    if (copy.payment) {
      copy.payment = {
        ...copy.payment,
        cost: BigInt(copy.payment.cost),
        costUsd: BigInt(copy.payment.costUsd),
        nonce: BigInt(copy.payment.nonce)
      };
    }
    return copy;
  }
};

// src/storage/factories/createTransactionStore.ts
function createTransactionStore(options = {}) {
  const backend = options.backend ?? "memory";
  switch (backend) {
    case "indexeddb":
      return new IndexedDBTransactionStore();
    case "memory":
    default:
      return new MemoryTransactionStore(options.maxRecords);
  }
}

// src/integrations/http/internal/LocalStore.ts
var MemoryHostChannelMappingStore = class {
  constructor() {
    this.stateStore = /* @__PURE__ */ new Map();
  }
  // New methods for full state management
  async getState(host) {
    return this.stateStore.get(host);
  }
  async setState(host, state) {
    const validatedState = PersistedHttpClientStateSchema.parse(state);
    this.stateStore.set(host, validatedState);
  }
  async deleteState(host) {
    this.stateStore.delete(host);
  }
  /**
   * Clear all stored mappings and states
   */
  clear() {
    this.stateStore.clear();
  }
};
var _LocalStorageHostChannelMappingStore = class _LocalStorageHostChannelMappingStore {
  // New methods for full state management
  async getState(host) {
    if (typeof localStorage === "undefined") {
      throw new Error("localStorage is not available");
    }
    const key = _LocalStorageHostChannelMappingStore.STATE_PREFIX + host;
    const value = localStorage.getItem(key);
    if (!value) {
      return void 0;
    }
    try {
      const parsedData = parseJson(value);
      if (parsedData && typeof parsedData === "object" && parsedData.pendingSubRAV) {
        const p = parsedData.pendingSubRAV;
        const looksLegacy = typeof p.version === "number" || typeof p.chainId !== "string";
        if (looksLegacy) {
          parsedData.pendingSubRAV = {
            version: String(p.version),
            chainId: String(p.chainId),
            channelId: String(p.channelId),
            channelEpoch: String(p.channelEpoch),
            vmIdFragment: String(p.vmIdFragment),
            accumulatedAmount: String(p.accumulatedAmount),
            nonce: String(p.nonce)
          };
        }
      }
      return PersistedHttpClientStateSchema.parse(parsedData);
    } catch (error) {
      return void 0;
    }
  }
  async setState(host, state) {
    if (typeof localStorage === "undefined") {
      throw new Error("localStorage is not available");
    }
    const stateKey = _LocalStorageHostChannelMappingStore.STATE_PREFIX + host;
    const stateWithTimestamp = {
      ...state,
      lastUpdated: (/* @__PURE__ */ new Date()).toISOString()
    };
    const validatedState = PersistedHttpClientStateSchema.parse(stateWithTimestamp);
    const serializedState = serializeJson(validatedState);
    localStorage.setItem(stateKey, serializedState);
  }
  async deleteState(host) {
    if (typeof localStorage === "undefined") {
      throw new Error("localStorage is not available");
    }
    const stateKey = _LocalStorageHostChannelMappingStore.STATE_PREFIX + host;
    localStorage.removeItem(stateKey);
  }
  /**
   * Clear all stored mappings and states
   */
  async clear() {
    if (typeof localStorage === "undefined") {
      throw new Error("localStorage is not available");
    }
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(_LocalStorageHostChannelMappingStore.STATE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }
};
_LocalStorageHostChannelMappingStore.STATE_PREFIX = "nuwa-payment-client-state:";
var LocalStorageHostChannelMappingStore = _LocalStorageHostChannelMappingStore;
function createDefaultMappingStore() {
  if (typeof window !== "undefined" && typeof localStorage !== "undefined") {
    return new LocalStorageHostChannelMappingStore();
  }
  return new MemoryHostChannelMappingStore();
}
function createDefaultChannelRepo() {
  if (typeof window !== "undefined" && typeof indexedDB !== "undefined") {
    return new IndexedDBChannelRepository();
  }
  return new MemoryChannelRepository();
}
function createDefaultTransactionStore() {
  if (typeof window !== "undefined" && typeof indexedDB !== "undefined") {
    return new IndexedDBTransactionStore();
  }
  return new MemoryTransactionStore();
}
function extractHost(url) {
  try {
    const urlObj = new URL(url);
    return urlObj.host;
  } catch (error) {
    throw new Error(`Invalid URL: ${url}`);
  }
}
var NamespacedHostChannelMappingStore = class {
  constructor(base, providers) {
    this.base = base;
    this.providers = providers;
  }
  async buildKey(host) {
    const payerDid = await this.providers.getPayerDid();
    const ns = [host, payerDid].join("::");
    return ns;
  }
  async getState(host) {
    const key = await this.buildKey(host);
    return this.base.getState(key);
  }
  async setState(host, state) {
    const key = await this.buildKey(host);
    return this.base.setState(key, state);
  }
  async deleteState(host) {
    const key = await this.buildKey(host);
    return this.base.deleteState(key);
  }
};
function createNamespacedMappingStore(base, providers) {
  return new NamespacedHostChannelMappingStore(base, providers);
}

// src/schema/api/recovery.ts
import { z as z2 } from "zod";
var RecoveryResponseSchema = z2.object({
  /** Channel information if channel exists */
  channel: ChannelInfoSchema.nullable(),
  /** Pending SubRAV if any exists for recovery */
  pendingSubRav: SerializableSubRAVSchema.nullable(),
  /** Optional sub-channel state for current key fragment (if authorized) */
  subChannel: SubChannelStateSchema.nullable().optional(),
  /** Response timestamp in ISO-8601 format */
  timestamp: z2.string()
});
var RecoveryRequestSchema = z2.object({}).passthrough();

// src/schema/api/commit.ts
import { z as z3 } from "zod";
var CommitRequestSchema = z3.object({
  /** Signed SubRAV to be committed */
  subRav: SerializableSignedSubRAVSchema
});
var CommitResponseSchema = z3.object({
  success: z3.boolean()
});

// src/schema/api/discovery.ts
import { z as z4 } from "zod";
var DiscoveryResponseSchema = ServiceDiscoverySchema;
var DiscoveryRequestSchema = z4.object({}).passthrough();

// src/schema/api/health.ts
import { z as z5 } from "zod";
var HealthResponseSchema = HealthCheckSchema;
var HealthRequestSchema = z5.object({}).passthrough();

// src/schema/api/subrav.ts
import { z as z6 } from "zod";
var SubRavRequestSchema = z6.object({
  /** Nonce as string (from query params), will be converted to BigInt in handler */
  nonce: z6.string()
});
var SubRavResponseSchema = SerializableSignedSubRAVSchema;

// src/schema/api/admin.ts
import { z as z7 } from "zod";
var SystemStatusResponseSchema = SystemStatusSchema;
var AdminRequestSchema = z7.object({}).passthrough();

// src/integrations/http/PaymentChannelHttpClient.ts
import { DebugLogger as DebugLogger10 } from "@nuwa-ai/identity-kit";

// src/integrations/http/internal/StreamPaymentFilter.ts
var DEFAULT_QUEUE_HWM = 16;
function wrapAndFilterInBandFrames(response, onPayment, log, onActivity, onFinish, options) {
  const originalBody = response.body;
  if (!originalBody || typeof originalBody.getReader !== "function") {
    return response;
  }
  const reader = originalBody.getReader();
  const textDecoder = new TextDecoder();
  const textEncoder = new TextEncoder();
  const ct = (response.headers.get("content-type") || "").toLowerCase();
  const isSSE = ct.includes("text/event-stream");
  const isNDJSON = ct.includes("application/x-ndjson");
  let sawPayment = false;
  const afterPayment = (controller) => {
    try {
      sawPayment = true;
      log("[afterPayment] Payment processed, continuing stream");
    } catch (e) {
      log("[afterPayment.error]", e);
    }
  };
  const isBg = options?.backgroundDrain !== false;
  const shouldEmit = (controller) => {
    if (isBg) {
      const ds = controller.desiredSize;
      if (typeof ds === "number" && ds <= 0) return false;
    }
    return true;
  };
  const parser = isSSE ? new SseInbandParser(textEncoder, onPayment, log, afterPayment, onActivity, shouldEmit) : new NdjsonInbandParser(textEncoder, onPayment, log, afterPayment, onActivity, shouldEmit);
  const makeBackgroundLoop = (controller) => {
    (async () => {
      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          if (!value) continue;
          try {
            onActivity?.();
          } catch (e) {
            log("[bg.onActivity.error]", e);
          }
          const chunkText = textDecoder.decode(value, { stream: true });
          await parser.process(chunkText, controller);
        }
        await parser.flush(controller);
        controller.close();
        try {
          onFinish?.({ sawPayment });
        } catch (e) {
          log("[finish.error]", e);
        }
      } catch (e) {
        try {
          reader.cancel();
        } catch {
        }
        controller.error(e);
      }
    })();
  };
  const filtered = isBg ? new ReadableStream(
    {
      start(controller) {
        makeBackgroundLoop(controller);
      },
      cancel() {
        try {
          reader.cancel();
        } catch (e) {
          log("[cancel.error]", e);
        }
      }
    },
    { highWaterMark: options?.queueHWM ?? DEFAULT_QUEUE_HWM }
  ) : new ReadableStream({
    async pull(controller) {
      try {
        const { value, done } = await reader.read();
        if (done) {
          await parser.flush(controller);
          controller.close();
          try {
            onFinish?.({ sawPayment });
          } catch (e) {
            log("[finish.error]", e);
          }
          return;
        }
        if (!value) return;
        try {
          onActivity?.();
        } catch (e) {
          log("[pull.onActivity.error]", e);
        }
        const chunkText = textDecoder.decode(value, { stream: true });
        await parser.process(chunkText, controller);
      } catch (e) {
        try {
          reader.cancel();
        } catch (e2) {
          log("[pull.error]", e2);
        }
        controller.error(e);
      }
    },
    cancel() {
      try {
        reader.cancel();
      } catch (e) {
        log("[cancel.error]", e);
      }
    }
  });
  const headers = new Headers(response.headers);
  headers.delete("content-length");
  try {
    if (isSSE) headers.set("content-type", "text/event-stream; charset=utf-8");
    else if (isNDJSON) headers.set("content-type", "application/x-ndjson");
  } catch {
  }
  const filteredResponse = new Response(filtered, {
    status: response.status,
    statusText: response.statusText,
    headers
  });
  return filteredResponse;
}
var SseInbandParser = class {
  constructor(encoder, onPayment, log, onAfterPayment, onTick, shouldEmit) {
    this.encoder = encoder;
    this.onPayment = onPayment;
    this.log = log;
    this.onAfterPayment = onAfterPayment;
    this.onTick = onTick;
    this.shouldEmit = shouldEmit;
    this.buffer = "";
    this.pendingEvent = [];
  }
  extractPaymentHeaderFromEventLines(lines) {
    for (const l of lines) {
      const m = l.match(/^data:\s*(.+)$/);
      if (!m) continue;
      try {
        const o = JSON.parse(m[1]);
        const header = o?.nuwa_payment_header || o?.__nuwa_payment_header__;
        if (typeof header === "string") {
          this.log("[extractPaymentHeaderFromEventLines]", header);
          return header;
        }
      } catch {
      }
    }
    return null;
  }
  async handleEvent(controller) {
    const headerValue = this.extractPaymentHeaderFromEventLines(this.pendingEvent);
    if (!headerValue) {
      for (const out of this.pendingEvent) {
        const bytes = this.encoder.encode(out + "\n");
        controller.enqueue(bytes);
      }
      const last = this.pendingEvent[this.pendingEvent.length - 1];
      if (last !== "") {
        const sep = this.encoder.encode("\n");
        controller.enqueue(sep);
        this.log("[sse.event.emit.sep]", true);
      }
      this.pendingEvent = [];
      return;
    }
    try {
      this.onTick?.();
    } catch (e) {
      this.log("[sse.event.onTick.error]", e);
    }
    await safeHandlePayment({ headerValue }, this.onPayment, this.log);
    this.onAfterPayment(controller);
    this.pendingEvent = [];
  }
  async process(textChunk, controller) {
    this.buffer += textChunk;
    const lines = this.buffer.split(/\r?\n/);
    this.buffer = lines.pop() ?? "";
    for (const line of lines) {
      try {
        this.onTick?.();
      } catch (e) {
        this.log("[sse.event.onTick.error]", e);
      }
      this.pendingEvent.push(line);
      if (line === "") {
        try {
          await this.handleEvent(controller);
        } catch (e) {
          this.log("[process.error]", e);
        }
      }
    }
  }
  async flush(controller) {
    if (!this.buffer) return;
    this.pendingEvent.push(this.buffer);
    try {
      await this.handleEvent(controller);
    } catch (e) {
      this.log("[flush.error]", e);
    }
    this.buffer = "";
  }
};
var NdjsonInbandParser = class {
  constructor(encoder, onPayment, log, onAfterPayment, onTick, shouldEmit) {
    this.encoder = encoder;
    this.onPayment = onPayment;
    this.log = log;
    this.onAfterPayment = onAfterPayment;
    this.onTick = onTick;
    this.shouldEmit = shouldEmit;
    this.buffer = "";
  }
  extractPaymentHeaderFromLine(t) {
    try {
      const obj = JSON.parse(t);
      const headerValue = obj?.__nuwa_payment_header__ || obj?.nuwa_payment_header;
      this.log("[extractPaymentHeaderFromLine]", headerValue);
      return typeof headerValue === "string" ? headerValue : null;
    } catch {
      return null;
    }
  }
  async handleLine(line, controller) {
    const t = line.trim();
    if (!t) return;
    try {
      this.log("[ndjson.emit.line]", t.slice(0, 128));
    } catch {
    }
    const headerValue = this.extractPaymentHeaderFromLine(t);
    if (!headerValue) {
      const bytes = this.encoder.encode(line + "\n");
      controller.enqueue(bytes);
      return;
    }
    this.onTick?.();
    await safeHandlePayment({ headerValue }, this.onPayment, this.log);
    this.onAfterPayment(controller);
  }
  async process(textChunk, controller) {
    this.buffer += textChunk;
    const lines = this.buffer.split(/\r?\n/);
    this.buffer = lines.pop() ?? "";
    for (const line of lines) {
      try {
        this.onTick?.();
      } catch {
      }
      try {
        await this.handleLine(line, controller);
      } catch (e) {
        this.log("[process.error]", e);
      }
    }
  }
  async flush(controller) {
    if (!this.buffer) return;
    try {
      await this.handleLine(this.buffer, controller);
    } catch (e) {
      this.log("[flush.error]", e);
    }
    this.buffer = "";
  }
};
async function safeHandlePayment(p, onPayment, log) {
  try {
    if (p && typeof p.headerValue === "string") {
      await onPayment(p);
    }
  } catch (e) {
    log("[inband.handle.error]", e?.message || String(e));
  }
}

// src/integrations/http/internal/utils.ts
function isStreamLikeResponse(response) {
  const ct = (response.headers.get("content-type") || "").toLowerCase();
  return ct.includes("text/event-stream") || ct.includes("application/x-ndjson");
}

// src/integrations/http/internal/RequestScheduler.ts
import { DebugLogger as DebugLogger5 } from "@nuwa-ai/identity-kit";
var RequestScheduler = class {
  constructor() {
    this.queue = [];
    this.active = 0;
    this.concurrency = 1;
    this.logger = DebugLogger5.get("RequestScheduler");
    this.isShutdown = false;
  }
  enqueue(start) {
    let cancelFn = () => {
    };
    const promise = new Promise((outerResolve, outerReject) => {
      if (this.isShutdown) {
        outerReject(new Error("RequestScheduler is shutdown"));
        return;
      }
      const schedulerController = new AbortController();
      let settled = false;
      const safeResolve = (value) => {
        if (settled) return;
        settled = true;
        outerResolve(value);
      };
      const safeReject = (reason) => {
        if (settled) return;
        settled = true;
        outerReject(reason);
      };
      const task = async () => {
        this.active += 1;
        let released = false;
        const release = () => {
          if (released) return;
          released = true;
          this.active -= 1;
          this.flush();
        };
        const abortPromise = new Promise((_resolve, reject) => {
          const onAbort = () => {
            try {
              reject(schedulerController.signal.reason || new Error("Task aborted"));
            } catch {
              reject(new Error("Task aborted"));
            }
          };
          if (schedulerController.signal.aborted) {
            onAbort();
          } else {
            schedulerController.signal.addEventListener("abort", onAbort, { once: true });
          }
        });
        try {
          const result = await Promise.race([
            start(release, schedulerController.signal),
            abortPromise
          ]);
          safeResolve(result);
        } catch (e) {
          this.logger.debug("Task failed:", e);
          safeReject(e);
        } finally {
          try {
            release();
          } catch (releaseError) {
            this.logger.debug("Error during release:", releaseError);
          }
        }
      };
      cancelFn = (reason) => {
        const idx = this.queue.indexOf(task);
        if (idx >= 0) {
          this.queue.splice(idx, 1);
          setTimeout(() => safeReject(reason || new Error("Task cancelled")), 0);
          try {
            schedulerController.abort(reason);
          } catch {
          }
          return;
        }
        try {
          schedulerController.abort(reason);
        } catch {
        }
      };
      this.queue.push(task);
      this.flush();
    });
    return { promise, cancel: cancelFn };
  }
  flush() {
    while (this.active < this.concurrency && this.queue.length > 0) {
      const next = this.queue.shift();
      void next();
    }
  }
  /**
   * Clear the queue and prevent any new tasks from being enqueued
   */
  clear() {
    this.isShutdown = true;
    const queueSize = this.queue.length;
    this.queue = [];
    this.logger.debug("RequestScheduler cleared, queue size was:", queueSize);
  }
};

// src/integrations/http/core/PaymentState.ts
import { DebugLogger as DebugLogger6 } from "@nuwa-ai/identity-kit";
var PaymentState = class {
  constructor() {
    this.recentlyRejectedRefs = /* @__PURE__ */ new Set();
    this.logger = DebugLogger6.get("PaymentState");
    this.state = {
      pendingPayments: /* @__PURE__ */ new Map()
    };
  }
  // Channel management
  setChannelId(channelId) {
    this.channelId = channelId;
    this.logger.debug("Channel ID set:", channelId);
  }
  getChannelId() {
    return this.channelId;
  }
  setChannelInfo(info) {
    this.channelInfo = info;
  }
  getChannelInfo() {
    return this.channelInfo;
  }
  setSubChannelInfo(info) {
    this.subChannelInfo = info;
  }
  getSubChannelInfo() {
    return this.subChannelInfo;
  }
  // Key management
  setKeyInfo(keyId, vmIdFragment) {
    this.keyId = keyId;
    this.vmIdFragment = vmIdFragment;
    this.logger.debug("Key info set:", { keyId, vmIdFragment });
  }
  getKeyId() {
    return this.keyId;
  }
  getVmIdFragment() {
    return this.vmIdFragment;
  }
  // Pending SubRAV management
  setPendingSubRAV(subRav) {
    if (subRav && this.shouldAcceptSubRAV(subRav)) {
      this.pendingSubRAV = subRav;
      this.logger.debug("Pending SubRAV set:", subRav.nonce);
    }
  }
  getPendingSubRAV() {
    return this.pendingSubRAV;
  }
  clearPendingSubRAV() {
    this.pendingSubRAV = void 0;
    this.logger.debug("Pending SubRAV cleared");
  }
  // Monotonic nonce guard
  updateHighestNonce(nonce) {
    if (!this.highestObservedNonce || nonce > this.highestObservedNonce) {
      this.highestObservedNonce = nonce;
    }
  }
  shouldAcceptSubRAV(subRav) {
    if (this.vmIdFragment && subRav.vmIdFragment !== this.vmIdFragment) {
      this.logger.debug("Rejecting SubRAV: fragment mismatch");
      return false;
    }
    const currentNonce = this.pendingSubRAV?.nonce;
    if (this.highestObservedNonce && subRav.nonce <= this.highestObservedNonce || currentNonce && subRav.nonce <= currentNonce) {
      this.logger.debug("Rejecting SubRAV: nonce not progressive", {
        highestObservedNonce: this.highestObservedNonce,
        currentNonce,
        subRavNonce: subRav.nonce
      });
      return false;
    }
    return true;
  }
  // Pending payments management
  addPendingPayment(clientTxRef, request) {
    this.state.pendingPayments?.set(clientTxRef, request);
    this.logger.debug("Pending payment added:", clientTxRef);
  }
  getPendingPayment(clientTxRef) {
    return this.state.pendingPayments?.get(clientTxRef);
  }
  removePendingPayment(clientTxRef) {
    this.state.pendingPayments?.delete(clientTxRef);
    this.logger.debug("Pending payment removed:", clientTxRef);
  }
  getAllPendingPayments() {
    return this.state.pendingPayments || /* @__PURE__ */ new Map();
  }
  clearAllPendingPayments() {
    this.state.pendingPayments?.clear();
    this.logger.debug("All pending payments cleared");
  }
  // Rejected refs tracking
  markAsRejected(clientTxRef) {
    this.recentlyRejectedRefs.add(clientTxRef);
  }
  isRecentlyRejected(clientTxRef) {
    return this.recentlyRejectedRefs.has(clientTxRef);
  }
  // State persistence
  getPersistedState() {
    return {
      channelId: this.channelId,
      pendingSubRAV: this.pendingSubRAV ? HttpPaymentCodec.serializeSubRAV(this.pendingSubRAV) : void 0,
      lastUpdated: (/* @__PURE__ */ new Date()).toISOString()
    };
  }
  loadPersistedState(state) {
    if (state.channelId) {
      this.channelId = state.channelId;
    }
    const pendingSubRAV = state.pendingSubRAV ? HttpPaymentCodec.deserializeSubRAV(state.pendingSubRAV) : void 0;
    if (pendingSubRAV && this.shouldAcceptSubRAV(pendingSubRAV)) {
      this.pendingSubRAV = pendingSubRAV;
    }
    this.logger.debug("Persisted state loaded");
  }
  // Reset state
  reset() {
    this.channelId = void 0;
    this.channelInfo = void 0;
    this.subChannelInfo = void 0;
    this.keyId = void 0;
    this.vmIdFragment = void 0;
    this.pendingSubRAV = void 0;
    this.highestObservedNonce = void 0;
    this.recentlyRejectedRefs.clear();
    this.state.pendingPayments?.clear();
    this.logger.debug("State reset");
  }
};

// src/integrations/http/core/PaymentProtocol.ts
import { DebugLogger as DebugLogger7 } from "@nuwa-ai/identity-kit";
var PaymentProtocol = class {
  constructor() {
    this.logger = DebugLogger7.get("PaymentProtocol");
    this.codec = new HttpPaymentCodec();
  }
  /**
   * Encode payment header for requests
   */
  encodeRequestHeader(signedSubRAV, clientTxRef, maxAmount = BigInt(0)) {
    return this.codec.encodePayload({
      signedSubRav: signedSubRAV,
      maxAmount,
      clientTxRef,
      version: 1
    });
  }
  /**
   * Parse payment header from response
   */
  parseResponseHeader(headerValue) {
    try {
      return HttpPaymentCodec.parseResponseHeader(headerValue);
    } catch (error) {
      this.logger.debug("Failed to parse payment header:", error);
      throw error;
    }
  }
  /**
   * Extract protocol information from response headers
   */
  parseProtocolFromResponse(response, context) {
    const headerName = HttpPaymentCodec.getHeaderName();
    let paymentHeader = response.headers.get(headerName);
    if (!paymentHeader) {
      paymentHeader = response.headers.get(headerName.toLowerCase());
    }
    if (!paymentHeader) {
      return { type: "none" };
    }
    try {
      const payload = this.parseResponseHeader(paymentHeader);
      if (payload?.error) {
        const code = payload.error.code;
        const message = payload.error.message || response.statusText || "Payment error";
        return {
          type: "error",
          clientTxRef: payload.clientTxRef || context?.clientTxRef,
          err: new PaymentKitError(code, message, response.status)
        };
      }
      if (payload?.subRav && payload.cost !== void 0) {
        return {
          type: "success",
          clientTxRef: payload.clientTxRef || context?.clientTxRef,
          subRav: payload.subRav,
          cost: payload.cost,
          costUsd: payload.costUsd,
          serviceTxRef: payload.serviceTxRef
        };
      }
      return { type: "none" };
    } catch (e) {
      this.logger.debug("Failed to parse payment response header:", e);
      return { type: "none" };
    }
  }
  /**
   * Handle protocol errors based on HTTP status codes
   */
  handleStatusCode(status) {
    switch (status) {
      case 402:
        return new PaymentKitError(
          "PAYMENT_REQUIRED" /* PAYMENT_REQUIRED */,
          "Payment required - insufficient balance or invalid proposal",
          402
        );
      case 409:
        return new PaymentKitError(
          "RAV_CONFLICT" /* RAV_CONFLICT */,
          "SubRAV conflict - cleared pending proposal",
          409
        );
      default:
        return null;
    }
  }
  /**
   * Get header name for payment protocol
   */
  getHeaderName() {
    return HttpPaymentCodec.getHeaderName();
  }
};

// src/integrations/http/core/RequestManager.ts
import { DebugLogger as DebugLogger8 } from "@nuwa-ai/identity-kit";
var RequestManager = class {
  constructor(paymentState, defaultTimeoutMs = 3e4) {
    this.traceOrigins = /* @__PURE__ */ new Map();
    this.logger = DebugLogger8.get("RequestManager");
    this.paymentState = paymentState;
    this.defaultTimeoutMs = defaultTimeoutMs;
  }
  /**
   * Create a payment promise for tracking request payment info
   */
  createPaymentPromise(clientTxRef, requestContext, sentSubRav, channelId, assetId, timeoutMs) {
    return new Promise((resolve, reject) => {
      const timeout = timeoutMs || this.defaultTimeoutMs;
      const timeoutId = setTimeout(() => {
        const pending = this.paymentState.getPendingPayment(clientTxRef);
        if (pending) {
          try {
            pending.release?.();
          } catch (e) {
            this.logger.debug("[release.error]", e);
          }
          this.paymentState.removePendingPayment(clientTxRef);
          this.logger.debug("[payment.timeout]", "clientTxRef=", clientTxRef);
          reject(new Error("Payment resolution timeout"));
        }
      }, timeout);
      const pendingRequest = {
        resolve,
        reject,
        timestamp: /* @__PURE__ */ new Date(),
        channelId,
        assetId,
        timeoutId,
        sentSubRav,
        requestContext
      };
      this.paymentState.addPendingPayment(clientTxRef, pendingRequest);
      this.logger.debug(
        "[payment.pending.create]",
        "clientTxRef=",
        clientTxRef,
        "channelId=",
        channelId,
        "signedSubRav=",
        !!sentSubRav
      );
    });
  }
  /**
   * Extend timeout for a specific pending payment (used for streaming)
   */
  extendTimeout(clientTxRef, newTimeoutMs) {
    const pending = this.paymentState.getPendingPayment(clientTxRef);
    if (!pending) return;
    clearTimeout(pending.timeoutId);
    pending.timeoutId = setTimeout(() => {
      if (this.paymentState.getPendingPayment(clientTxRef)) {
        try {
          pending.release?.();
        } catch (e) {
          this.logger.debug("[release.error]", e);
        }
        this.paymentState.removePendingPayment(clientTxRef);
        this.logger.debug("[payment.timeout.stream]", "clientTxRef=", clientTxRef);
        pending.reject(new Error("Payment resolution timeout"));
      }
    }, newTimeoutMs);
  }
  /**
   * Resolve a pending payment by reference
   */
  resolveByRef(clientTxRef, info) {
    const pending = this.paymentState.getPendingPayment(clientTxRef);
    if (!pending) return false;
    clearTimeout(pending.timeoutId);
    try {
      pending.release?.();
    } catch (e) {
      this.logger.debug("[release.error]", e);
    }
    pending.resolve(info);
    this.paymentState.removePendingPayment(clientTxRef);
    this.clearTraceOrigin(clientTxRef);
    this.logger.debug("[payment.pending.resolve]", "clientTxRef=", clientTxRef);
    return true;
  }
  /**
   * Reject a pending payment by reference
   */
  rejectByRef(clientTxRef, err) {
    const pending = this.paymentState.getPendingPayment(clientTxRef);
    if (!pending) return false;
    clearTimeout(pending.timeoutId);
    try {
      pending.release?.();
    } catch (e) {
      this.logger.debug("[release.error]", e);
    }
    pending.reject(err);
    this.paymentState.removePendingPayment(clientTxRef);
    this.paymentState.markAsRejected(clientTxRef);
    this.clearTraceOrigin(clientTxRef);
    this.logger.debug(
      "[payment.pending.reject]",
      "clientTxRef=",
      clientTxRef,
      "error=",
      err.message
    );
    return true;
  }
  /**
   * Reject all pending payments
   */
  rejectAll(err) {
    const pendingPayments = this.paymentState.getAllPendingPayments();
    for (const [key, pending] of pendingPayments.entries()) {
      clearTimeout(pending.timeoutId);
      try {
        pending.release?.();
      } catch (e) {
        this.logger.debug("[release.error]", e);
      }
      try {
        pending.reject(err);
      } catch (e) {
        this.logger.debug("[pending.reject.error]", e);
      }
    }
    this.paymentState.clearAllPendingPayments();
  }
  /**
   * Resolve all pending payments as free
   */
  resolveAllAsFree() {
    const pendingPayments = this.paymentState.getAllPendingPayments();
    const keys = [];
    for (const [key, pending] of pendingPayments.entries()) {
      clearTimeout(pending.timeoutId);
      try {
        pending.release?.();
      } catch (e) {
        this.logger.debug("[release.error]", e);
      }
      pending.resolve(void 0);
      keys.push(key);
    }
    this.paymentState.clearAllPendingPayments();
    if (keys.length > 0) {
      this.logger.debug("[payment.pending.free]", "resolved", keys.length, "requests as free");
    }
  }
  /**
   * Get all pending payment keys
   */
  getPendingKeys() {
    return Array.from(this.paymentState.getAllPendingPayments().keys());
  }
  /**
   * Record trace origin for debugging
   */
  recordTraceOrigin(clientTxRef, origin) {
    this.traceOrigins.set(clientTxRef, origin);
  }
  /**
   * Get trace origin for a client transaction reference
   */
  getTraceOrigin(clientTxRef) {
    return this.traceOrigins.get(clientTxRef);
  }
  /**
   * Clear trace origin
   */
  clearTraceOrigin(clientTxRef) {
    this.traceOrigins.delete(clientTxRef);
  }
  /**
   * Get trace origin snapshots for debugging
   */
  getTraceSnapshots(limit = 5) {
    const snapshots = [];
    for (const [key, stack] of this.traceOrigins.entries()) {
      snapshots.push({ key, head: stack.split("\n")[0] });
      if (snapshots.length >= limit) break;
    }
    return snapshots;
  }
};

// src/integrations/http/core/ChannelManager.ts
import { DebugLogger as DebugLogger9 } from "@nuwa-ai/identity-kit";
var ChannelManager = class {
  constructor(options) {
    this.options = options;
    this.logger = DebugLogger9.get("ChannelManager");
  }
  /**
   * Ensure channel is ready for use
   */
  async ensureChannelReady() {
    if (!this.ensureReadyPromise) {
      this.ensureReadyPromise = this.doEnsureChannelReady().finally(() => {
        this.ensureReadyPromise = void 0;
      });
    }
    await this.ensureReadyPromise;
  }
  async doEnsureChannelReady() {
    await this.ensureKeyFragmentPresent();
    await this.loadPersistedState();
    let channelId = this.options.paymentState.getChannelId();
    if (channelId) {
      try {
        const info = await this.options.payerClient.getChannelInfo(channelId);
        this.options.paymentState.setChannelInfo(info);
      } catch (e) {
        this.logger.debug("Persisted channel not found, clearing and recovering:", e);
        this.options.paymentState.setChannelId(void 0);
        this.options.paymentState.setChannelInfo(void 0);
        channelId = void 0;
      }
    }
    if (!channelId) {
      await this.createNewChannel();
      channelId = this.options.paymentState.getChannelId();
    }
    const vmIdFragment = this.options.paymentState.getVmIdFragment();
    if (channelId && vmIdFragment) {
      const existingSub = this.options.paymentState.getSubChannelInfo();
      if (!existingSub) {
        try {
          const subInfo = await this.options.payerClient.getSubChannelInfo(channelId, vmIdFragment);
          this.options.paymentState.setSubChannelInfo(subInfo);
        } catch {
          try {
            await this.options.payerClient.authorizeSubChannel({ channelId, vmIdFragment });
            await this.waitForSubChannelAuthorization(channelId, vmIdFragment);
            const subInfo = await this.options.payerClient.getSubChannelInfo(
              channelId,
              vmIdFragment
            );
            this.options.paymentState.setSubChannelInfo(subInfo);
            await this.persistState();
          } catch (e2) {
            this.logger.debug("Direct sub-channel authorization failed:", e2);
            throw e2;
          }
        }
      }
    }
  }
  /**
   * Create a new payment channel
   */
  async createNewChannel() {
    const defaultAssetId = this.options.defaultAssetId || "0x3::gas_coin::RGas";
    let payeeDid = this.options.payeeDid;
    if (!payeeDid) {
      const serviceInfo = await this.discoverService();
      payeeDid = serviceInfo.serviceDid;
      this.logger.debug("Discovered payeeDid from service:", payeeDid);
    }
    const openChannelResult = await this.options.payerClient.openChannelWithSubChannel({
      payeeDid,
      assetId: defaultAssetId
    });
    this.options.paymentState.setChannelId(openChannelResult.channelId);
    if (openChannelResult.channelInfo) {
      this.options.paymentState.setChannelInfo(openChannelResult.channelInfo);
    }
    if (openChannelResult.subChannelInfo) {
      this.options.paymentState.setSubChannelInfo(openChannelResult.subChannelInfo);
    }
    await this.persistState();
    this.logger.debug("Created new channel:", openChannelResult.channelId);
  }
  /**
   * Ensure keyId and vmIdFragment are present in PaymentState
   */
  async ensureKeyFragmentPresent() {
    if (this.options.paymentState.getKeyId() && this.options.paymentState.getVmIdFragment()) return;
    let keyId = this.options.keyId;
    if (!keyId) {
      if (this.options.signer && typeof this.options.signer.listKeyIds === "function") {
        try {
          const ids = await this.options.signer.listKeyIds();
          keyId = Array.isArray(ids) && ids.length > 0 ? ids[0] : void 0;
        } catch (e) {
          this.logger.debug("listKeyIds failed:", e);
        }
      }
    }
    if (keyId) {
      const parts = keyId.split("#");
      const vmIdFragment = parts.length > 1 ? parts[1] : void 0;
      if (vmIdFragment) {
        this.options.paymentState.setKeyInfo(keyId, vmIdFragment);
      }
    }
    if (!this.options.paymentState.getKeyId() || !this.options.paymentState.getVmIdFragment()) {
      throw new Error("No keyId found");
    }
  }
  /**
   * Discover service information
   */
  async discoverService() {
    if (!this.cachedDiscoveryInfo) {
      await this.performDiscovery();
    }
    if (this.cachedDiscoveryInfo) {
      return this.cachedDiscoveryInfo;
    }
    throw new Error("Service discovery failed: No discovery information available");
  }
  async performDiscovery() {
    const discoveryUrl = new URL("/.well-known/nuwa-payment/info", this.options.baseUrl);
    try {
      const response = await this.options.fetchImpl(discoveryUrl.toString(), {
        method: "GET",
        headers: {
          Accept: "application/json"
        }
      });
      if (response.ok) {
        const serviceInfo = await response.json();
        this.logger.debug("Service discovery successful:", serviceInfo);
        this.cachedDiscoveryInfo = serviceInfo;
        if (serviceInfo.basePath) {
          this.discoveredBasePath = serviceInfo.basePath;
        }
      }
    } catch (error) {
      this.logger.debug("Service discovery failed:", error);
    }
    if (!this.discoveredBasePath) {
      this.discoveredBasePath = "/payment-channel";
    }
  }
  /**
   * Recover channel state from service
   */
  async recoverFromService() {
    const recoveryUrl = this.buildPaymentUrl("/recovery");
    try {
      const payerDid = this.options.payerDid || await this.options.signer.getDid();
      const authHeader = await DidAuthHelper.generateAuthHeader(
        payerDid,
        this.options.signer,
        recoveryUrl,
        "GET",
        this.options.keyId
      );
      const headers = {
        Accept: "application/json"
      };
      if (authHeader) {
        headers["Authorization"] = authHeader;
      }
      const response = await this.options.fetchImpl(recoveryUrl, {
        method: "GET",
        headers
      });
      if (!response.ok) {
        throw new Error(`Failed to recover from service: HTTP ${response.status}`);
      }
      const jsonData = await response.json();
      this.logger.debug("Raw recovery response:", serializeJson(jsonData));
      let dataToValidate = jsonData;
      if (jsonData && typeof jsonData === "object" && "success" in jsonData) {
        if (jsonData.success && "data" in jsonData) {
          dataToValidate = jsonData.data;
        } else if (!jsonData.success) {
          const error = jsonData.error || {};
          throw new Error(error.message || "Recovery request failed");
        }
      }
      try {
        const recoveryData = RecoveryResponseSchema.parse(dataToValidate);
        this.logger.debug("Recovery completed successfully");
        return recoveryData;
      } catch (parseError) {
        this.logger.debug("Recovery response parsing failed:", parseError);
        this.logger.debug("Returning raw recovery data");
        return dataToValidate;
      }
    } catch (error) {
      const errorMessage = `Recovery failed: ${error instanceof Error ? error.message : String(error)}`;
      this.logger.debug(errorMessage);
      throw new Error(errorMessage);
    }
  }
  /**
   * Wait for sub-channel authorization to be visible on-chain
   */
  async waitForSubChannelAuthorization(channelId, vmIdFragment, attempts = 10, delayMs = 500) {
    for (let i = 0; i < attempts; i++) {
      try {
        await this.options.payerClient.getSubChannelInfo(channelId, vmIdFragment);
        this.logger.debug("Sub-channel visible on-chain:", vmIdFragment);
        return;
      } catch {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }
    throw new Error("Sub-channel authorization not visible on-chain within timeout");
  }
  /**
   * Commit a signed SubRAV to the service
   */
  async commitSubRAV(signedSubRAV) {
    const commitUrl = this.buildPaymentUrl("/commit");
    try {
      const payerDid = this.options.payerDid || await this.options.signer.getDid();
      const authHeader = await DidAuthHelper.generateAuthHeader(
        payerDid,
        this.options.signer,
        commitUrl,
        "POST",
        this.options.keyId
      );
      const headers = {
        Accept: "application/json",
        "Content-Type": "application/json"
      };
      if (authHeader) {
        headers["Authorization"] = authHeader;
      }
      const response = await this.options.fetchImpl(commitUrl, {
        method: "POST",
        headers,
        body: serializeJson({ subRav: signedSubRAV })
      });
      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`Failed to commit SubRAV: HTTP ${response.status} - ${errorBody}`);
      }
      this.logger.debug("SubRAV committed successfully");
    } catch (error) {
      const errorMessage = `SubRAV commit failed: ${error instanceof Error ? error.message : String(error)}`;
      this.logger.debug(errorMessage);
      throw new Error(errorMessage);
    }
  }
  /**
   * Load persisted state from storage
   */
  async loadPersistedState() {
    try {
      const persistedState = await this.options.mappingStore.getState(this.options.host);
      if (persistedState) {
        this.options.paymentState.loadPersistedState(persistedState);
        this.logger.debug("Loaded persisted state");
      }
    } catch (error) {
      this.logger.debug("Failed to load persisted state:", error);
    }
  }
  /**
   * Persist current state to storage
   */
  async persistState() {
    try {
      const state = this.options.paymentState.getPersistedState();
      await this.options.mappingStore.setState(this.options.host, state);
      this.logger.debug("Persisted state");
    } catch (error) {
      this.logger.debug("Failed to persist state:", error);
    }
  }
  /**
   * Build URL for payment-related endpoints
   */
  buildPaymentUrl(endpoint) {
    const basePath = this.discoveredBasePath || "/payment-channel";
    return new URL(`${basePath}${endpoint}`, this.options.baseUrl).toString();
  }
  /**
   * Get the discovered base path
   */
  getBasePath() {
    return this.discoveredBasePath || "/payment-channel";
  }
};

// src/integrations/http/PaymentChannelHttpClient.ts
var PaymentChannelHttpClient = class {
  constructor(options) {
    this.scheduler = new RequestScheduler();
    this.isCleanedUp = false;
    /**
     * Associates protocol errors with HTTP responses to avoid double-throwing errors.
     * This WeakMap tracks errors encountered during the response handling flow,
     * ensuring that only one error is thrown per response and preventing duplicate
     * error propagation. This is especially important in asynchronous flows where
     * multiple handlers may process the same response.
     */
    this.protocolErrorByResponse = /* @__PURE__ */ new WeakMap();
    this.options = options;
    this.fetchImpl = options.fetchImpl || globalThis.fetch?.bind(globalThis);
    this.host = extractHost(options.baseUrl);
    this.channelRepo = options.channelRepo || createDefaultChannelRepo();
    this.transactionStore = options.transactionStore || createDefaultTransactionStore();
    if (!this.fetchImpl) {
      throw new Error("fetch implementation not available. Please provide fetchImpl option.");
    }
    this.payerClient = PaymentChannelFactory.createClient({
      chainConfig: options.chainConfig,
      signer: options.signer,
      keyId: options.keyId,
      storageOptions: {
        channelRepo: this.channelRepo
      }
    });
    this.rateProvider = new ContractRateProvider(this.payerClient.getContract());
    this.logger = DebugLogger10.get("PaymentChannelHttpClient");
    this.logger.setLevel(this.options.debug ? "debug" : "info");
    this.requestTimeoutMs = this.options.timeoutMs ?? 3e4;
    if (this.options.timeoutMsStream === void 0) {
      this.options.timeoutMsStream = this.requestTimeoutMs;
    }
    this.paymentState = new PaymentState();
    this.paymentProtocol = new PaymentProtocol();
    this.requestManager = new RequestManager(this.paymentState, this.requestTimeoutMs);
    const baseMapping = options.mappingStore || createDefaultMappingStore();
    const mappingStore = createNamespacedMappingStore(baseMapping, {
      getPayerDid: async () => this.options.payerDid || await this.options.signer.getDid()
    });
    this.channelManager = new ChannelManager({
      host: this.host,
      baseUrl: options.baseUrl,
      payerClient: this.payerClient,
      paymentState: this.paymentState,
      mappingStore,
      fetchImpl: this.fetchImpl,
      signer: options.signer,
      keyId: options.keyId,
      payerDid: options.payerDid,
      payeeDid: options.payeeDid,
      defaultAssetId: options.defaultAssetId
    });
  }
  /**
   * Primary API – returns a handle with separate response and payment promises.
   */
  async requestWithPayment(method, path, init) {
    return this.createRequestHandle(method, path, init);
  }
  /**
   * Convenience: return only the HTTP response (non-blocking for payment).
   */
  async request(method, path, init) {
    const handle = await this.createRequestHandle(method, path, init);
    return handle.response;
  }
  /**
   * Convenience: wait for response and payment (recommended for non-streaming endpoints).
   */
  async requestAndWaitForPayment(method, path, init) {
    const handle = await this.createRequestHandle(method, path, init);
    const data = await handle.response;
    let payment = void 0;
    try {
      payment = await handle.payment;
    } catch (e) {
      this.log("[payment.error]", e);
    }
    return { data, payment };
  }
  /**
   * Convenience methods for common HTTP verbs
   */
  async get(path, init) {
    const result = await this.requestAndWaitForPayment("GET", path, init);
    const data = await this.parseJsonAuto(result.data);
    return { data, payment: result.payment };
  }
  async post(path, body, init) {
    const requestInit = {
      ...init,
      body: body ? JSON.stringify(body) : void 0,
      headers: {
        "Content-Type": "application/json",
        ...init?.headers
      }
    };
    const result = await this.requestAndWaitForPayment("POST", path, requestInit);
    const data = await this.parseJsonAuto(result.data);
    return { data, payment: result.payment };
  }
  async put(path, body, init) {
    const requestInit = {
      ...init,
      body: body ? JSON.stringify(body) : void 0,
      headers: {
        "Content-Type": "application/json",
        ...init?.headers
      }
    };
    const result = await this.requestAndWaitForPayment("PUT", path, requestInit);
    const data = await this.parseJsonAuto(result.data);
    return { data, payment: result.payment };
  }
  async patch(path, body, init) {
    const requestInit = {
      ...init,
      body: body ? JSON.stringify(body) : void 0,
      headers: {
        "Content-Type": "application/json",
        ...init?.headers
      }
    };
    const result = await this.requestAndWaitForPayment("PATCH", path, requestInit);
    const data = await this.parseJsonAuto(result.data);
    return { data, payment: result.payment };
  }
  async delete(path, init) {
    const result = await this.requestAndWaitForPayment("DELETE", path, init);
    const data = await this.parseJsonAuto(result.data);
    return { data, payment: result.payment };
  }
  /**
   * Get the currently cached pending SubRAV
   */
  getPendingSubRAV() {
    return this.paymentState.getPendingSubRAV() || null;
  }
  /**
   * Clear the pending SubRAV cache
   */
  clearPendingSubRAV() {
    this.paymentState.clearPendingSubRAV();
    void this.persistClientState();
  }
  /**
   * Get the current channel ID
   */
  getChannelId() {
    return this.paymentState.getChannelId();
  }
  getPayerClient() {
    return this.payerClient;
  }
  getHubClient() {
    return this.payerClient.getHubClient();
  }
  getTransactionStore() {
    return this.transactionStore;
  }
  /**
   * Discover service information
   */
  async discoverService() {
    return this.channelManager.discoverService();
  }
  async healthCheck() {
    const healthUrl = this.channelManager.buildPaymentUrl("/health");
    const response = await this.fetchImpl(healthUrl, { method: "GET" });
    return this.parseJsonAuto(response, HealthResponseSchema);
  }
  /**
   * Recover channel state and pending SubRAV from service
   */
  async recoverFromService() {
    return this.channelManager.recoverFromService();
  }
  /**
   * Commit a signed SubRAV to the service
   */
  async commitSubRAV(signedSubRAV) {
    await this.channelManager.commitSubRAV(signedSubRAV);
    return { success: true };
  }
  buildPaymentUrl(endpoint) {
    return this.channelManager.buildPaymentUrl(endpoint);
  }
  getPersistedState() {
    return this.paymentState.getPersistedState();
  }
  // NOTE: Additional methods will be added in subsequent parts
  // This is a partial implementation to demonstrate the integration
  log(...args) {
    this.logger.debug(...args);
  }
  async parseJsonAuto(response, schema) {
    const protoErr = this.protocolErrorByResponse.get(response);
    if (protoErr) {
      throw protoErr;
    }
    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
      throw new Error("Response is not JSON");
    }
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    let responseData;
    try {
      responseData = await parseJsonResponse(response);
    } catch (error) {
      throw new Error("Failed to parse JSON response");
    }
    if (schema) {
      if (responseData && typeof responseData === "object" && "success" in responseData) {
        const apiResponse = responseData;
        if (apiResponse.success) {
          return schema.parse(apiResponse.data);
        } else {
          const error = apiResponse.error;
          throw new PaymentKitError(
            error?.code || ErrorCode.INTERNAL_ERROR,
            error?.message || "Unknown error",
            error?.httpStatus || response.status,
            error?.details
          );
        }
      }
      return schema.parse(responseData);
    }
    if (responseData && typeof responseData === "object" && "success" in responseData) {
      const apiResponse = responseData;
      if (apiResponse.success) {
        return apiResponse.data;
      } else {
        const error = apiResponse.error;
        throw new PaymentKitError(
          error?.code || ErrorCode.INTERNAL_ERROR,
          error?.message || "Unknown error",
          error?.httpStatus || response.status,
          error?.details
        );
      }
    }
    return responseData;
  }
  /**
   * Create a handle containing both response and payment promises
   */
  async createRequestHandle(method, path, init) {
    const fullUrl = path.startsWith("http") ? path : new URL(path, this.options.baseUrl).toString();
    const clientTxRef = this.extractOrGenerateClientTxRef(init?.headers);
    try {
      const err = new Error(`[origin] ${method} ${fullUrl}`);
      const stack = err.stack ? err.stack.split("\n").slice(1, 10).join("\n") : "no stack";
      this.requestManager.recordTraceOrigin(clientTxRef, stack);
    } catch {
    }
    let requestContext;
    let paymentResolve;
    let paymentReject;
    const paymentPromise = new Promise((res, rej) => {
      paymentResolve = res;
      paymentReject = rej;
    });
    let paymentBridgeAttached = false;
    let scheduledHandle;
    let abort = () => {
      setTimeout(() => {
        try {
          scheduledHandle?.cancel(void 0);
          if (!paymentBridgeAttached) {
            try {
              paymentResolve(void 0);
            } catch {
            }
          } else {
            try {
              void this.requestManager.resolveByRef(clientTxRef, void 0);
            } catch {
            }
          }
        } catch {
        }
      }, 0);
    };
    if (init?.signal instanceof AbortSignal) {
      const onInitAbort = () => {
        abort?.();
      };
      if (init.signal.aborted) {
        onInitAbort();
      } else {
        init.signal.addEventListener("abort", onInitAbort, { once: true });
      }
    }
    const responsePromise = (scheduledHandle = this.scheduler.enqueue(
      async (release, signal) => {
        if (this.isCleanedUp || signal.aborted) {
          throw new Error("Client has been cleaned up");
        }
        if (signal.aborted) throw new Error("Request aborted");
        await this.channelManager.ensureChannelReady();
        if (signal.aborted) throw new Error("Request aborted");
        await this.channelManager.discoverService();
        if (signal.aborted) throw new Error("Request aborted");
        try {
          await this.tryRecoverPendingIfNeeded();
        } catch (e) {
          this.log("[serialized.recover.error]", e);
        }
        if (signal.aborted) throw new Error("Request aborted");
        const { headers, sentedSubRav } = await this.prepareHeaders(
          fullUrl,
          method,
          clientTxRef,
          init?.headers
        );
        if (signal.aborted) throw new Error("Request aborted");
        requestContext = {
          method,
          url: fullUrl,
          headers,
          body: init?.body,
          clientTxRef
        };
        const channelId = this.paymentState.getChannelId();
        const assetId = this.options.defaultAssetId || "0x3::gas_coin::RGas";
        if (!channelId) {
          throw new Error("Channel not initialized");
        }
        const pp = this.requestManager.createPaymentPromise(
          clientTxRef,
          requestContext,
          sentedSubRav,
          channelId,
          assetId
        );
        const pending = this.paymentState.getPendingPayment(clientTxRef);
        if (pending) {
          pending.release = () => {
            try {
              release();
            } catch (e) {
              this.log?.("[release.error]", e);
            }
            pending.release = void 0;
          };
        }
        paymentBridgeAttached = true;
        void pp.then(paymentResolve).catch(paymentReject);
        try {
          await this.logTransaction(clientTxRef, requestContext, sentedSubRav);
        } catch (e) {
          this.log("[txlog.create.error]", e);
        }
        if (signal.aborted) throw new Error("Request aborted");
        const fetchInit = { ...init, signal };
        return this.executeRequest(requestContext, fetchInit);
      }
    )).promise;
    responsePromise.catch(() => {
    });
    this.log(
      "[request.start]",
      method,
      fullUrl,
      "clientTxRef=",
      clientTxRef,
      "channelId=",
      this.paymentState.getChannelId()
    );
    responsePromise.catch((err) => {
      this.log("[response.error]", err);
      try {
        const settled = this.requestManager.resolveByRef(clientTxRef, void 0);
        if (!settled) {
          try {
            paymentResolve(void 0);
          } catch {
          }
        }
      } catch (settleErr) {
        this.log("[response.error.settle]", settleErr);
      }
    });
    const startTs = Date.now();
    const done = responsePromise.then(async (data) => {
      let payment = void 0;
      try {
        payment = await paymentPromise;
      } catch (e) {
        this.log?.("[payment.error]", e);
      }
      return { data, payment };
    });
    void paymentPromise.catch(() => {
    });
    void done.catch(() => {
    });
    void responsePromise.then((res) => {
      const durationMs = Date.now() - startTs;
      this.transactionStore?.update(clientTxRef, {
        statusCode: res.status,
        durationMs
      });
      return res;
    }).catch((err) => this.log("[txlog.update.error]", err));
    return {
      clientTxRef,
      response: responsePromise,
      payment: paymentPromise,
      done,
      abort
    };
  }
  /**
   * Cleanup client state on logout
   */
  async logoutCleanup(options = { clearMapping: true }) {
    const reason = options?.reason || "Logout cleanup";
    this.isCleanedUp = true;
    try {
      const pending = this.paymentState.getPendingSubRAV();
      if (pending) {
        const vmIdFragment = this.paymentState.getVmIdFragment();
        if (vmIdFragment && vmIdFragment === pending.vmIdFragment) {
          this.log("logoutCleanup: committed pending SubRAV on logout:", pending.nonce);
        }
      }
    } catch (e) {
      this.log("logoutCleanup: failed to commit pending SubRAV:", e);
    }
    this.scheduler.clear();
    this.requestManager.rejectAll(new Error(reason));
    this.paymentState.reset();
    if (options?.clearMapping !== false) {
      try {
        const mappingStore = this.options.mappingStore || createDefaultMappingStore();
        const namespacedStore = createNamespacedMappingStore(mappingStore, {
          getPayerDid: async () => this.options.payerDid || await this.options.signer.getDid()
        });
        await namespacedStore.deleteState(this.host);
      } catch (e) {
        this.log("logoutCleanup: deleteState(mapping) failed:", e);
      }
    }
    await this.persistClientState();
  }
  /**
   * Compute unsettled amount for the current sub-channel
   */
  async getUnsettledAmountForSubChannel() {
    await this.channelManager.ensureChannelReady();
    const channelInfo = this.paymentState.getChannelInfo();
    const vmIdFragment = this.paymentState.getVmIdFragment();
    if (!channelInfo || !vmIdFragment) {
      throw new Error("Channel or vmIdFragment not initialized");
    }
    const assetId = channelInfo.assetId;
    const channelId = channelInfo.channelId;
    let subChannelInfo = this.paymentState.getSubChannelInfo();
    if (!subChannelInfo) {
      subChannelInfo = await this.payerClient.getSubChannelInfo(channelId, vmIdFragment);
      this.paymentState.setSubChannelInfo(subChannelInfo);
    }
    const lastClaimed = subChannelInfo.lastClaimedAmount;
    let authorizedAccumulated = void 0;
    let latestSubRavNonce = void 0;
    const pending = this.paymentState.getPendingSubRAV();
    if (pending && pending.channelId === channelId && pending.vmIdFragment === vmIdFragment) {
      authorizedAccumulated = pending.accumulatedAmount;
      latestSubRavNonce = pending.nonce;
    }
    if (authorizedAccumulated === void 0) {
      authorizedAccumulated = lastClaimed;
    }
    const diff = authorizedAccumulated - lastClaimed;
    const unsettled = diff > 0n ? diff : 0n;
    const unsettledUsd = await this.rateProvider.getPricePicoUSD(assetId) * unsettled;
    return {
      channelId,
      vmIdFragment,
      authorizedAccumulated,
      lastClaimed,
      unsettled,
      unsettledUsd,
      subChannelInfo,
      latestSubRavNonce
    };
  }
  // -------- Private Methods --------
  /**
   * Extract clientTxRef from headers or generate new one
   */
  extractOrGenerateClientTxRef(providedHeaders) {
    if (providedHeaders) {
      let clientTxRef;
      if (providedHeaders instanceof Headers) {
        clientTxRef = providedHeaders.get("X-Client-Tx-Ref") || void 0;
      } else if (Array.isArray(providedHeaders)) {
        const found = providedHeaders.find(([key]) => key.toLowerCase() === "x-client-tx-ref");
        clientTxRef = found?.[1];
      } else if (typeof providedHeaders === "object") {
        clientTxRef = providedHeaders["X-Client-Tx-Ref"] || providedHeaders["x-client-tx-ref"];
      }
      if (clientTxRef) {
        return clientTxRef;
      }
    }
    return crypto.randomUUID();
  }
  /**
   * Try to recover pending SubRAV if needed
   */
  async tryRecoverPendingIfNeeded() {
    return;
  }
  /**
   * Prepare headers for the request
   */
  async prepareHeaders(fullUrl, method, clientTxRef, providedHeaders) {
    const headers = {};
    if (providedHeaders) {
      if (providedHeaders instanceof Headers) {
        providedHeaders.forEach((value, key) => {
          headers[key] = value;
        });
      } else if (Array.isArray(providedHeaders)) {
        providedHeaders.forEach(([key, value]) => {
          headers[key] = value;
        });
      } else {
        Object.assign(headers, providedHeaders);
      }
    }
    try {
      const payerDid = this.options.payerDid || await this.options.signer.getDid();
      const authHeader = await DidAuthHelper.generateAuthHeader(
        payerDid,
        this.options.signer,
        fullUrl,
        method,
        this.options.keyId
      );
      if (authHeader) {
        headers["Authorization"] = authHeader;
      }
    } catch (error) {
      this.log("Failed to generate DID auth header:", error);
    }
    const sentedSubRav = await this.addPaymentChannelHeader(headers, clientTxRef);
    this.log(
      "[request.headers]",
      method,
      fullUrl,
      "clientTxRef=",
      clientTxRef,
      "channelId=",
      this.paymentState.getChannelId(),
      "signedSubRav=",
      !!sentedSubRav
    );
    return { headers, sentedSubRav };
  }
  /**
   * Add payment channel data to headers
   */
  async addPaymentChannelHeader(headers, clientTxRef) {
    if (!this.paymentState.getChannelId()) {
      throw new Error("Channel not initialized");
    }
    try {
      const signedSubRAV = await this.buildSignedSubRavIfNeeded();
      const headerValue = this.paymentProtocol.encodeRequestHeader(
        signedSubRAV,
        clientTxRef,
        this.options.maxAmount
      );
      headers[this.paymentProtocol.getHeaderName()] = headerValue;
      if (signedSubRAV) {
        this.log("Added payment header with SignedSubRAV");
      } else {
        this.log("Added payment header in FREE mode (clientTxRef only)");
      }
      return signedSubRAV;
    } catch (error) {
      this.log("Failed to add payment channel header:", error);
      throw error;
    }
  }
  /**
   * Build and sign SubRAV if pending exists
   */
  async buildSignedSubRavIfNeeded() {
    const pending = this.paymentState.getPendingSubRAV();
    if (!pending) {
      this.log("No pending SubRAV - operating in FREE mode");
      return void 0;
    }
    this.paymentState.clearPendingSubRAV();
    const signed = await this.payerClient.signSubRAV(pending);
    this.log("Signed pending SubRAV:", pending.nonce, pending.accumulatedAmount);
    this.paymentState.updateHighestNonce(pending.nonce);
    return signed;
  }
  /**
   * Execute the HTTP request
   */
  async executeRequest(context, init, allowRetry = true) {
    try {
      const { headers: _, ...initWithoutHeaders } = init || {};
      const response = await this.fetchImpl(context.url, {
        method: context.method,
        headers: context.headers,
        body: context.body,
        ...initWithoutHeaders
      });
      return await this.processResponse(response, context, initWithoutHeaders, allowRetry);
    } catch (error) {
      this.log("Request failed:", error);
      throw error;
    }
  }
  async processResponse(response, context, initWithoutHeaders, allowRetry) {
    const headerName = this.paymentProtocol.getHeaderName();
    let paymentHeader = response.headers.get(headerName) || response.headers.get(headerName.toLowerCase());
    if (allowRetry && paymentHeader && typeof paymentHeader === "string") {
      try {
        const payload = this.paymentProtocol.parseResponseHeader(paymentHeader);
        if (payload?.error?.code === "PAYMENT_REQUIRED" && payload.subRav && context.clientTxRef) {
          const signed = await this.payerClient.signSubRAV(payload.subRav);
          const newHeader = this.paymentProtocol.encodeRequestHeader(
            signed,
            context.clientTxRef,
            this.options.maxAmount
          );
          context.headers[headerName] = newHeader;
          try {
            const payerDid = this.options.payerDid || await this.options.signer.getDid();
            const authHeader = await DidAuthHelper.generateAuthHeader(
              payerDid,
              this.options.signer,
              context.url,
              context.method,
              this.options.keyId
            );
            if (authHeader) {
              context.headers["Authorization"] = authHeader;
            }
          } catch {
          }
          const retried = await this.fetchImpl(context.url, {
            method: context.method,
            headers: context.headers,
            body: context.body,
            ...initWithoutHeaders
          });
          return await this.processResponse(retried, context, initWithoutHeaders, false);
        }
      } catch (error) {
        this.log("[payment.required.retry.failed]", {
          url: context.url,
          method: context.method,
          clientTxRef: context.clientTxRef,
          error
        });
      }
    }
    await this.handleResponse(response, context);
    return await this.wrapStreamingIfNeeded(response, context);
  }
  async wrapStreamingIfNeeded(response, context) {
    if (isStreamLikeResponse(response) && response.body && typeof response.body.getReader === "function") {
      const onActivity = () => {
        if (context.clientTxRef && typeof this.options.timeoutMsStream === "number") {
          this.requestManager.extendTimeout(context.clientTxRef, this.options.timeoutMsStream);
        }
      };
      const filtered = wrapAndFilterInBandFrames(
        response,
        async (p) => {
          try {
            const decoded = this.paymentProtocol.parseResponseHeader(p.headerValue);
            this.log("[inband.decode payment response]", decoded);
            if (decoded?.subRav && decoded.cost !== void 0) {
              await this.handleProtocolSuccess({
                type: "success",
                clientTxRef: decoded.clientTxRef,
                subRav: decoded.subRav,
                cost: decoded.cost,
                costUsd: decoded.costUsd,
                serviceTxRef: decoded.serviceTxRef
              });
            } else if (decoded?.error) {
              await this.handleProtocolError({
                type: "error",
                clientTxRef: decoded.clientTxRef,
                err: new PaymentKitError(
                  decoded.error.code,
                  decoded.error.message || "Payment error (stream)"
                )
              });
            }
          } catch (e) {
            this.log("[inband.decode.error]", e);
          }
        },
        (...args) => this.log(...args),
        onActivity,
        ({ sawPayment }) => {
          try {
            if (!sawPayment && context.clientTxRef) {
              this.requestManager.resolveByRef(context.clientTxRef, void 0);
              if (this.options.transactionLog?.enabled !== false && this.transactionStore) {
                void this.transactionStore.update(context.clientTxRef, { status: "free" });
              }
            }
          } catch (e) {
            this.log("[onFinish.error]", e);
          }
        }
      );
      if (this.transactionStore && context.clientTxRef) {
        await this.transactionStore.update(context.clientTxRef, { stream: true });
      }
      if (context.clientTxRef && typeof this.options.timeoutMsStream === "number") {
        this.requestManager.extendTimeout(context.clientTxRef, this.options.timeoutMsStream);
      }
      return filtered;
    }
    return response;
  }
  /**
   * Handle the HTTP response
   */
  async handleResponse(response, context) {
    const protocol = this.paymentProtocol.parseProtocolFromResponse(response, context);
    if (protocol.type !== "none") {
      this.log(
        "[response.header]",
        "type=",
        protocol.type,
        "clientTxRef=",
        protocol.clientTxRef,
        "status=",
        response.status
      );
    } else {
      this.log(
        "[response.no-header]",
        "status=",
        response.status,
        "pendingKeys=",
        this.requestManager.getPendingKeys()
      );
    }
    if (protocol.type === "error") {
      this.logTraceOrigin(protocol.clientTxRef);
      await this.handleProtocolError(protocol);
      this.protocolErrorByResponse.set(response, protocol.err);
      return;
    }
    if (protocol.type === "success") {
      await this.handleProtocolSuccess(protocol);
      return;
    }
    await this.handleNoProtocolHeader(response, context);
  }
  async handleProtocolError(proto) {
    if (proto.clientTxRef && this.requestManager.resolveByRef(proto.clientTxRef, void 0)) {
      this.paymentState.clearPendingSubRAV();
      await this.persistClientState();
      return;
    }
    const pendingPayments = this.paymentState.getAllPendingPayments();
    if (pendingPayments.size === 1) {
      const [[onlyKey]] = pendingPayments.entries();
      this.requestManager.resolveByRef(onlyKey, void 0);
      this.paymentState.clearPendingSubRAV();
      await this.persistClientState();
      return;
    }
    if (pendingPayments.size > 1) {
      for (const [key] of pendingPayments.entries()) {
        this.requestManager.resolveByRef(key, void 0);
      }
      this.paymentState.clearPendingSubRAV();
      await this.persistClientState();
    }
  }
  async handleProtocolSuccess(proto) {
    let pendingKey;
    let pendingRequest = this.findMatchingPendingPayment(proto);
    if (!pendingRequest) {
      if (proto.clientTxRef && this.paymentState.isRecentlyRejected(proto.clientTxRef)) {
        this.log("[payment.late-success.ignored]", proto.clientTxRef);
        return;
      }
      this.paymentState.setPendingSubRAV(proto.subRav);
      await this.persistClientState();
      return;
    }
    pendingKey = pendingRequest.key;
    const prev = pendingRequest.pending.sentSubRav?.subRav;
    if (prev) {
      try {
        assertSubRavProgression(prev, proto.subRav, true);
      } catch (e) {
        this.requestManager.rejectByRef(
          pendingKey,
          new Error(
            "Invalid SubRAV progression: " + e.message + ", response: " + serializeJson({ subRav: proto.subRav, cost: proto.cost, costUsd: proto.costUsd }) + " prev: " + serializeJson(prev)
          )
        );
        return;
      }
    }
    this.paymentState.setPendingSubRAV(proto.subRav);
    await this.persistClientState();
    const paymentInfo = {
      clientTxRef: proto.clientTxRef || pendingKey,
      serviceTxRef: proto.serviceTxRef,
      cost: proto.cost,
      costUsd: proto.costUsd ?? BigInt(0),
      nonce: proto.subRav.nonce,
      channelId: pendingRequest.pending.channelId,
      vmIdFragment: proto.subRav.vmIdFragment,
      assetId: pendingRequest.pending.assetId,
      timestamp: (/* @__PURE__ */ new Date()).toISOString()
    };
    this.requestManager.resolveByRef(pendingKey, paymentInfo);
    try {
      if (this.options.transactionLog?.enabled !== false && this.transactionStore) {
        await this.transactionStore.update(paymentInfo.clientTxRef, {
          payment: {
            cost: paymentInfo.cost,
            costUsd: paymentInfo.costUsd,
            nonce: paymentInfo.nonce,
            serviceTxRef: paymentInfo.serviceTxRef
          },
          vmIdFragment: paymentInfo.vmIdFragment,
          status: "paid"
        });
      }
    } catch (e) {
      this.log("[txlog.finalize.error]", e);
    }
    this.log(
      "Resolved payment for clientTxRef:",
      paymentInfo.clientTxRef,
      "cost:",
      paymentInfo.cost.toString()
    );
    this.paymentState.updateHighestNonce(paymentInfo.nonce);
  }
  async handleNoProtocolHeader(response, context) {
    if (isStreamLikeResponse(response)) {
      return;
    }
    this.requestManager.resolveAllAsFree();
    try {
      if (this.options.transactionLog?.enabled !== false && this.transactionStore && context?.clientTxRef) {
        await this.transactionStore.update(context.clientTxRef, { status: "free" });
      }
    } catch (e) {
      this.log("[txlog.free.update.error]", e);
    }
    const error = this.paymentProtocol.handleStatusCode(response.status);
    if (error) {
      if (response.status === 402 || response.status === 409) {
        this.paymentState.clearPendingSubRAV();
        await this.persistClientState();
      }
      throw error;
    }
  }
  /**
   * Find matching pending payment for a protocol success response
   */
  findMatchingPendingPayment(proto) {
    const pendingPayments = this.paymentState.getAllPendingPayments();
    if (proto.clientTxRef && pendingPayments.has(proto.clientTxRef)) {
      return {
        key: proto.clientTxRef,
        pending: pendingPayments.get(proto.clientTxRef)
      };
    }
    if (pendingPayments.size === 1) {
      const [[key, pending]] = pendingPayments.entries();
      return { key, pending };
    }
    if (pendingPayments.size > 1) {
      for (const [k, p] of pendingPayments.entries()) {
        const prevSent = p.sentSubRav?.subRav;
        if (!prevSent) continue;
        try {
          assertSubRavProgression(prevSent, proto.subRav, true);
          return { key: k, pending: p };
        } catch {
        }
      }
    }
    if (pendingPayments.size >= 1) {
      const all = Array.from(pendingPayments.entries());
      const [key, pending] = all[all.length - 1];
      return { key, pending };
    }
    return void 0;
  }
  /**
   * Log transaction details
   */
  async logTransaction(clientTxRef, context, sentedSubRav) {
    if (this.options.transactionLog?.enabled === false || !this.transactionStore) {
      return;
    }
    const urlObj = new URL(context.url);
    const sanitize = this.options.transactionLog?.sanitizeRequest;
    const sanitized = sanitize ? sanitize(context.headers, context.body) : void 0;
    const headersSummary = sanitized?.headersSummary ?? {
      "content-type": context.headers["Content-Type"] || context.headers["content-type"] || ""
    };
    await this.transactionStore.create({
      clientTxRef,
      timestamp: Date.now(),
      protocol: "http",
      method: context.method,
      urlOrTarget: context.url,
      operation: `${context.method}:${urlObj.pathname}`,
      headersSummary,
      requestBodyHash: sanitized?.requestBodyHash,
      stream: false,
      channelId: this.paymentState.getChannelId(),
      vmIdFragment: sentedSubRav?.subRav?.vmIdFragment,
      assetId: this.options.defaultAssetId || "0x3::gas_coin::RGas",
      status: "pending"
    });
  }
  /**
   * Log trace origin for debugging
   */
  logTraceOrigin(clientTxRef) {
    try {
      if (clientTxRef) {
        const origin = this.requestManager.getTraceOrigin(clientTxRef);
        if (origin) {
          this.log("[trace.origin]", clientTxRef, "\n", origin);
        }
        this.requestManager.clearTraceOrigin(clientTxRef);
      } else {
        const snapshots = this.requestManager.getTraceSnapshots(5);
        if (snapshots.length > 0) {
          this.log("[trace.origin.scan]", JSON.stringify(snapshots));
        }
      }
    } catch {
    }
  }
  /**
   * Persist client state
   */
  async persistClientState() {
    try {
      const mappingStore = this.options.mappingStore || createDefaultMappingStore();
      const namespacedStore = createNamespacedMappingStore(mappingStore, {
        getPayerDid: async () => this.options.payerDid || await this.options.signer.getDid()
      });
      const state = this.paymentState.getPersistedState();
      await namespacedStore.setState(this.host, state);
      this.log("Persisted client state");
    } catch (error) {
      this.log("Failed to persist client state:", error);
    }
  }
};

// src/integrations/http/PaymentChannelAdminClient.ts
var PaymentChannelAdminClient = class {
  constructor(httpClient) {
    this.httpClient = httpClient;
  }
  /**
   * Build URL for payment-related endpoints (internal use)
   */
  buildPaymentUrl(endpoint) {
    return this.httpClient.buildPaymentUrl(endpoint);
  }
  /**
   * Health check endpoint (public, no auth required)
   */
  async healthCheck() {
    return this.httpClient.healthCheck();
  }
  /**
   * Get system internal status (admin only)
   */
  async getSystemStatus() {
    const result = await this.httpClient.get(
      this.buildPaymentUrl("/admin/status")
    );
    return result.data;
  }
  /**
   * Manually trigger a claim for a specific channel (admin only)
   */
  async triggerClaim(request) {
    const result = await this.httpClient.post(
      this.buildPaymentUrl("/admin/claim-trigger"),
      request
    );
    return result.data;
  }
  /**
   * Query SubRAV details (requires auth, users can only query their own)
   */
  async querySubRav(request) {
    const queryPath = `/subrav?nonce=${encodeURIComponent(request.nonce)}`;
    const result = await this.httpClient.get(this.buildPaymentUrl(queryPath));
    return result.data;
  }
  /**
   * Discover service information
   */
  async discoverService() {
    return this.httpClient.discoverService();
  }
};
function createAdminClient(httpClient) {
  return new PaymentChannelAdminClient(httpClient);
}

// src/helpers/fromIdentityEnv.ts
function getChainConfigFromEnv(env) {
  const roochVdr = env.registry.getVDR("rooch");
  if (!roochVdr) {
    throw new Error(
      'RoochVDR not registered in IdentityEnv. Please ensure bootstrapIdentityEnv was called with method="rooch" or useVDR("rooch") was called.'
    );
  }
  const vdrOptions = roochVdr.options || {};
  const rpcUrl = vdrOptions.rpcUrl || void 0;
  return {
    chain: "rooch",
    rpcUrl,
    network: vdrOptions.network || "test",
    debug: vdrOptions.debug || false
  };
}

// src/integrations/http/factory.ts
async function createHttpClient(options) {
  const chainConfig = getChainConfigFromEnv(options.env);
  let keyId;
  const keyIds = await options.env.keyManager.listKeyIds?.();
  if (keyIds && keyIds.length > 0) {
    keyId = keyIds[0];
  } else {
    throw new Error("No keyId available");
  }
  const httpPayerOptions = {
    baseUrl: options.baseUrl,
    chainConfig,
    signer: options.env.keyManager,
    keyId,
    maxAmount: options.maxAmount || BigInt("500000000000"),
    // Default: 50 cents USD
    debug: options.debug ?? chainConfig.debug,
    onError: options.onError,
    fetchImpl: options.fetchImpl,
    mappingStore: options.mappingStore,
    channelRepo: options.channelRepo,
    timeoutMs: options.timeoutMs,
    timeoutMsStream: options.timeoutMsStream
  };
  const client = new PaymentChannelHttpClient(httpPayerOptions);
  return client;
}

// src/integrations/mcp/types.ts
var McpServerType = /* @__PURE__ */ ((McpServerType2) => {
  McpServerType2["PAYMENT_ENABLED"] = "payment";
  McpServerType2["STANDARD"] = "standard";
  McpServerType2["UNKNOWN"] = "unknown";
  return McpServerType2;
})(McpServerType || {});

// src/integrations/mcp/ServerDetector.ts
import { DebugLogger as DebugLogger11 } from "@nuwa-ai/identity-kit";
import { Client as McpClient } from "@modelcontextprotocol/sdk/client/index.js";
import { StreamableHTTPClientTransport } from "@modelcontextprotocol/sdk/client/streamableHttp.js";
var ServerDetector = class {
  constructor(options = {}) {
    this.options = options;
    this.cache = /* @__PURE__ */ new Map();
    this.logger = DebugLogger11.get("ServerDetector");
  }
  /**
   * Detect server capabilities and type
   * @param baseUrl - MCP server base URL
   * @returns Detection result with server type and capabilities
   */
  async detectCapabilities(baseUrl) {
    const cacheKey = baseUrl;
    if (this.options.cache !== false && this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.detectedAt < 5 * 60 * 1e3) {
        this.logger.debug("Using cached detection result", { baseUrl });
        return cached;
      }
    }
    this.logger.debug("Detecting server capabilities", { baseUrl });
    try {
      const paymentInfo = await this.checkWellKnownEndpoint(baseUrl);
      const mcpCapabilities = await this.getMcpCapabilities(baseUrl);
      const result = this.combineCapabilities(paymentInfo, mcpCapabilities);
      if (this.options.cache !== false) {
        this.cache.set(cacheKey, result);
      }
      this.logger.debug("Detection completed", {
        baseUrl,
        type: result.type,
        hasPaymentInfo: !!result.paymentInfo
      });
      return result;
    } catch (error) {
      this.logger.warn("Detection failed, falling back to standard MCP", {
        baseUrl,
        error: error instanceof Error ? error.message : String(error)
      });
      const fallbackResult = {
        type: "standard" /* STANDARD */,
        capabilities: {},
        detectedAt: Date.now()
      };
      if (this.options.cache !== false) {
        this.cache.set(cacheKey, fallbackResult);
      }
      return fallbackResult;
    }
  }
  /**
   * Check for Nuwa payment protocol support via well-known endpoint
   * @param baseUrl - MCP server base URL
   * @returns Payment info if supported, null otherwise
   */
  async checkWellKnownEndpoint(baseUrl) {
    try {
      const url = new URL("/.well-known/nuwa-payment/info", baseUrl);
      const fetchImpl = this.options.fetchImpl || globalThis.fetch?.bind(globalThis);
      if (!fetchImpl) {
        this.logger.debug("No fetch implementation available");
        return null;
      }
      const controller = new AbortController();
      const timeout = this.options.timeout || 5e3;
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      try {
        const response = await fetchImpl(url.toString(), {
          method: "GET",
          headers: {
            Accept: "application/json",
            "User-Agent": "nuwa-payment-kit/1.0.0"
          },
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        if (response.ok) {
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            const paymentInfo = await response.json();
            if (paymentInfo.serviceId && paymentInfo.serviceDid && paymentInfo.defaultAssetId) {
              this.logger.debug("Found Nuwa payment info", {
                serviceId: paymentInfo.serviceId,
                serviceDid: paymentInfo.serviceDid,
                defaultAssetId: paymentInfo.defaultAssetId
              });
              return paymentInfo;
            } else {
              this.logger.warn("Invalid payment info format", { paymentInfo });
            }
          }
        } else {
          this.logger.debug("Well-known endpoint returned non-OK status", {
            status: response.status,
            statusText: response.statusText
          });
        }
      } catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === "AbortError") {
          this.logger.debug("Well-known endpoint request timed out");
        } else {
          this.logger.debug("Well-known endpoint request failed", {
            error: error instanceof Error ? error.message : String(error)
          });
        }
      }
    } catch (error) {
      this.logger.debug("Failed to check well-known endpoint", {
        error: error instanceof Error ? error.message : String(error)
      });
    }
    return null;
  }
  /**
   * Get standard MCP server capabilities
   * @param baseUrl - MCP server base URL
   * @returns MCP capabilities or empty object if failed
   */
  async getMcpCapabilities(baseUrl) {
    let mcpClient;
    try {
      const transport = new StreamableHTTPClientTransport(new URL(baseUrl));
      if (typeof transport.setProtocolVersion === "function") {
        transport.setProtocolVersion("2025-06-18");
      }
      mcpClient = new McpClient({
        name: "nuwa-detector-client",
        version: "1.0.0"
      });
      const controller = new AbortController();
      const timeout = this.options.timeout || 5e3;
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      try {
        await mcpClient.connect(transport);
        clearTimeout(timeoutId);
        const capabilities = mcpClient.serverCapabilities || {};
        this.logger.debug("Retrieved MCP capabilities", { capabilities });
        return capabilities;
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      }
    } catch (error) {
      this.logger.debug("Failed to get MCP capabilities", {
        error: error instanceof Error ? error.message : String(error)
      });
      return {};
    } finally {
      if (mcpClient) {
        try {
          await mcpClient.close();
        } catch (error) {
        }
      }
    }
  }
  /**
   * Combine payment info and MCP capabilities to determine server type
   * @param paymentInfo - Nuwa payment info if available
   * @param mcpCapabilities - Standard MCP capabilities
   * @returns Combined detection result
   */
  combineCapabilities(paymentInfo, mcpCapabilities) {
    const hasPaymentSupport = !!paymentInfo;
    const serverType = hasPaymentSupport ? "payment" /* PAYMENT_ENABLED */ : "standard" /* STANDARD */;
    const enhancedCapabilities = {
      ...mcpCapabilities,
      nuwa: {
        payment: hasPaymentSupport ? {
          supported: true,
          serviceId: paymentInfo.serviceId,
          serviceDid: paymentInfo.serviceDid,
          defaultAssetId: paymentInfo.defaultAssetId,
          basePath: paymentInfo.basePath,
          protocolVersion: paymentInfo.protocolVersion
        } : {
          supported: false
        },
        auth: {
          supported: hasPaymentSupport,
          // Auth is typically required for payment
          methods: hasPaymentSupport ? ["did-auth"] : []
        },
        builtinTools: {
          supported: hasPaymentSupport,
          // Built-in tools are part of payment protocol
          tools: hasPaymentSupport ? [
            "nuwa.health",
            "nuwa.discovery",
            "nuwa.recovery",
            "nuwa.admin.status",
            "nuwa.subrav.query"
          ] : []
        }
      }
    };
    return {
      type: serverType,
      capabilities: enhancedCapabilities,
      paymentInfo: paymentInfo || void 0,
      detectedAt: Date.now()
    };
  }
  /**
   * Clear detection cache
   */
  clearCache() {
    this.cache.clear();
    this.logger.debug("Detection cache cleared");
  }
  /**
   * Get cache size for debugging
   */
  getCacheSize() {
    return this.cache.size;
  }
};

// src/integrations/mcp/PaymentChannelMcpClient.ts
import { DebugLogger as DebugLogger13, DIDAuth as DIDAuth3 } from "@nuwa-ai/identity-kit";
import { Client as McpClient2 } from "@modelcontextprotocol/sdk/client/index.js";
import { StreamableHTTPClientTransport as StreamableHTTPClientTransport2 } from "@modelcontextprotocol/sdk/client/streamableHttp.js";

// src/integrations/mcp/McpChannelManager.ts
import { DebugLogger as DebugLogger12, DIDAuth as DIDAuth2 } from "@nuwa-ai/identity-kit";
var McpChannelManager = class {
  constructor(options) {
    this.options = options;
    this.logger = DebugLogger12.get("McpChannelManager");
  }
  async ensureChannelReady() {
    if (!this.ensureReadyPromise) {
      this.ensureReadyPromise = this.doEnsureChannelReady().finally(() => {
        this.ensureReadyPromise = void 0;
      });
    }
    await this.ensureReadyPromise;
  }
  async doEnsureChannelReady() {
    await this.ensureKeyFragmentPresent();
    let channelId = this.options.paymentState.getChannelId();
    if (channelId) {
      try {
        const info = await this.options.payerClient.getChannelInfo(channelId);
        this.options.paymentState.setChannelInfo(info);
      } catch (e) {
        this.logger.debug("Persisted channel not found, clearing and recovering:", e);
        this.options.paymentState.setChannelId(void 0);
        this.options.paymentState.setChannelInfo(void 0);
        channelId = void 0;
      }
    }
    if (!channelId) {
      await this.createNewChannel();
      channelId = this.options.paymentState.getChannelId();
    }
    const vmIdFragment = this.options.paymentState.getVmIdFragment();
    if (channelId && vmIdFragment) {
      try {
        const subInfo = await this.options.payerClient.getSubChannelInfo(channelId, vmIdFragment);
        this.options.paymentState.setSubChannelInfo(subInfo);
      } catch {
        this.logger.debug("ensureChannelReady: subChannel missing, authorizing directly");
        await this.directAuthorizeSubChannel();
      }
    }
  }
  async createNewChannel() {
    if (!this.payeeDid) {
      const service = await this.discoverService();
      this.payeeDid = service.serviceDid;
    }
    if (!this.defaultAssetId) {
      const service = await this.discoverService();
      this.defaultAssetId = service.defaultAssetId;
    }
    this.logger.debug("createNewChannel: opening new channel", {
      payeeDid: this.payeeDid,
      defaultAssetId: this.defaultAssetId
    });
    if (!this.payeeDid || !this.defaultAssetId) {
      this.logger.debug("createNewChannel: missing payeeDid, cannot create channel");
      throw new Error("payeeDid or defaultAssetId missing for channel creation");
    }
    try {
      const opened = await this.options.payerClient.openChannelWithSubChannel({
        payeeDid: this.payeeDid,
        assetId: this.defaultAssetId
      });
      this.options.paymentState.setChannelId(opened.channelId);
      if (opened.channelInfo) this.options.paymentState.setChannelInfo(opened.channelInfo);
      if (opened.subChannelInfo) this.options.paymentState.setSubChannelInfo(opened.subChannelInfo);
      this.logger.debug("createNewChannel: channel opened", { channelId: opened.channelId });
    } catch (err) {
      this.logger.debug("createNewChannel: openChannelWithSubChannel failed", err);
      throw err;
    }
  }
  async ensureKeyFragmentPresent() {
    if (this.options.paymentState.getKeyId() && this.options.paymentState.getVmIdFragment()) return;
    let keyId = this.options.keyId;
    if (!keyId && this.options.signer?.listKeyIds) {
      try {
        const ids = await this.options.signer.listKeyIds();
        keyId = Array.isArray(ids) && ids.length > 0 ? ids[0] : void 0;
      } catch {
      }
    }
    if (keyId) {
      const parts = keyId.split("#");
      const frag = parts.length > 1 ? parts[1] : void 0;
      if (frag) this.options.paymentState.setKeyInfo(keyId, frag);
    }
    if (!this.options.paymentState.getKeyId() || !this.options.paymentState.getVmIdFragment()) {
      throw new Error("No keyId found");
    }
  }
  async discoverService() {
    if (!this.cachedDiscovery) {
      await this.performDiscovery();
    }
    if (this.cachedDiscovery) return this.cachedDiscovery;
    throw new Error("Service discovery failed");
  }
  async performDiscovery() {
    const discoveryUrl = new URL("/.well-known/nuwa-payment/info", this.options.baseUrl);
    const fetchImpl = this.options.fetchImpl || globalThis.fetch?.bind(globalThis);
    if (!fetchImpl) return;
    try {
      const res = await fetchImpl(discoveryUrl.toString(), {
        method: "GET",
        headers: { Accept: "application/json" }
      });
      if (res.ok) {
        const info = await res.json();
        this.cachedDiscovery = info;
        if (info.basePath) this.discoveredBasePath = info.basePath;
      }
    } catch (e) {
      this.logger.debug("MCP discovery failed:", e);
    }
    if (!this.discoveredBasePath) this.discoveredBasePath = "/payment-channel";
  }
  async recoverFromService() {
    return this.doRecoverFromService();
  }
  async doRecoverFromService() {
    const clientTxRef = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const auth = await this.generateAuthToken("nuwa.recovery", clientTxRef).catch(() => void 0);
    const result = await this.options.mcpCall("nuwa.recovery", auth ? { __nuwa_auth: auth } : {});
    let payload = result;
    if (result && typeof result === "object" && Array.isArray(result.content)) {
      const contents = result.content;
      try {
        const payment = HttpPaymentCodec.parseMcpPaymentFromContents(contents);
        if (payment?.subRav) {
          const sub = HttpPaymentCodec.deserializeSubRAV(payment.subRav);
          this.options.paymentState.setPendingSubRAV(sub);
        }
      } catch {
      }
      const text = contents.find((c) => c?.type === "text");
      if (text?.text) {
        try {
          payload = JSON.parse(text.text);
        } catch {
        }
      }
    }
    return payload;
  }
  async commitSubRAV(signedSubRAV) {
    const clientTxRef = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const auth = await this.generateAuthToken("nuwa.commit", clientTxRef).catch(() => void 0);
    await this.options.mcpCall("nuwa.commit", {
      signedSubRav: HttpPaymentCodec.serializeSignedSubRAV(signedSubRAV),
      __nuwa_auth: auth
    });
    this.options.paymentState.clearPendingSubRAV();
  }
  async applyRecovery(recovery, options) {
    if (recovery.channel) {
      this.options.paymentState.setChannelId(recovery.channel.channelId);
      try {
        const info = await this.options.payerClient.getChannelInfo(recovery.channel.channelId);
        this.options.paymentState.setChannelInfo(info);
      } catch (e) {
        this.logger.debug("GetChannelInfo failed during MCP recovery:", e);
      }
    }
    if (recovery.pendingSubRav) {
      try {
        const sub = HttpPaymentCodec.deserializeSubRAV(recovery.pendingSubRav);
        this.options.paymentState.setPendingSubRAV(sub);
      } catch {
      }
    }
    if (options?.authorizeIfMissing && recovery.channel) {
      await this.ensureSubChannelAuthorized(recovery, options.requireVmFragment);
    }
  }
  async ensureSubChannelAuthorized(recovery, requireVmFragment) {
    const channelId = this.options.paymentState.getChannelId();
    if (!channelId) return;
    let vmIdFragment = recovery.subChannel?.vmIdFragment || this.options.paymentState.getVmIdFragment();
    if (!vmIdFragment && requireVmFragment) {
      if (this.options.keyId) {
        const parts = this.options.keyId.split("#");
        vmIdFragment = parts.length > 1 ? parts[1] : "";
      }
      if (!vmIdFragment) throw new Error("Missing vmIdFragment for sub-channel authorization");
    }
    if (vmIdFragment && !recovery.subChannel) {
      const auth = await this.options.payerClient.authorizeSubChannel({ channelId, vmIdFragment });
      this.options.paymentState.setSubChannelInfo(auth.subChannelInfo);
    }
  }
  async directAuthorizeSubChannel() {
    const channelId = this.options.paymentState.getChannelId();
    const vmIdFragment = this.options.paymentState.getVmIdFragment();
    if (!channelId || !vmIdFragment) return;
    const auth = await this.options.payerClient.authorizeSubChannel({ channelId, vmIdFragment });
    this.options.paymentState.setSubChannelInfo(auth.subChannelInfo);
  }
  async generateAuthToken(method, clientTxRef) {
    let keyId = this.options.keyId;
    if (!keyId && this.options.signer?.listKeyIds) {
      try {
        const ids = await this.options.signer.listKeyIds();
        keyId = Array.isArray(ids) && ids.length > 0 ? ids[0] : void 0;
      } catch {
      }
    }
    if (!keyId) throw new Error("No keyId for DIDAuth");
    const signedObject = await DIDAuth2.v1.createSignature(
      {
        operation: "mcp_tool_call",
        params: { tool: method, clientTxRef }
      },
      this.options.signer,
      keyId
    );
    return DIDAuth2.v1.toAuthorizationHeader(signedObject);
  }
  getDefaultAssetId() {
    return this.defaultAssetId;
  }
};

// src/integrations/mcp/McpToolConverter.ts
import { dynamicTool, jsonSchema } from "ai";
var McpToolConverter = class {
  /**
   * Convert MCP tool definition to AI SDK compatible format
   * @param tool - MCP tool definition
   * @param executeFunction - Function to execute the tool
   * @returns AI SDK compatible tool with proper typing
   */
  static convertToAiSdkFormat(tool, executeFunction) {
    let rawSchema = tool.inputSchema || tool.parameters || tool.input_schema || {};
    if (rawSchema && typeof rawSchema === "object" && rawSchema.jsonSchema) {
      rawSchema = rawSchema.jsonSchema;
    }
    const processedSchema = {
      ...rawSchema,
      properties: rawSchema.properties ?? {},
      additionalProperties: false
    };
    return dynamicTool({
      description: tool.description || `Tool: ${tool.name}`,
      inputSchema: jsonSchema(processedSchema),
      execute: executeFunction
    });
  }
};

// src/integrations/mcp/PaymentChannelMcpClient.ts
var PaymentChannelMcpClient = class {
  constructor(options) {
    this.notificationsSubscribed = false;
    this.scheduler = new RequestScheduler();
    this.options = options;
    this.logger = DebugLogger13.get("PaymentChannelMcpClient");
    this.logger.setLevel(options.debug ? "debug" : "info");
    this.paymentState = new PaymentState();
    this.host = extractHost(options.baseUrl);
    const baseMapping = options.mappingStore || createDefaultMappingStore();
    this.mappingStore = createNamespacedMappingStore(baseMapping, {
      getPayerDid: async () => await options.signer.getDid()
    });
    let channelRepo;
    if (options.storageOptions?.channelRepo) {
      channelRepo = options.storageOptions.channelRepo;
    } else {
      channelRepo = createDefaultChannelRepo();
    }
    this.payerClient = PaymentChannelFactory.createClient({
      chainConfig: options.chainConfig,
      signer: options.signer,
      keyId: options.keyId,
      storageOptions: {
        channelRepo
      }
    });
    this.transactionStore = options.transactionStore || createDefaultTransactionStore();
    this.channelManager = new McpChannelManager({
      baseUrl: options.baseUrl,
      payerClient: this.payerClient,
      paymentState: this.paymentState,
      signer: options.signer,
      keyId: options.keyId,
      fetchImpl: globalThis.fetch?.bind(globalThis),
      mcpCall: async (name, params) => {
        const client = await this.ensureClient();
        const res = await client.callTool({ name, arguments: params || {} });
        return res;
      },
      autoRecover: false
    });
    void this.loadPersistedState();
  }
  async call(method, params, schemaOrOptions) {
    let schema;
    let clientTxRef;
    if (schemaOrOptions && typeof schemaOrOptions === "object" && // Ensure it's not a Zod schema (Zod schemas have a 'safeParse' function)
    !(typeof schemaOrOptions.safeParse === "function" || typeof schemaOrOptions.parse === "function") && "clientTxRef" in schemaOrOptions) {
      clientTxRef = schemaOrOptions.clientTxRef;
      schema = schemaOrOptions.schema;
    } else {
      schema = schemaOrOptions;
    }
    const { content, payment } = await this.callToolWithPayment(method, params, clientTxRef);
    const raw = this.parseFirstJsonText(content);
    let data = void 0;
    if (schema) {
      try {
        data = schema.parse(raw);
      } catch (e) {
        this.logger.warn("Failed to parse response", {
          cause: e,
          raw,
          schema
        });
        throw new PaymentKitError("RESPONSE_PARSE_ERROR", "Failed to parse response", 500, {
          cause: e,
          raw,
          schema
        });
      }
    } else {
      data = raw;
    }
    return { data, payment };
  }
  async callTool(name, args, optionsOrClientTxRef) {
    let clientTxRef;
    if (typeof optionsOrClientTxRef === "string") {
      clientTxRef = optionsOrClientTxRef;
    } else if (optionsOrClientTxRef && typeof optionsOrClientTxRef === "object" && "clientTxRef" in optionsOrClientTxRef) {
      clientTxRef = optionsOrClientTxRef.clientTxRef;
    }
    const { content } = await this.callToolWithPayment(name, args, clientTxRef);
    return { content };
  }
  /**
   * List prompts exposed by the server (FREE, no payment).
   */
  async listPrompts() {
    const client = await this.ensureClient();
    if (typeof client.listPrompts === "function") {
      return await client.listPrompts();
    }
    if (typeof client.prompts === "function") {
      return await client.prompts();
    }
    return {};
  }
  /**
   * Load a prompt by name and return string content (FREE, no payment).
   */
  async loadPrompt(name, args) {
    const client = await this.ensureClient();
    if (typeof client.getPrompt === "function") {
      const res = await client.getPrompt({ name, arguments: args || {} });
      return this.extractStringFromPromptResult(res);
    }
    if (typeof client.prompts === "function") {
      const prompts = await client.prompts();
      const p = prompts?.[name];
      if (p && typeof p.load === "function") {
        const out = await p.load(args || {});
        return typeof out === "string" ? out : JSON.stringify(out);
      }
    }
    throw new Error(`Prompt '${name}' not available`);
  }
  /**
   * List resources (FREE, no payment).
   */
  async listResources() {
    const client = await this.ensureClient();
    if (typeof client.listResources === "function") {
      return await client.listResources();
    }
    return [];
  }
  /**
   * List resource templates (FREE, no payment).
   */
  async listResourceTemplates() {
    const client = await this.ensureClient();
    if (typeof client.listResourceTemplates === "function") {
      return await client.listResourceTemplates();
    }
    return [];
  }
  /**
   * Read a resource. Accepts either uri string or parameter object.
   */
  async readResource(params) {
    const client = await this.ensureClient();
    const p = typeof params === "string" ? { uri: params } : params;
    if (typeof client.readResource === "function") {
      const res = await client.readResource(p);
      return this.normalizeResourceResult(res);
    }
    throw new Error("readResource not supported by underlying MCP client");
  }
  /**
   * Returns tools exposed by the server with internal parameters filtered out for public consumption.
   * @param options - Options for filtering tools, or boolean for backward compatibility
   */
  async listTools(options) {
    const raw = await this.listToolsInternal();
    const sanitized = this.sanitizeTools(raw);
    const { includeBuiltinTools = false } = options || {};
    return includeBuiltinTools ? sanitized : this.filterBuiltinTools(sanitized);
  }
  /**
   * Internal listTools that returns server's raw schemas (may include SDK-internal parameters like __nuwa_auth and __nuwa_payment).
   */
  async listToolsInternal() {
    const client = await this.ensureClient();
    if (typeof client.listTools === "function") {
      return await client.listTools();
    }
    return {};
  }
  /** Remove SDK-internal parameters from tool schemas (e.g., __nuwa_auth, __nuwa_payment). */
  sanitizeTools(tools) {
    const sanitizeSchema = (schema) => {
      try {
        if (!schema || typeof schema !== "object") return schema;
        const out = { ...schema };
        const props = schema.properties;
        if (props && typeof props === "object") {
          const filtered = {};
          for (const [k, v] of Object.entries(props)) {
            if (typeof k === "string" && k.startsWith("__nuwa")) continue;
            filtered[k] = v;
          }
          out.properties = filtered;
          if (Array.isArray(out.required)) {
            out.required = out.required.filter(
              (k) => typeof k !== "string" || !k.startsWith("__nuwa")
            );
          }
        }
        return out;
      } catch {
        return schema;
      }
    };
    const sanitizeTool = (t) => {
      const inputSchemaKey = t?.inputSchema ? "inputSchema" : t?.parameters ? "parameters" : t?.input_schema ? "input_schema" : void 0;
      if (!inputSchemaKey) return t;
      const copy = { ...t };
      copy[inputSchemaKey] = sanitizeSchema(t[inputSchemaKey]);
      return copy;
    };
    try {
      if (tools && Array.isArray(tools.tools)) {
        return { tools: tools.tools.map(sanitizeTool) };
      }
      if (Array.isArray(tools)) {
        return tools.map(sanitizeTool);
      }
      if (tools && typeof tools === "object") {
        const out = {};
        for (const [name, v] of Object.entries(tools)) {
          out[name] = sanitizeTool({ name, ...v });
        }
        return out;
      }
    } catch {
    }
    return tools;
  }
  /** Filter out nuwa built-in tools that are not useful for AI consumption */
  filterBuiltinTools(tools) {
    const isBuiltinTool = (toolName) => {
      return typeof toolName === "string" && toolName.startsWith("nuwa.");
    };
    try {
      if (tools && Array.isArray(tools.tools)) {
        const filtered = tools.tools.filter((tool) => {
          return !(tool && typeof tool === "object" && tool.name && isBuiltinTool(tool.name));
        });
        return { tools: filtered };
      }
      if (Array.isArray(tools)) {
        return tools.filter((tool) => {
          return !(tool && typeof tool === "object" && tool.name && isBuiltinTool(tool.name));
        });
      }
      if (tools && typeof tools === "object") {
        const filtered = {};
        for (const [name, toolDef] of Object.entries(tools)) {
          if (!isBuiltinTool(name)) {
            filtered[name] = toolDef;
          }
        }
        return filtered;
      }
    } catch {
    }
    return tools;
  }
  /**
   * Call a tool with payment
   * @param method - The name of the tool to call
   * @param params - The parameters to pass to the tool
   * @param clientTxRef - Optional custom client transaction reference. If not provided, a UUID will be generated
   * @returns The content of the tool call and the payment info
   */
  async callToolWithPayment(method, params, clientTxRef) {
    const handle = this.scheduler.enqueue(async (_release, signal) => {
      const startTs = Date.now();
      let createdLog = false;
      const txEnabled = this.options.transactionLog?.enabled !== false;
      if (signal.aborted) throw new Error("Request aborted");
      const txRef = clientTxRef || crypto.randomUUID();
      const client = await this.ensureClient();
      if (signal.aborted) throw new Error("Request aborted");
      await this.ensureNotificationSubscription();
      const infoTools = /* @__PURE__ */ new Set([
        "nuwa.health",
        "nuwa.discovery",
        "nuwa.recovery",
        "nuwa.admin.status",
        "nuwa.subrav.query"
      ]);
      if (!infoTools.has(method)) {
        await this.ensureChannelReady();
      }
      if (signal.aborted) throw new Error("Request aborted");
      const reqParams = await this.buildParams(method, params, txRef);
      if (txEnabled) {
        try {
          await this.logTransactionCreate(txRef, method, reqParams);
          createdLog = true;
        } catch (e) {
          this.logger.debug("txlog.create.failed", e);
        }
      }
      let result = await client.callTool({ name: method, arguments: reqParams });
      let content = [];
      let paymentPayload;
      this.lastContents = void 0;
      if (result && typeof result === "object" && result.content && Array.isArray(result.content)) {
        const parsed = this.extractPaymentFromContents(result.content);
        content = parsed.clean;
        paymentPayload = parsed.payload;
      }
      const maybePayment = paymentPayload;
      if (maybePayment && maybePayment.error?.code === "PAYMENT_REQUIRED" && maybePayment.subRav) {
        try {
          this.logger.debug("retry call", {
            method,
            clientTxRef: txRef
          });
          const subRav = HttpPaymentCodec.deserializeSubRAV(maybePayment.subRav);
          const signed = await this.payerClient.signSubRAV(subRav);
          const retryParams = await this.buildParams(method, params, txRef);
          const newReqPayload = {
            version: 1,
            clientTxRef: txRef,
            maxAmount: this.options.maxAmount,
            signedSubRav: signed
          };
          retryParams.__nuwa_payment = HttpPaymentCodec.toJSONRequest(newReqPayload);
          result = await client.callTool({ name: method, arguments: retryParams });
          if (result && typeof result === "object" && result.content && Array.isArray(result.content)) {
            const parsed2 = this.extractPaymentFromContents(result.content);
            content = parsed2.clean;
            paymentPayload = parsed2.payload;
          }
        } catch {
          this.logger.debug("retry call failed", {
            method,
            clientTxRef: txRef
          });
        }
      }
      if (paymentPayload && paymentPayload.error) {
        const err = paymentPayload.error;
        const svc = paymentPayload.serviceTxRef;
        const cRef = paymentPayload.clientTxRef ?? txRef;
        const code = err?.code || "PAYMENT_ERROR";
        const message = err?.message || "Payment negotiation failed";
        const details = { code, message, clientTxRef: cRef, serviceTxRef: svc };
        this.logger.debug("payment error", {
          method,
          clientTxRef: cRef,
          serviceTxRef: svc,
          paymentPayload
        });
        const errObj = new PaymentKitError(code, message, 402, details);
        if (txEnabled && createdLog) {
          try {
            const durationMs = Date.now() - startTs;
            await this.transactionStore.update(cRef, {
              durationMs,
              status: "error",
              errorCode: code,
              errorMessage: message
            });
          } catch (e) {
            this.logger.debug("txlog.update.error", e);
          }
        }
        throw errObj;
      }
      const paymentInfo = await this.toPaymentInfoFromPayload(paymentPayload, txRef);
      if (txEnabled && createdLog) {
        try {
          const durationMs = Date.now() - startTs;
          if (paymentInfo) {
            await this.transactionStore.update(paymentInfo.clientTxRef, {
              durationMs,
              status: "paid",
              payment: {
                cost: paymentInfo.cost,
                costUsd: paymentInfo.costUsd,
                nonce: paymentInfo.nonce,
                serviceTxRef: paymentInfo.serviceTxRef
              },
              vmIdFragment: paymentInfo.vmIdFragment
            });
          } else {
            await this.transactionStore.update(txRef, {
              durationMs,
              status: "free"
            });
          }
        } catch (e) {
          this.logger.debug("txlog.update.error", e);
        }
      }
      return { content, payment: paymentInfo };
    });
    return await handle.promise;
  }
  getLastContents() {
    return this.lastContents;
  }
  getLastPaymentPayload() {
    return this.lastPaymentPayload;
  }
  getPendingSubRAV() {
    return this.paymentState.getPendingSubRAV() || null;
  }
  clearPendingSubRAV() {
    this.paymentState.clearPendingSubRAV();
    void this.persistClientState();
  }
  parseFirstJsonText(content) {
    const dataItem = Array.isArray(content) ? content.find((c) => c?.type === "text") || content[0] : void 0;
    if (dataItem && dataItem.type === "text" && typeof dataItem.text === "string") {
      try {
        return JSON.parse(dataItem.text);
      } catch {
        return dataItem.text;
      }
    }
    return void 0;
  }
  // Schema should be provided by call sites for strong typing, no internal mapping
  async buildParams(method, userParams, clientTxRef) {
    const payerDid = await this.options.signer.getDid();
    const signedSubRav = await this.buildSignedSubRavIfNeeded();
    const reqPayload = {
      version: 1,
      clientTxRef,
      maxAmount: this.options.maxAmount,
      signedSubRav: signedSubRav || void 0
    };
    const payment = HttpPaymentCodec.toJSONRequest(reqPayload);
    const __nuwa_auth = await this.generateAuthToken(payerDid, method, clientTxRef);
    const params = { ...userParams || {}, __nuwa_auth, __nuwa_payment: payment };
    try {
      this.logger.debug?.("mcp.buildParams", {
        method,
        clientTxRef,
        hasAuth: typeof __nuwa_auth === "string" && __nuwa_auth.length > 0,
        hasPayment: !!payment,
        signedSubRav
      });
    } catch {
    }
    return JSON.parse(serializeJson(params));
  }
  async buildSignedSubRavIfNeeded() {
    const pending = this.paymentState.getPendingSubRAV();
    if (!pending) return void 0;
    this.paymentState.clearPendingSubRAV();
    void this.persistClientState();
    return this.payerClient.signSubRAV(pending);
  }
  async generateAuthToken(payerDid, method, clientTxRef) {
    const keyId = this.options.keyId;
    this.logger.debug("generateAuthToken", {
      payerDid,
      method,
      clientTxRef,
      keyId
    });
    const signedObject = await DIDAuth3.v1.createSignature(
      {
        operation: "mcp_tool_call",
        params: { tool: method, clientTxRef }
      },
      this.options.signer,
      keyId
    );
    return DIDAuth3.v1.toAuthorizationHeader(signedObject);
  }
  async ensureChannelReady() {
    try {
      await this.channelManager.ensureChannelReady();
    } catch (e) {
      this.logger.debug("ensureChannelReady failed:", e);
      throw e instanceof Error ? e : new Error(String(e));
    }
  }
  async ensureClient() {
    if (this.mcpClient) return this.mcpClient;
    if (!this.options.baseUrl) {
      throw new Error("MCP baseUrl is required to create client");
    }
    let transport;
    if (this.options.customTransport) {
      transport = this.options.customTransport;
    } else {
      const httpTransport = new StreamableHTTPClientTransport2(new URL(this.options.baseUrl));
      if (typeof httpTransport.setProtocolVersion === "function") {
        httpTransport.setProtocolVersion("2025-06-18");
      }
      transport = httpTransport;
    }
    this.mcpClient = new McpClient2({
      name: "nuwa-payment-kit-client",
      version: "1.0.0"
    });
    await this.mcpClient.connect(transport);
    const tools = await this.mcpClient.listTools();
    this.logger.debug("mcp.ensureClient", {
      baseUrl: this.options.baseUrl,
      tools
    });
    return this.mcpClient;
  }
  async ensureNotificationSubscription() {
    return;
  }
  async toPaymentInfoFromPayload(payload, clientTxRef) {
    if (!payload) return void 0;
    try {
      const decoded = HttpPaymentCodec.fromJSONResponse(payload);
      if (decoded.subRav) {
        this.paymentState.setPendingSubRAV(decoded.subRav);
        void this.persistClientState();
      }
      if (decoded.error || decoded.cost === void 0 || !decoded.subRav) return void 0;
      const channelInfo = this.paymentState.getChannelInfo();
      const assetId = channelInfo?.assetId ?? "unknown";
      return {
        clientTxRef: decoded.clientTxRef ?? clientTxRef,
        serviceTxRef: decoded.serviceTxRef,
        cost: decoded.cost,
        costUsd: decoded.costUsd ?? BigInt(0),
        nonce: decoded.subRav.nonce,
        channelId: decoded.subRav.channelId,
        vmIdFragment: decoded.subRav.vmIdFragment,
        assetId,
        timestamp: (/* @__PURE__ */ new Date()).toISOString()
      };
    } catch {
      return void 0;
    }
  }
  /** Single-pass extractor: separates payment resource and returns clean contents */
  extractPaymentFromContents(contents) {
    if (!Array.isArray(contents)) return { clean: [], payload: void 0 };
    let payload;
    const clean = [];
    for (const c of contents) {
      const isPayment = c && c.type === "resource" && c.resource && (c.resource.uri === HttpPaymentCodec.MCP_PAYMENT_URI || c.resource.mimeType === HttpPaymentCodec.MCP_PAYMENT_MIME);
      if (isPayment) {
        try {
          const text = c?.resource?.text;
          if (typeof text === "string") payload = JSON.parse(text);
        } catch {
        }
        continue;
      }
      clean.push(c);
    }
    this.lastPaymentPayload = payload;
    this.lastContents = clean;
    return { clean, payload };
  }
  getPayerClient() {
    return this.payerClient;
  }
  getTransactionStore() {
    return this.transactionStore;
  }
  extractStringFromPromptResult(res) {
    if (typeof res === "string") return res;
    if (res && typeof res === "object") {
      if (Array.isArray(res.content)) {
        const textItem = res.content.find((c) => c?.type === "text");
        if (textItem?.text) return String(textItem.text);
      }
      if (typeof res.text === "string") return res.text;
    }
    try {
      return JSON.stringify(res);
    } catch {
      return String(res);
    }
  }
  normalizeResourceResult(res) {
    try {
      if (typeof res === "string") return { text: res };
      if (res && typeof res === "object") {
        if (typeof res.text === "string")
          return { text: res.text, mimeType: res.mimeType };
        if (res.type === "text" && typeof res.text === "string") return { text: res.text };
        const contents = res.contents || res.content;
        if (Array.isArray(contents)) {
          const textItem = contents.find(
            (c) => c?.type === "text" && typeof c.text === "string"
          );
          if (textItem) return { text: String(textItem.text), mimeType: textItem.mimeType };
          const anyItem = contents[0];
          if (anyItem && (anyItem.blob || anyItem.data)) {
            return { blob: anyItem.blob || anyItem.data, mimeType: anyItem.mimeType };
          }
        }
      }
    } catch {
    }
    return res;
  }
  async logTransactionCreate(clientTxRef, method, reqParams) {
    if (this.options.transactionLog?.enabled === false) return;
    const headersSummary = {
      tool: String(method)
    };
    try {
      const hasAuth = !!reqParams?.__nuwa_auth;
      const hasPayment = !!reqParams?.__nuwa_payment;
      const hasSignedSubRav = !!reqParams?.__nuwa_payment?.signedSubRav;
      headersSummary["hasAuth"] = String(hasAuth);
      headersSummary["hasPayment"] = String(hasPayment);
      headersSummary["hasSignedSubRav"] = String(hasSignedSubRav);
    } catch {
    }
    let requestBodyHash = void 0;
    try {
      const sanitize = this.options.transactionLog?.sanitizeRequest;
      if (sanitize) {
        const sanitized = sanitize(headersSummary, reqParams);
        if (sanitized?.headersSummary) {
          Object.assign(headersSummary, sanitized.headersSummary);
        }
        requestBodyHash = sanitized?.requestBodyHash;
      }
    } catch {
    }
    const channelId = this.paymentState.getChannelId();
    const vmIdFragment = this.paymentState.getVmIdFragment();
    const assetId = this.channelManager.getDefaultAssetId() || "unknown";
    await this.transactionStore.create({
      clientTxRef,
      timestamp: Date.now(),
      protocol: "mcp",
      urlOrTarget: this.options.baseUrl,
      operation: `tool:${method}`,
      headersSummary,
      requestBodyHash,
      stream: false,
      channelId,
      vmIdFragment,
      assetId,
      status: "pending"
    });
  }
  async healthCheck() {
    const res = await this.call("nuwa.health", void 0, HealthResponseSchema);
    return res.data;
  }
  async recoverFromService() {
    const res = await this.call(
      "nuwa.recovery",
      void 0,
      RecoveryResponseSchema
    );
    return res.data;
  }
  async commitSubRAV(signedSubRAV) {
    await this.channelManager.commitSubRAV(signedSubRAV);
    return { success: true };
  }
  /**
   * Get tools in AI SDK compatible format
   * This method provides compatibility with AI SDK's streamText function
   * @param options - Options for filtering tools, or boolean for backward compatibility
   * @returns A record of tool definitions compatible with AI SDK
   */
  async tools(options) {
    const rawTools = await this.listTools(options);
    const aiSdkTools = {};
    if (rawTools && typeof rawTools === "object") {
      if (Array.isArray(rawTools.tools)) {
        for (const tool of rawTools.tools) {
          if (tool && typeof tool === "object" && tool.name) {
            aiSdkTools[tool.name] = McpToolConverter.convertToAiSdkFormat(
              tool,
              async (args, options2) => {
                const clientTxRef = options2?.toolCallId;
                const { content, payment: _ } = await this.callToolWithPayment(
                  tool.name,
                  args,
                  clientTxRef
                );
                return { content };
              }
            );
          }
        }
      } else if (Array.isArray(rawTools)) {
        for (const tool of rawTools) {
          if (tool && typeof tool === "object" && tool.name) {
            aiSdkTools[tool.name] = McpToolConverter.convertToAiSdkFormat(
              tool,
              async (args, options2) => {
                const clientTxRef = options2?.toolCallId;
                const { content, payment: _ } = await this.callToolWithPayment(
                  tool.name,
                  args,
                  clientTxRef
                );
                return { content };
              }
            );
          }
        }
      } else {
        for (const [name, toolDef] of Object.entries(rawTools)) {
          if (toolDef && typeof toolDef === "object") {
            const tool = { name, ...toolDef };
            aiSdkTools[name] = McpToolConverter.convertToAiSdkFormat(
              tool,
              async (args, options2) => {
                const clientTxRef = options2?.toolCallId;
                const { content, payment: _ } = await this.callToolWithPayment(
                  tool.name,
                  args,
                  clientTxRef
                );
                return { content };
              }
            );
          }
        }
      }
    }
    return aiSdkTools;
  }
  /**
   * Close the MCP client connection and clean up resources
   */
  async close() {
    try {
      await this.persistClientState();
      if (this.mcpClient) {
        await this.mcpClient.close();
        this.mcpClient = void 0;
      }
      this.lastContents = void 0;
      this.lastPaymentPayload = void 0;
      this.notificationsSubscribed = false;
      this.logger.debug("PaymentChannelMcpClient closed successfully");
    } catch (error) {
      this.logger.warn("Error during close:", error);
      throw error;
    }
  }
  /**
   * Load persisted state from storage
   */
  async loadPersistedState() {
    try {
      const state = await this.mappingStore.getState(this.host);
      if (state) {
        this.paymentState.loadPersistedState(state);
        this.logger.debug("Loaded persisted state for host:", this.host);
      }
    } catch (error) {
      this.logger.debug("Failed to load persisted state:", error);
    }
  }
  /**
   * Persist current client state to storage
   */
  async persistClientState() {
    try {
      const state = this.paymentState.getPersistedState();
      await this.mappingStore.setState(this.host, state);
      this.logger.debug("Persisted client state for host:", this.host);
    } catch (error) {
      this.logger.debug("Failed to persist client state:", error);
    }
  }
  /**
   * Clear persisted state (useful for logout/cleanup)
   */
  async clearPersistedState() {
    try {
      await this.mappingStore.deleteState(this.host);
      this.paymentState.reset();
      this.logger.debug("Cleared persisted state for host:", this.host);
    } catch (error) {
      this.logger.debug("Failed to clear persisted state:", error);
    }
  }
};

// src/integrations/mcp/UniversalMcpClient.ts
import { DebugLogger as DebugLogger14 } from "@nuwa-ai/identity-kit";
import { Client as McpClient3 } from "@modelcontextprotocol/sdk/client/index.js";
import { StreamableHTTPClientTransport as StreamableHTTPClientTransport3 } from "@modelcontextprotocol/sdk/client/streamableHttp.js";
var UniversalMcpClient = class {
  constructor(options) {
    this.options = options;
    this.serverType = "unknown" /* UNKNOWN */;
    this.detector = new ServerDetector({
      timeout: options.detectionTimeout || 5e3,
      cache: true,
      fetchImpl: globalThis.fetch?.bind(globalThis)
    });
    this.logger = DebugLogger14.get("UniversalMcpClient");
    this.logger.setLevel(options.debug ? "debug" : "info");
  }
  async call(method, params, schemaOrOptions) {
    await this.ensureInitialized();
    if (this.serverType === "payment" /* PAYMENT_ENABLED */ && this.paymentClient) {
      return await this.paymentClient.call(method, params, schemaOrOptions);
    }
    const { content } = await this.callTool(method, params);
    const raw = this.parseFirstJsonText(content);
    let data;
    if (schemaOrOptions) {
      let schema;
      if (typeof schemaOrOptions === "object" && "clientTxRef" in schemaOrOptions && schemaOrOptions.schema) {
        schema = schemaOrOptions.schema;
      } else if (typeof schemaOrOptions.safeParse === "function" || typeof schemaOrOptions.parse === "function") {
        schema = schemaOrOptions;
      }
      if (schema) {
        try {
          data = schema.parse(raw);
        } catch (e) {
          this.logger.warn("Failed to parse response", { cause: e, raw, schema });
          throw new Error("Failed to parse response");
        }
      } else {
        data = raw;
      }
    } else {
      data = raw;
    }
    return { data, payment: void 0 };
  }
  async callTool(name, args, optionsOrClientTxRef) {
    await this.ensureInitialized();
    if (this.serverType === "payment" /* PAYMENT_ENABLED */ && this.paymentClient) {
      return await this.paymentClient.callTool(name, args, optionsOrClientTxRef);
    }
    const result = await this.standardClient.callTool({ name, arguments: args || {} });
    return { content: Array.isArray(result.content) ? result.content : [] };
  }
  /**
   * Call a tool with payment processing
   */
  async callToolWithPayment(method, params, clientTxRef) {
    await this.ensureInitialized();
    if (this.serverType === "payment" /* PAYMENT_ENABLED */ && this.paymentClient) {
      return await this.paymentClient.callToolWithPayment(method, params, clientTxRef);
    }
    const { content } = await this.callTool(method, params);
    return { content, payment: void 0 };
  }
  /**
   * List tools exposed by the server
   */
  async listTools(options) {
    await this.ensureInitialized();
    if (this.serverType === "payment" /* PAYMENT_ENABLED */ && this.paymentClient) {
      const paymentOptions = {
        includeBuiltinTools: options?.includeBuiltinTools
      };
      return await this.paymentClient.listTools(paymentOptions);
    }
    const result = await this.standardClient.listTools();
    return this.normalizeToolsFormat(result);
  }
  /**
   * Get tools in AI SDK compatible format
   */
  async tools(options) {
    await this.ensureInitialized();
    if (this.serverType === "payment" /* PAYMENT_ENABLED */ && this.paymentClient) {
      const paymentOptions = {
        includeBuiltinTools: options?.includeBuiltinTools
      };
      const result2 = await this.paymentClient.tools(paymentOptions);
      this.logger.debug("Payment client tools result:", result2);
      return result2;
    }
    const result = await this.createStandardToolSet(options);
    this.logger.debug("Standard MCP tools result:", result);
    return result;
  }
  /**
   * List prompts exposed by the server (FREE, no payment)
   */
  async listPrompts() {
    await this.ensureInitialized();
    if (this.serverType === "payment" /* PAYMENT_ENABLED */ && this.paymentClient) {
      return await this.paymentClient.listPrompts();
    }
    return await this.standardClient.listPrompts();
  }
  /**
   * Load a prompt by name and return string content (FREE, no payment)
   */
  async loadPrompt(name, args) {
    await this.ensureInitialized();
    if (this.serverType === "payment" /* PAYMENT_ENABLED */ && this.paymentClient) {
      return await this.paymentClient.loadPrompt(name, args);
    }
    const result = await this.standardClient.getPrompt({ name, arguments: args || {} });
    return this.extractStringFromPromptResult(result);
  }
  /**
   * List resources (FREE, no payment)
   */
  async listResources() {
    await this.ensureInitialized();
    if (this.serverType === "payment" /* PAYMENT_ENABLED */ && this.paymentClient) {
      return await this.paymentClient.listResources();
    }
    const result = await this.standardClient.listResources();
    return Array.isArray(result?.resources) ? result.resources : [];
  }
  /**
   * List resource templates (FREE, no payment)
   */
  async listResourceTemplates() {
    await this.ensureInitialized();
    if (this.serverType === "payment" /* PAYMENT_ENABLED */ && this.paymentClient) {
      return await this.paymentClient.listResourceTemplates();
    }
    const result = await this.standardClient.listResourceTemplates();
    return Array.isArray(result.resourceTemplates) ? result.resourceTemplates : [];
  }
  /**
   * Read a resource
   */
  async readResource(params) {
    await this.ensureInitialized();
    if (this.serverType === "payment" /* PAYMENT_ENABLED */ && this.paymentClient) {
      return await this.paymentClient.readResource(params);
    }
    const p = typeof params === "string" ? { uri: params } : params;
    const result = await this.standardClient.readResource(p);
    return this.normalizeResourceResult(result);
  }
  // ===== New methods =====
  /**
   * Get detected server type
   */
  getServerType() {
    return this.serverType;
  }
  /**
   * Get enhanced server capabilities
   */
  getCapabilities() {
    return this.capabilities;
  }
  /**
   * Get standard MCP capabilities (without Nuwa extensions)
   */
  getStandardCapabilities() {
    if (!this.capabilities) return {};
    const { nuwa, ...standard } = this.capabilities;
    return {
      experimental: standard.experimental,
      logging: standard.logging,
      prompts: standard.prompts,
      resources: standard.resources,
      tools: standard.tools,
      ...standard
    };
  }
  /**
   * Check if server supports payment protocol
   */
  supportsPayment() {
    return this.capabilities?.nuwa?.payment?.supported === true;
  }
  /**
   * Check if server supports DID authentication
   */
  supportsAuth() {
    return this.capabilities?.nuwa?.auth?.supported === true;
  }
  /**
   * Check if server has Nuwa built-in tools
   */
  hasBuiltinTools() {
    return this.capabilities?.nuwa?.builtinTools?.supported === true;
  }
  /**
   * Get detection result with timestamp
   */
  getDetectionResult() {
    return this.detectionResult;
  }
  /**
   * Force re-detection of server capabilities
   */
  async redetect() {
    this.detector.clearCache();
    this.serverType = "unknown" /* UNKNOWN */;
    this.capabilities = void 0;
    this.detectionResult = void 0;
    if (this.paymentClient) {
      await this.paymentClient.close();
      this.paymentClient = void 0;
    }
    if (this.standardClient) {
      await this.standardClient.close();
      this.standardClient = void 0;
    }
    await this.ensureInitialized();
    return this.detectionResult;
  }
  // ===== Lifecycle Management =====
  /**
   * Close the client and clean up resources
   */
  async close() {
    try {
      if (this.paymentClient) {
        await this.paymentClient.close();
        this.paymentClient = void 0;
      }
      if (this.standardClient) {
        await this.standardClient.close();
        this.standardClient = void 0;
      }
      this.serverType = "unknown" /* UNKNOWN */;
      this.capabilities = void 0;
      this.detectionResult = void 0;
      this.logger.debug("UniversalMcpClient closed successfully");
    } catch (error) {
      this.logger.warn("Error during close:", error);
      throw error;
    }
  }
  // ===== Internal Implementation =====
  /**
   * Ensure client is initialized and server type is detected
   */
  async ensureInitialized() {
    if (this.serverType !== "unknown" /* UNKNOWN */) return;
    if (this.options.forceMode === "payment") {
      this.serverType = "payment" /* PAYMENT_ENABLED */;
      const { forceMode, detectionTimeout, ...cleanOptions } = this.options;
      this.paymentClient = new PaymentChannelMcpClient(cleanOptions);
      this.capabilities = { nuwa: { payment: { supported: true } } };
      this.logger.debug("Forced payment mode");
      return;
    }
    if (this.options.forceMode === "standard") {
      this.serverType = "standard" /* STANDARD */;
      this.standardClient = await this.createStandardMcpClient();
      this.capabilities = {};
      this.logger.debug("Forced standard mode");
      return;
    }
    this.detectionResult = await this.detector.detectCapabilities(this.options.baseUrl);
    this.serverType = this.detectionResult.type;
    this.capabilities = this.detectionResult.capabilities;
    if (this.serverType === "payment" /* PAYMENT_ENABLED */) {
      const { forceMode, detectionTimeout, ...cleanOptions } = this.options;
      this.paymentClient = new PaymentChannelMcpClient(cleanOptions);
      this.logger.debug("Created payment-enabled client");
    } else {
      this.standardClient = await this.createStandardMcpClient();
      this.logger.debug("Created standard MCP client");
    }
  }
  /**
   * Create standard MCP client
   */
  async createStandardMcpClient() {
    let transport;
    if (this.options.customTransport) {
      transport = this.options.customTransport;
    } else {
      const httpTransport = new StreamableHTTPClientTransport3(new URL(this.options.baseUrl));
      if (typeof httpTransport.setProtocolVersion === "function") {
        httpTransport.setProtocolVersion("2025-06-18");
      }
      transport = httpTransport;
    }
    const client = new McpClient3({
      name: "nuwa-universal-mcp-client",
      version: "1.0.0"
    });
    await client.connect(transport);
    return client;
  }
  /**
   * Normalize tools format for consistency
   */
  normalizeToolsFormat(result) {
    if (!result || !result.tools) return {};
    const normalized = {};
    for (const tool of result.tools) {
      if (tool && tool.name) {
        normalized[tool.name] = tool;
      }
    }
    return normalized;
  }
  /**
   * Create AI SDK compatible ToolSet for standard MCP
   */
  async createStandardToolSet(options) {
    const rawTools = await this.listTools(options);
    const toolSet = {};
    for (const [name, toolDef] of Object.entries(rawTools)) {
      if (toolDef && typeof toolDef === "object") {
        toolSet[name] = McpToolConverter.convertToAiSdkFormat(
          { name, ...toolDef },
          async (args, options2) => {
            const { content } = await this.callTool(name, args);
            return { content };
          }
        );
      }
    }
    return toolSet;
  }
  /**
   * Parse first JSON text from content array
   */
  parseFirstJsonText(content) {
    const dataItem = Array.isArray(content) ? content.find((c) => c?.type === "text") || content[0] : void 0;
    if (dataItem && dataItem.type === "text" && typeof dataItem.text === "string") {
      try {
        return JSON.parse(dataItem.text);
      } catch {
        return dataItem.text;
      }
    }
    return void 0;
  }
  /**
   * Extract string from prompt result
   */
  extractStringFromPromptResult(res) {
    if (typeof res === "string") return res;
    if (res && typeof res === "object") {
      if (Array.isArray(res.content)) {
        const textItem = res.content.find((c) => c?.type === "text");
        if (textItem?.text) return String(textItem.text);
      }
      if (typeof res.text === "string") return res.text;
    }
    try {
      return JSON.stringify(res);
    } catch {
      return String(res);
    }
  }
  /**
   * Normalize resource result
   */
  normalizeResourceResult(res) {
    try {
      if (typeof res === "string") return { text: res };
      if (res && typeof res === "object") {
        if (typeof res.text === "string") {
          return { text: res.text, mimeType: res.mimeType };
        }
        if (res.type === "text" && typeof res.text === "string") {
          return { text: res.text };
        }
        const contents = res.contents || res.content;
        if (Array.isArray(contents)) {
          const textItem = contents.find(
            (c) => c?.type === "text" && typeof c.text === "string"
          );
          if (textItem) return { text: String(textItem.text), mimeType: textItem.mimeType };
          const anyItem = contents[0];
          if (anyItem && (anyItem.blob || anyItem.data)) {
            return { blob: anyItem.blob || anyItem.data, mimeType: anyItem.mimeType };
          }
        }
      }
    } catch {
    }
    return res;
  }
  // ===== Compatibility method proxies =====
  /**
   * Get underlying payment client (if available)
   */
  getPayerClient() {
    if (this.serverType === "unknown" /* UNKNOWN */) {
      return void 0;
    }
    if (this.serverType === "payment" /* PAYMENT_ENABLED */ && this.paymentClient) {
      return this.paymentClient.getPayerClient();
    }
    return void 0;
  }
  /**
   * Get transaction store (if available)
   */
  getTransactionStore() {
    if (this.serverType === "payment" /* PAYMENT_ENABLED */ && this.paymentClient) {
      return this.paymentClient.getTransactionStore();
    }
    return void 0;
  }
  /**
   * Get last contents (if available)
   */
  getLastContents() {
    if (this.serverType === "payment" /* PAYMENT_ENABLED */ && this.paymentClient) {
      return this.paymentClient.getLastContents();
    }
    return void 0;
  }
  /**
   * Get last payment payload (if available)
   */
  getLastPaymentPayload() {
    if (this.serverType === "payment" /* PAYMENT_ENABLED */ && this.paymentClient) {
      return this.paymentClient.getLastPaymentPayload();
    }
    return void 0;
  }
  /**
   * Get pending SubRAV (if available)
   */
  getPendingSubRAV() {
    if (this.serverType === "payment" /* PAYMENT_ENABLED */ && this.paymentClient) {
      return this.paymentClient.getPendingSubRAV();
    }
    return null;
  }
  /**
   * Clear pending SubRAV (if available)
   */
  clearPendingSubRAV() {
    if (this.serverType === "payment" /* PAYMENT_ENABLED */ && this.paymentClient) {
      this.paymentClient.clearPendingSubRAV();
    }
  }
  /**
   * Commit a signed SubRAV (if available)
   */
  async commitSubRAV(signedSubRAV) {
    await this.ensureInitialized();
    if (this.serverType === "payment" /* PAYMENT_ENABLED */ && this.paymentClient) {
      return await this.paymentClient.commitSubRAV(signedSubRAV);
    }
    throw new Error("commitSubRAV is only available for payment-enabled servers");
  }
  /**
   * Health check (if available)
   */
  async healthCheck() {
    await this.ensureInitialized();
    if (this.serverType === "payment" /* PAYMENT_ENABLED */ && this.paymentClient) {
      return await this.paymentClient.healthCheck();
    }
    try {
      await this.callTool("ping", {});
      return { status: "healthy" };
    } catch {
      return { status: "unknown" };
    }
  }
  /**
   * Recover from service (if available)
   */
  async recoverFromService() {
    await this.ensureInitialized();
    if (this.serverType === "payment" /* PAYMENT_ENABLED */ && this.paymentClient) {
      return await this.paymentClient.recoverFromService();
    }
    throw new Error("recoverFromService is only available for payment-enabled servers");
  }
};

// src/integrations/mcp/factory.ts
async function createMcpClient(options) {
  const chainConfig = getChainConfigFromEnv(options.env);
  let keyId;
  const keyIds = await options.env.keyManager.listKeyIds?.();
  if (keyIds && keyIds.length > 0) {
    keyId = keyIds[0];
  } else {
    throw new Error("No keyId available");
  }
  const mcpPayerOptions = {
    baseUrl: options.baseUrl,
    chainConfig,
    signer: options.env.keyManager,
    keyId,
    maxAmount: options.maxAmount || BigInt("500000000000"),
    // Default: 50 cents USD
    debug: options.debug ?? chainConfig.debug,
    storageOptions: options.storageOptions,
    transactionStore: options.transactionStore,
    transactionLog: options.transactionLog,
    mappingStore: options.mappingStore,
    customTransport: options.customTransport,
    // Universal client options
    forceMode: options.forceMode || "auto",
    detectionTimeout: options.detectionTimeout || 5e3
  };
  const client = new UniversalMcpClient(mcpPayerOptions);
  return client;
}
async function createPaymentMcpClient(options) {
  return createMcpClient({
    ...options,
    forceMode: "payment"
  });
}
async function createStandardMcpClient(options) {
  return createMcpClient({
    ...options,
    forceMode: "standard",
    maxAmount: BigInt(0)
    // Not used in standard mode
  });
}

// src/utils/index.ts
import { DebugLogger as DebugLogger15 } from "@nuwa-ai/identity-kit";
function generateNonce() {
  return BigInt(Date.now()) * BigInt(1e3) + BigInt(Math.floor(Math.random() * 1e3));
}
function extractFragment(keyId) {
  const hashIndex = keyId.indexOf("#");
  if (hashIndex === -1) {
    throw new Error(`No fragment found in keyId: ${keyId}`);
  }
  return keyId.substring(hashIndex + 1);
}
function isValidHex(hex, expectedLength) {
  if (!hex || typeof hex !== "string") {
    return false;
  }
  if (!hex.startsWith("0x")) {
    return false;
  }
  const hexPart = hex.substring(2);
  if (!/^[0-9a-fA-F]*$/.test(hexPart)) {
    return false;
  }
  if (expectedLength !== void 0 && hexPart.length !== expectedLength) {
    return false;
  }
  return true;
}
function formatAmount(amount, decimals = 18) {
  const divisor = BigInt(10 ** decimals);
  const whole = amount / divisor;
  const remainder = amount % divisor;
  if (remainder === 0n) {
    return whole.toString();
  }
  const remainderStr = remainder.toString().padStart(decimals, "0");
  return `${whole}.${remainderStr.replace(/0+$/, "")}`;
}
function generateChannelId() {
  return `0x${Math.random().toString(16).substring(2).padStart(64, "0")}`;
}
function bigintToString(value) {
  return value.toString();
}
function stringToBigint(value) {
  return BigInt(value);
}
function formatUsdAmount(picoUsd, maxFractionDigits = 12) {
  const divisor = 1000000000000n;
  const whole = picoUsd / divisor;
  const remainder = picoUsd % divisor;
  const digits = Math.max(0, Math.min(12, Math.floor(maxFractionDigits)));
  const fullRemainderStr = remainder.toString().padStart(12, "0");
  const usedRemainder = digits === 12 ? fullRemainderStr : fullRemainderStr.slice(0, digits);
  const fractional = usedRemainder.replace(/0+$/, "") || "0";
  return `$${whole.toString()}.${fractional}`;
}
export {
  AdminRequestSchema,
  CURRENT_SUBRAV_VERSION,
  CancelProofSchema,
  CancelProofsSchema,
  ClaimTriggerRequestSchema,
  ClaimTriggerResponseSchema,
  CloseProofSchema,
  CloseProofsSchema,
  CommitRequestSchema,
  CommitResponseSchema,
  DebugLogger15 as DebugLogger,
  DidAuthHelper,
  DiscoveryRequestSchema,
  DiscoveryResponseSchema,
  ErrorCode,
  Errors,
  HealthRequestSchema,
  HealthResponseSchema,
  HttpBillingMiddleware,
  HttpPaymentCodec,
  IndexedDBTransactionStore,
  LocalStorageHostChannelMappingStore,
  McpServerType,
  MemoryHostChannelMappingStore,
  MemoryTransactionStore,
  PaymentChannelAdminClient,
  PaymentChannelFactory,
  PaymentChannelHttpClient,
  PaymentChannelMcpClient,
  PaymentChannelPayeeClient,
  PaymentChannelPayerClient,
  PaymentErrorCode,
  PaymentHubClient,
  PaymentKitError,
  PaymentProcessor,
  PaymentRevenueClient,
  PaymentUtils,
  PersistedHttpClientStateSchema,
  RecoveryRequestSchema,
  RecoveryResponseSchema,
  RoochContractBase,
  RoochPaymentChannelContract,
  RoochPaymentRevenueContract,
  SUBRAV_VERSION_1,
  ServerDetector,
  ServiceDiscoverySchema,
  SubRAVBCSSchema,
  SubRAVCodec,
  SubRAVSigner,
  SubRAVUtils,
  SubRavRequestSchema,
  SubRavResponseSchema,
  SystemStatusResponseSchema,
  SystemStatusSchema,
  UniversalMcpClient,
  bigintToString,
  createAdminClient,
  createDefaultMappingStore,
  createErrorResponse,
  createHttpClient,
  createMcpClient,
  createPaymentChannelClient,
  createPaymentMcpClient,
  createStandardMcpClient,
  createSuccessResponse,
  createTransactionStore,
  extractFragment,
  extractHost,
  formatAmount,
  formatUsdAmount,
  generateChannelId,
  generateNonce,
  getChainConfigFromEnv,
  httpStatusFor,
  isValidHex,
  stringToBigint,
  toApiError
};
//# sourceMappingURL=index.js.map